/* =====================================================================
   mk-portal-core.js  (Shopify theme asset)  -  window.MKPortal
   MK Supplements customer portal (Skio Customer Portal v3, /a/account/*)

   Shared core for the theme modules (home, delivery, rewards widgets).
   Loaded by snippets/mk-portal.liquid (defer, before the modules). Does
   nothing unless the loader's gate put class "mk-portal-on" on <html>.

   RULES THIS FILE ENFORCES
   - Fail closed: every function catches its own errors and returns null /
     {data:null, errors:[...]} / false instead of throwing into the page.
   - The customer's login token (Cognito ID token) is read from Skio's
     localStorage, held only in local variables, never logged, never stored,
     never put in a URL, and sent only to the FOUR hosts below (hard-coded, not
     parameters; TOKEN_HOSTS lists them, r9 1.4.0 added the third for ONE URL, R11 1.6.0
     the fourth for ONE URL, Jim's OK of 2026-10-27):
       https://graphql.skio.com/v1/graphql                       (Skio, customer role)
       https://mks-delivery-status.mksupps.workers.dev/v1/orders (delivery status)
       https://mks-delivery-status.mksupps.workers.dev/v1/tracking (all scans of one order)
       https://api.skio.com/storefront-v2-http/claim-credit-link (r9: Skio's own claim request, in
                                                                  the JSON body, as Skio's claim page sends it)
       https://mks-returns.mksupps.workers.dev/v1/returns         (R11: the returns service, GET and POST,
                                                                  as a Bearer header like the delivery service)
   - Read-only: skio() refuses GraphQL mutations and subscriptions. There are exactly FOUR writes.
     Two GraphQL writes (r8, 1.3.0), each Skio's own document verbatim, from a frozen table of two
     (WRITES; the allowlist is checked where the request is built, and postWrite() takes a kind,
     never a document): addSubscriptionLine() (batch 3) and changeNextOrderDate() (r8, off unless
     MKPortalConfig.writes.nextDate === true). One REST write (r9, 1.4.0): claimReward(), Skio's own
     claim-credit-link request, from a frozen table of one (REST_WRITES / REST_WRITE_ALLOWLIST, checked
     in postRestWrite(), the one place it leaves this file), off unless MKPortalConfig.writes.claim
     === true, only for the two reward ids the claim guard snippet holds, only after Klaviyo accepted
     that channel's sign-up in this page view, at most once per reward per page view, never retried.
     R11 (1.6.0): sendReturn(), a return request to the returns service (POST RETURNS_URL, built in
     postReturn() only, the one place it leaves this file; a body of exactly RETURN_KEYS), off unless
     MKPortalConfig.modules.returns AND MKPortalConfig.writes.returns are exactly true (both recorded at
     load), a fresh requestId per call, never re-sent after a lost answer (re-read only).
     Each write only with a fresh single-use token that the open dialog mints from the customer's own
     click, one write at a time page-wide, never retried blindly.
   - Klaviyo (batch 3): klaviyo.subscribeEmail() / subscribeSms() post to Klaviyo's
     public client endpoint https://a.klaviyo.com/client/subscriptions with MK Supplements' public
     site key; no login token, no secret. Email and phone are never logged or stored.
   - Text only: el()/text() never parse HTML; URLs pass safeUrl() (http/https).
   - Caches live in memory only (they survive Next.js client-side route
     changes, not page loads), are keyed by the logged-in customer, and are
     dropped on logout, on the login page and on a back/forward-cache restore.
   - Every request (headers and body) is limited to 12 s.

   API  (all on window.MKPortal; "Promise<T>" never rejects)
   Gate / config
     on() -> boolean                  true when <html> has class mk-portal-on
     config -> object                 window.MKPortalConfig from the loader ({} if absent):
                                      {mode, on, modules, tierArt, tokenWaitMs?, klaviyo?: {companyId?,
                                      emailListId?, smsListId?, emailVersion?, smsVersion?}, writes?: {nextDate, claim}}
                                      (emailListId / smsListId: a list id, or false to switch that list off;
                                      writes.nextDate: r8, the loader's mk_portal_write_next_date; writes.claim: r9,
                                      the loader's mk_portal_write_claim; see writeEnabled())
     writeEnabled(kind) -> boolean    (r8) 'add-line': the gate is on. 'next-date': the gate is on AND
                                      writes.nextDate is exactly true when the core loaded (recorded once as a boolean:
                                      an in-place edit later cannot turn it on), still in the config object it loaded
                                      with, and in window.MKPortalConfig now (anything else, a missing key included, =
                                      off; turned off in place = off for the page view).
                                      (r9) 'claim': the same rule for writes.claim (its own load-time boolean).
                                      Anything else: false. Home offers the change-date pop-up only when true.
     catalogEnabled() -> boolean      (r10, 1.5.0) the "Add products" catalogue switch: the gate is on AND
                                      modules.catalog (the loader's mk_portal_catalog) is exactly true when the core
                                      loaded (recorded once), in the loaded config and in window.MKPortalConfig now, AND
                                      modules.home is not false (the home module owns the screen). A missing key = off
                                      (a loader older than r10 shows no catalogue). Not a write switch: adds still go
                                      only through addSubscriptionLine() and its guards.
     returnsEnabled() -> boolean      (R11, 1.6.0) the returns switch: the gate is on AND modules.returns (the loader's
                                      mk_portal_returns) is exactly true when the core loaded (recorded once), in the
                                      loaded config and in window.MKPortalConfig now. A missing key = off. returns()
                                      reads nothing while it is false. writeEnabled('return') also needs writes.returns
                                      (the loader's mk_portal_returns_writes), recorded the same way.
     version -> string
   Routes (Next.js client navigation)
     parsePath(pathname) -> {page, subscriptionId} | null
                                      page: 'subscriptions' | 'subscription' | 'loyalty' |
                                      'orders' | 'profile' | 'login' | 'other'; null when the
                                      path is not under /a/account
     route() -> {page, subscriptionId, path, seq}
                                      from the URL, else from the page root's data-skio-name;
                                      seq increases on every route change
     onRoute(cb) -> unsubscribe()     cb(route, previousRoute) once now (async) and after
                                      every pathname change; errors in cb are swallowed
     waitFor(selector, {timeout=10000, root=document, sameRoute=true}) -> Promise<Element|null>
                                      null on timeout, or when the route changed meanwhile
   Login token
     getIdToken({timeout=15000}) -> Promise<string|null>
                                      polls localStorage until a token that is a Skio ID token
                                      (iss, aud, token_use) and valid for >= 30 s more is there;
                                      null after the timeout. Never log or store the result.
     claims({timeout=15000}) -> Promise<object|null>
                                      a fresh copy of that token's decoded payload
                                      (e.g. "custom:storefrontUserId"). Never log it.
     storefrontUserId() -> Promise<string|null>
   Data
     skio(query, variables?, {ttl=60000, fresh=false}?) -> Promise<{data, errors}>
                                      POST to Skio GraphQL with exactly these header names:
                                      content-type, region, userpoolid, authorization.
                                      errors is null or an array. One retry with a renewed
                                      token after 401 / 403 / invalid-jwt. Only successful answers
                                      (no errors) are cached; identical requests in flight
                                      are shared.
     skioPages(query, variables?, {pick, pageSize=24, maxPages=5, ttl=60000, fresh=false}) -> Promise<result>
                                      (r10, 1.5.0) a list read in pages through skio() (read-only: a mutation or
                                      subscription document is refused, 'mk-read-only'). The query must declare $limit
                                      and $offset (else 'mk-bad-query'); this function sets them (limit = pageSize,
                                      offset = page x pageSize; any limit / offset in `variables` is ignored). pick(data)
                                      -> the page's array (e.g. d => d.SiteByPk.Products); required ('mk-bad-input').
                                      Pages are read one after another until a page shorter than pageSize; the last
                                      allowed page (maxPages) asks for pageSize + 1, so capped is true only when Skio
                                      has more than pageSize x maxPages. Items with the same string id are kept once
                                      (first wins). pageSize 1..100, maxPages 1..10. Each page is cached by skio()
                                      for ttl (e.g. 300000 for a 5-minute list; invalidate('skio') drops it).
                                      result: {ok: true, items, pages, capped, data, errors: null} |
                                      {ok: false, items (the pages read so far), pages, capped: false, data, errors}
                                      data = the first page's whole answer (other fields of the same query, e.g.
                                      SiteByPk.currencyCode); any page failing makes ok false (never a silent gap).
     delivery(limit=3, {ttl=120000, fresh=false}?) -> Promise<object|null>
                                      GET /v1/orders?limit=1..5 from the delivery service;
                                      the parsed body ({orders:[...], generatedAt}) or null on
                                      any error (401 is retried once with a renewed token).
     deliveryStatus() -> {status, at} | null
                                      (r7, 1.2.1) how the last network read of delivery() ended:
                                      status = the HTTP status (a number, e.g. 200, 429, 503), or
                                      'timeout' / 'network' / 'no-token' / 'bad-body' (200 with an
                                      unexpected body); at = ms timestamp. (R11, 1.5.2) also 401 right
                                      after a first 401 answer, while the renewed-token retry runs.
                                      Codes only: never the
                                      body, the token or anything personal. null before any read.
     tracking(order, {ttl=120000, fresh=false}?) -> Promise<{ok:true, data} | {ok:false, reason}>
                                      GET /v1/tracking?order=<digits> (service 2.2): every scan of
                                      ONE of the customer's own orders ('#28761' or '28761'; plain
                                      digits, no leading zero, at most 12, else reason
                                      'bad_request' without a request). data = {order,
                                      fulfillments:[{carrier, events:[...], moreScans, ...}],
                                      moreShipments}. reason: 'not_found' (404: not one of her
                                      newest 5 orders of 60 days), 'rate_limited', 'bad_request',
                                      'unauthorized', 'no_token', 'off', 'error'. Same token, 401
                                      retry, timeout and cache rules as delivery() (only ok answers
                                      are cached); requests run one at a time. Ask ONLY when the
                                      customer asks for all updates of one order, never in advance.
     returns({ttl=60000, fresh=false}?) -> Promise<{ok:true, data} | {ok:false, reason, status?}>
                                      (R11, 1.6.0) GET RETURNS_URL from the returns service 1.1.1 (its README): the
                                      customer's recent orders with what can be returned, and their return requests.
                                      data = the service's answer ({windowDays, writes, reasons, outcomes, returnAddress,
                                      orders: [{name, processedAt, arrivedAt, arrived, titles, selfShip, returnBy, state,
                                      items, unavailable, requests}]}). reason: 'off' (returnsEnabled() false, nothing
                                      sent), 'no_token', 'unauthorized' (401 after the one renewed-token retry),
                                      'no_match' (403 {code: no_match}: the service cannot match this login),
                                      'rate_limited' (429), 'error' (5xx, network, timeout, an unexpected body). Same
                                      token, header, timeout, 401-retry and cache rules as delivery() (only ok answers
                                      are cached, per customer; invalidate('returns') drops them).
     checkReturn(order) -> Promise<{seen, request?, held, read} | null>
                                      (R11) read only ("Check again" after an unknown outcome): one fresh read; seen when
                                      the request of this page's last send for that order ('#1061') is there. Never a write.
     returnHeld(order) -> boolean     (R11) an earlier sendReturn() of this page view for that order may still arrive (its
                                      outcome was unknown less than resendHoldMs ago, or the service accepted it and no
                                      read has shown it yet): the screen says "Checking your last return request".
                                      Review fix: any ok returns() answer (no send running) that shows the request ends
                                      the hold, so the Returns screen's own reads end it too.
     landing.is(name) / landing.drop(name) -> boolean
                                      the one-use landing hash '#mk-tracking' (gate on): is it in
                                      the address; remove it (replaceState, URL only)
     invalidate(kind?) -> void        drop cached answers: 'skio', 'delivery', 'tracking', 'returns' or all. Also
                                      done automatically when the customer presses a Skio
                                      button (outside the navbar) or one of ours marked
                                      data-mk-invalidate, so data stays fresh after a change.
   DOM (safe: text only)
     q(sel, root?) / qa(sel, root?)   querySelector / querySelectorAll as an array (never throw)
     byName(name, root?)              element with data-skio-name="name"
     el(tag, attrs?, ...children) -> Element
                                      attrs: class, id, role, title, alt, type, name, value,
                                      for, tabindex, hidden, disabled, loading, decoding, width,
                                      height, datetime, target, rel, colspan, rowspan, (r10) placeholder,
                                      autocomplete, enterkeyhint, inputmode, spellcheck,
                                      autocapitalize, maxlength, aria-*, data-*; href/src
                                      pass safeUrl() (dropped if unsafe); style only as an
                                      object of plain values (no url()); on* and anything else
                                      is ignored; text: '...' sets textContent; urlBase: base for
                                      relative href/src. Tags script/style/iframe/svg/... become
                                      div. children: strings (text nodes), nodes, arrays,
                                      null/false (skipped).
     svg(viewBox, paths[], {class}?) -> SVGElement  stroke icon from path data (d strings only)
     text(node, value) -> node        sets textContent ('' for null)
     esc(value) -> string             HTML-escapes & < > " ' (prefer el()/text())
     safeUrl(url, {base, sameOrigin, httpsOnly, absolute}?) -> string|null
                                      absolute http(s) URL, or null (javascript:, data:,
                                      credentials in the URL, control characters, ...)
     mount({id, anchor, where, build, sig, module}) -> Element|null
                                      fail-closed insert: build() must return an Element;
                                      where = 'before' | 'after' | 'prepend' | 'append';
                                      same id + same sig in the same place = left as is; any
                                      error, null result, gate off or detached anchor removes
                                      the old node and returns null
     unmount(id) -> void
     hide(element, owner) -> boolean / unhideAll(owner?)
                                      hide a native Skio element (inline display:none
                                      !important, marked data-mk-hidden-by) and restore its
                                      previous inline display later (no owner = all)
     guard(fn, onError?) -> fn        wraps fn (sync or async) so it never throws/rejects
   Input modality (r4, 2026-10-12)
     keyboard() -> boolean            true while <html> has class "mk-kbd" (the customer's last input
                                      was a key that moves or activates focus: Tab, arrows, Enter,
                                      Space, Escape, Home, End, PageUp/PageDown; not with Cmd/Ctrl/Alt)
     pointer() -> boolean             true while <html> has class "mk-pointer" (the last input was a
                                      pointerdown / mousedown / touchstart)
                                      The gate is opt-OUT: the stylesheets draw our own focus rings on
                                      html:not(.mk-pointer) :focus-visible, so before any input (the
                                      first Tab from the address bar, Switch Control, Voice Control,
                                      VoiceOver moving focus) a keyboard focus always shows our ring;
                                      only right after a mouse or touch press is it left off, so a mouse
                                      or touch choice never leaves a ring on our menu items, sheet or
                                      buttons. A key clears mk-pointer; so does keyboard focus that
                                      arrives without a key the page saw (focusin on a :focus-visible
                                      element more than 1 s after the last press, not a tabindex=-1
                                      title). Elements focused by script (screen titles) never show a
                                      ring or the theme's focus box.
   Dates (customer-local calendar days)
     toDate(value) -> Date|null       ISO timestamp, 'YYYY-MM-DD' (local midnight), Date, ms
     calendarDay(value, {timeZone}?) -> {y, m, d}|null
                                      'YYYY-MM-DD' and UTC-midnight timestamps
                                      ('YYYY-MM-DDT00:00:00Z') are date-only values: they keep
                                      that calendar day in every time zone (Shopify's
                                      estimatedDeliveryAt 2026-10-16 -> Fri, Oct 16). Other
                                      timestamps use the customer's time zone.
     formatDate(value, style='short', {timeZone, year:'auto'|true|false, now}?) -> string
                                      styles: short 'Oct 16', weekday 'Fri, Oct 16',
                                      long 'Friday, October 16', full 'Oct 16, 2026',
                                      monthYear 'Oct 2026', iso '2026-10-16'. year 'auto' adds
                                      the year when it is not the current one. '' if invalid.
     daysFromToday(value, {timeZone, now}?) -> integer|null   calendar days (0 today, 1 tomorrow)
     daysBetween(a, b, {timeZone}?) -> integer|null           calendar days from a to b
     addDays(value, n) -> Date|null / addInterval(value, interval, count) -> Date|null
                                      interval DAY|WEEK|MONTH|YEAR (Skio Policy); months clamp
                                      to the month's last day (Jan 31 + 1 month = Feb 28/29)
   Money / points / tiers
     num(value) -> number|null        number or numeric string
     money(amount, currency='USD') -> string     '$1,234.50' ('' if invalid)
     points(n) -> string              '1,234' (whole points, rounded down)
     pointsText(n) -> string          '1 point' / '1,234 points'
     pointsValue(points) -> number    dollars (100 points = $1)
     earnPoints(amount, pointsPerDollar) -> integer   whole points, rounded down, cent-exact
     TIERS                            [{key, name, minStreak, pointsPerDollar, perks}]
     tierName(name) -> string         strips the "Rewards Tier:" prefix
     tierFor(nameOrStreak) -> tier|null / nextTier(tier) -> tier|null
     tierArt(key) -> string           tier image URL (theme asset)
   Schedule (pure, no network; shared by the delivery timeline and the Upcoming orders screen)
     schedule.normalize(sub, full) -> sub|null    Skio subscription -> {id, status, statusContext, next,
                                      cycles, interval, count, digital, gift, lines[, streak, prepaid,
                                      anchors, maxCycles, maxCycleV2, skipCycle]} (full: those last
                                      fields are in the answer); schedule.normLine(line) -> line|null
     schedule.open(sub) -> boolean    ACTIVE, not on hold, has a next date, not digital, not a gift
     schedule.label(interval, count) -> 'Every month' | 'Every 2 weeks' | ''
     schedule.count(sub, max=6) -> 0..max        upcoming orders that can be listed (1 = next only)
     schedule.linesFor(sub, k) -> lines[]        lines that ship in upcoming order k (k = 1 next)
     schedule.upcoming(sub, timeZone, {max=6, until}?) -> [{k, ymd, lines}]
                                      stops at max rows, where the two month-end rules disagree,
                                      or after `until` ('YYYY-MM-DD'; the next order is always kept)
     schedule.siteYmd(value, timeZone) -> 'YYYY-MM-DD'|null / schedule.ymd(Date) -> 'YYYY-MM-DD'|null
     schedule.addYmd(ymd, n) -> 'YYYY-MM-DD'|null   (r8) plain calendar-day arithmetic (UTC; no time zone, no DST)
   Next order date (r8, 1.3.0: pure, no network; the change-date pop-up and changeNextOrderDate() share them)
     orderDay(value, timeZone) -> 'YYYY-MM-DD'|null
                                      THE day of a Skio nextBillingDate: the store day (Site.ianaTimezone), Skio's
                                      header rule (= schedule.siteYmd). null without a valid IANA zone (never the
                                      browser's zone). Use it for the NEXT ORDER card, Upcoming, the pop-up title,
                                      the "Currently" box and nowhere a different rule: changeNextOrderDate() reports
                                      success only when orderDay(re-read) equals the picked day. (After live test L6:
                                      if Skio stores UTC midnight, this one function changes, 02 sec. 4.5 rule 4.)
     nextDateRange(timeZone, cpv3Settings, {now}?) -> {today, min, max, limit} | null
                                      store days: today = the store's day now; min = today + 1 (Skio: never today);
                                      max = today + limit - 1 only when limit = cpv3Settings.timing.
                                      nextBillingDateDaysIntoFutureLimit is a positive whole number (number or digit
                                      string; cpv3Settings an object or its JSON text), else null = no latest day (MKS
                                      today). null for an invalid zone. A day d is allowed when d >= min and
                                      (max === null || d <= max) (plain string comparison). The calendar offers exactly
                                      these days and changeNextOrderDate() accepts exactly these days.
                                      Deliberately one day stricter than Skio's own window (r8 review): Skio allows up to
                                      today + limit and ignores a digit-string limit; we stop at today + limit - 1 and honour
                                      the string, so we never offer a day Skio could refuse. MKS sets no limit today.
     nextDateInstant(nextBillingDate, ymd, timeZone) -> ISO string | null
                                      the instant sent for store day ymd: that day at the order's CURRENT wall-clock
                                      billing time in the store's zone (DST-aware; Oct 30 09:00 CDT -> Nov 10 09:00 CST
                                      = '2026-11-10T15:00:00.000Z'), used only if its store day AND its UTC day are ymd
                                      and it is neither UTC midnight nor store midnight; else 12:00 store time under
                                      the same tests; else null. Never a date-only string, never midnight.
     nextDateEligible(sub, site, {now}?) -> {ok: true, day, tz, range} | {ok: false, rule}
                                      the pop-up's gate (02 sec. 3.1), on Skio's own field names: sub = a Skio
                                      Subscription (id, status, statusContext, nextBillingDate, cyclesCompleted,
                                      isDigital, prepaidDeliveryPolicyId, prepaidGiftRecipientId, PrepaidDeliveryPolicy,
                                      BillingPolicy {minCycles, Anchors}, SubscriptionLines?), site = Skio's Site
                                      (ianaTimezone, cpv3Settings, settings; objects or JSON text). rule: bad_id,
                                      status (not ACTIVE), hold (statusContext TEMPORARILY_PAUSED / 3DS_PENDING / DUNNING
                                      / UNDER_FRAUD_REVIEW), digital, prepaid, gift, timezone, switch_off (cpv3Settings
                                      .actionButtonToggleSettings['Next order date'] not truthy), anchors (Anchors not a
                                      list, or not empty without settings.allowAnyNextBillingDate === true), min_cycles
                                      (settings.disableBillingDateChangeIncompleteMinCycles === true and cyclesCompleted
                                      < BillingPolicy.minCycles, or minCycles not read), no_date, due_today (the order's
                                      store day is before nextDateRange().min: today or earlier). changeNextOrderDate()
                                      runs the same function on its own fresh read.
   Screen (round 2: one screen of ours at a time over Skio's page)
     screen.get() -> '' | 'upcoming' | 'support' | 'add-products' | 'returns' | 'return'   <html data-mk-screen>
                                      (R11, 1.6.0: 'returns' = the Returns screen, 'return' = Start a return, both
                                      owned by the returns module over Order history, history entries "#returns" and
                                      "#return";
                                      r10: 'add-products' = the "Add products" catalogue, owned by the home
                                      module, history entry "#add-products"; one screen at a time: set() of another
                                      name replaces the open one)
     screen.set(name) / screen.clear(name) -> boolean
                                      set it (gate on only) / clear it only while it still holds
                                      `name`; each change fires window 'mk-portal:screen' {screen}
                                      ('' when cleared)
     screen.dropHash(name) -> boolean removes "#name" from the address bar (replaceState, URL only)
     screen.pushHash(name) -> boolean adds a history entry "#name" for the current page, carrying
                                      Next's own router state (a copy of the current entry's state,
                                      plus mkScreen: name), so Back from another page traverses to
                                      the right page instead of being ignored by Next (no hashchange
                                      fires; the caller opens its screen itself)
     screen.ownEntry(name) -> boolean the current entry is "#name" added in this page (by pushHash,
                                      or a plain hash change: state null), so history.back() closes it
   Subscription page
     focusCombineCard(route?) -> Promise<boolean>
                                      runs by itself once at page load, only on a subscription
                                      page whose URL ends in #mk-combine (the home's "Review and
                                      combine" link, a full page load). Waits up to 8 s for the Combine card's
                                      title #mk-x-combine-title to be shown, scrolls the card
                                      into view below the sticky page header (instantly with
                                      prefers-reduced-motion) and focuses the title; true when
                                      it did. No card: nothing moves. The hash is removed
                                      either way (history.replaceState, URL only). If content
                                      mounts above the card afterwards (the delivery timeline),
                                      it scrolls to the card again (until 8 s after load, and
                                      1.5 s after the last correction). Gives up at once if the
                                      customer scrolls, clicks or types meanwhile.
   Dialog (batch 3, 1.1.0: the shared pop-up; approved batch 3 design)
     dialog.open(opts) -> handle | null
                                      opts: {title (text, required), body?, actions?, icon?,   (Element | [Element] | text)
                                      id? ('mk-...', default 'mk-dlg'), opener? (default: the focused element),
                                      returnFocus? (Element | () => Element, used when the opener is gone),
                                      onClose?(reason), pin? (default true: actions pinned at the foot; false:
                                      they follow the body in the scroll), center?, stack? (actions in a column),
                                      wide? (560 px instead of 500), handle? (phone grab handle, default true),
                                      className? (extra classes), describedBy? (ids), bodyLabel? (makes the body a
                                      focusable region with this label, for long scrolling content), closeLabel?
                                      ('Close'), dismissible? (default true), module?, host? ('body' | 'skio-root')}
                                      One dialog at a time (a new one replaces the open one). Native <dialog>
                                      showModal() when available (page behind inert), else role=dialog + inert on
                                      the rest of <body>. Centred on desktop / tablet; a bottom sheet under 768 px
                                      wide or 500 px tall. Head (X, icon, title) fixed, body scrolls, actions
                                      pinned. Focus starts on the title; Tab loops inside; Escape, the scrim and
                                      the X close it; focus returns to the opener; a route change closes it.
                                      --mk-kb follows the phone keyboard (visualViewport). Styles: injected once
                                      as <style id="mk-dlg-css"> (classes below), all gated by html.mk-portal-on.
                                      Reasons: 'escape' | 'scrim' | 'close-button' | 'route' | 'replaced' | 'api'.
     handle: {id, root, body, actions, title, isOpen(), set(parts) (title/icon/body/actions/pin/center/stack/
                                      describedBy; focus moves to the title unless focus: false), focusTitle(),
                                      busy(on) (Escape / scrim / X and confirm() refused; auto-release 30 s),
                                      isBusy(), confirm(clickEvent, purpose) -> token | null, reveal(node),
                                      close(reason?, {returnFocus}?)}
     dialog.current() -> handle | null / dialog.isOpen() -> boolean / dialog.close(reason?)
                                      window event 'mk-portal:dialog' {open, id} on open and close
     Shared classes: mk-dlg-btn + --primary | --gold | --ghost, mk-dlg-lead, mk-dlg-note, mk-dlg-label,
                                      mk-dlg-input, mk-dlg-hint, mk-dlg-check + mk-dlg-box (+ is-invalid),
                                      mk-dlg-field-msg, mk-dlg-legal, mk-dlg-alert, mk-dlg-fine, mk-dlg-after,
                                      mk-dlg-cross, mk-dlg-icon (+ --gold | --done), mk-dlg-sr, mk-dlg-choice
                                      (a label wrapping a radio: keyboard ring via :has(input:focus-visible))
   Confirmation tokens (for the writes and for Klaviyo)
     handle.confirm(ev, purpose) -> token | null
                                      only inside the click handler of a button in the open dialog (not the X),
                                      for a real click (ev.isTrusted, type 'click': a mouse, Enter or Space; read
                                      through the browser's own Event getters, so a look-alike object never counts),
                                      at most ONE token per click (a second confirm() with the same event = null), not
                                      while busy. purpose: {kind: 'add-line', subscriptionId, variantId, oneTime}
                                      or (R11) {kind: 'return', order, items, reason, note, outcome} (the exact choices
                                      sendReturn() gets; the note compared after the same clean-up)
                                      or {kind: 'next-date', subscriptionId, date, from} (r8: date = the picked store
                                      day, from = the store day the pop-up shows as "Currently", both 'YYYY-MM-DD',
                                      different) or {kind: 'klaviyo', channel: 'email' | 'sms'} or (r9) {kind: 'claim',
                                      rewardId} (rewardId = the reward's uuid; key 'claim:' + id). Single use, 10 s,
                                      only while that dialog is open, only for that exact purpose (a token for Nov 10
                                      never moves to Nov 13; one minted against another "from" is refused; an
                                      add-line token never confirms a date change, nor the reverse). Mint it BEFORE
                                      busy(true); it is used up by the call whatever the outcome.
                                      SMS: the token also records whether the pop-up holds a ticked, enabled
                                      input[type=checkbox][data-mk-consent="sms"] at that click (review fix).
   The writes (exactly four. GraphQL, r8: WRITES = {'add-line', 'next-date'}, each Skio's own document verbatim, its
   operation name and the only input keys it may carry; postWrite(token, kind, input) is the one place a GraphQL write
   leaves this file, with one call site per kind. REST, r9: REST_WRITES = {'claim'}, Skio's claim-credit-link URL and
   the only input key it carries (id); postRestWrite(token, kind, input) is the one place it leaves this file, one call
   site. R11: RETURN_KEYS, postReturn(token, body) is the one place a return leaves this file, one call site)
   1. add a product
     addSubscriptionLine({subscriptionId, variantId, oneTime, confirm[, quantity: 1]}) -> Promise<result>
                                      Skio's addSubscriptionLineAction, verbatim; input {subscriptionId, variantId,
                                      quantity: 1, upsell: true only when oneTime}; never a price. Steps: re-read
                                      the subscription (status ACTIVE, the variant not already there: every order
                                      = no line of that variant, one time = no one-time line of it) -> send once
                                      -> ok: true only when Skio answered ok: true AND a re-read shows the new
                                      line (re-read at once, then every 2 s, 5 more times). Any other answer:
                                      re-read before reporting. An earlier attempt of this page for the same
                                      subscription and variant (either how-often choice) is checked first: its line
                                      there = success with the same choice (nothing sent), already_in_order with the
                                      other; while it is unresolved (Skio said ok: true but the line has not shown:
                                      for the rest of the page view; a send with an unknown outcome: for 60 s) the
                                      call only re-reads (3 reads, 2 s apart) and never sends a second add: result
                                      reason 'pending', outcome 'unknown', sent false, held true (review fix).
                                      The whole call ends within 25 s (deadline: 'unknown' if it had sent, else
                                      'error' / 'none'; deadline: true; nothing is sent after it), under the
                                      dialog's 30 s busy limit.
                                      result: {ok: true, line: {id, quantity, oneTime, priceWithoutDiscount},
                                      via: 'answer' | 'reread', sent} | {ok: false, reason, outcome, sent[, accepted,
                                      held, deadline]}
                                      reason / outcome: off, bad_input, busy, not_confirmed, no_token,
                                      precheck_failed, not_active, already_in_order (outcome 'none': nothing sent);
                                      refused, unauthorized (outcome 'refused'); unknown, pending (outcome
                                      'unknown': Skio may still apply it; never retry without a new click, and the
                                      next attempt re-reads first); error.
                                      Test-only knobs in MKPortalConfig (bounded; production uses the defaults):
                                      writePollMs 20..5000 (2000), writeDeadlineMs 1000..25000 (25000),
                                      resendHoldMs 2000..600000 (60000), confirmTtlMs 100..10000 (10000).
   2. move the next order (r8, 1.3.0; 02-safety-design sec. 4-5)
     changeNextOrderDate({subscriptionId, date, from, confirm}) -> Promise<result>  (never rejects)
                                      Skio's updateNextBillingDateAction, verbatim; input exactly {subscriptionId,
                                      date: nextDateInstant(...)} (never debugCaller, never a date-only string or a
                                      midnight). date = the picked store day, from = the day the pop-up showed as
                                      "Currently" ('YYYY-MM-DD' strings, different; no other keys: a time part, a Date,
                                      a number, nextBillingDate, debugCaller or query = bad_input). Order: kill switch
                                      (writeEnabled('next-date')) -> input -> one write at a time page-wide (shared
                                      with add-line) -> the token for exactly {subscriptionId, date, from} -> login
                                      token -> a fresh read (mkNextDateCheck, no cache: SubscriptionByPk (its
                                      storefrontUserId must be the login's) + StorefrontUserByPk.Site) -> an earlier
                                      attempt of this page for this subscription (its day showing now = ok via reread
                                      when the same day was asked; held: re-read only, 3 reads 2 s apart, never send)
                                      -> nextDateEligible() on that read -> nextDateRange() -> the read's day = date: ok
                                      via 'already', nothing sent; the read's day != from: 'changed' with current,
                                      nothing sent -> send ONCE. After Skio's answer:
                                        200, ok: true -> re-read at once, then every 2 s, 5 more times: day = date: ok
                                          via 'answer'; still the old day: 'pending' (accepted; held for the page view);
                                          a third day: 'mismatch' with current.
                                        401 / 403 / invalid JWT (nothing applied) -> new login token, re-read: date
                                          already -> ok; still from -> sent once more (the only second send); another
                                          day -> 'changed'; no new token / fails again -> 'unauthorized'.
                                        GraphQL errors, ok: false, other 4xx -> one re-read: date -> ok via reread;
                                          else 'refused' with current (not held).
                                        5xx, network, timeout, unreadable answer -> 3 re-reads: date -> ok via reread;
                                          else 'unknown' with current (held for resendHoldMs, 60 s).
                                      The whole call ends within 25 s (deadline: true; 'unknown' if it had sent, else
                                      'error'; nothing is sent after it). invalidate('skio') after every result.
                                      result: {ok: true, date, nextBillingDate, via: 'answer' | 'reread' | 'already',
                                      sent} | {ok: false, reason, outcome, sent[, current, accepted, held, deadline,
                                      rule]}; current = the store day the last read showed (when one was read).
                                      outcome 'none' (nothing applied): off, bad_input, busy, not_confirmed, no_token,
                                      precheck_failed, not_allowed (+ rule, see nextDateEligible), out_of_range,
                                      changed; outcome 'refused': refused, unauthorized; outcome 'unknown' (Skio may
                                      still apply it; never send again without a new click, and the next call re-reads
                                      first): unknown, pending, mismatch; error.
     nextOrderDate(subscriptionId) -> Promise<{date, nextBillingDate, status} | null>
                                      read only ("Check again"): the same fresh read; date = orderDay(); null on any
                                      failure. Never a write.
   3. claim a sign-up reward in place (r9, 1.4.0; C9 option c, Jim's OK 2026-10-15)
     claimReward({rewardId, confirm}) -> Promise<result>  (never rejects)
                                      Sends the ONE request Skio's own claim page (/a/account/credits/claim) sends: POST
                                      https://api.skio.com/storefront-v2-http/claim-credit-link, header Content-Type:
                                      application/json only, credentials 'omit' (no cookies), body JSON exactly
                                      {id, token, region: 'us-west-2', userPoolId} in that order (token = the login's ID
                                      token, in the body only, as Skio's helper sends it). 12 s limit; never retried (not
                                      after a 401, a 5xx or a timeout); the answer body is ignored (its shape is unknown).
                                      Order of the checks (nothing is sent unless every one passes):
                                        writeEnabled('claim') -> input exactly {rewardId, confirm}, rewardId a uuid -> the
                                        id is one of the two the claim guard snippet holds (newsletter, sms; read once at
                                        load from <style data-mk-portal="claim-guard"> in <head>, its rule 3) -> not sent
                                        for this id before in this page view -> no other write running (shared with
                                        add-line and next-date) -> the token for {kind: 'claim', rewardId} -> Klaviyo
                                        accepted that reward's channel (newsletter: email, sms: texts) in this page view ->
                                        a login token (iss, aud, token_use, >= 30 s left; waits up to 5 s) of the SAME
                                        customer that was logged in when Klaviyo accepted -> send once. 1.4.1: the
                                        sign-up is recorded for the customer logged in at the Join / Sign up press, and
                                        only if that customer is still logged in at Klaviyo's answer (else none).
                                      result: {ok, outcome, reason, sent[, status]}
                                        outcome 'ok'           2xx (reason 'ok'). Skio answered; it does NOT prove the points
                                                               are in: say "added" only once Skio's own card says Completed.
                                        outcome 'unauthorized' 401 / 403 (reason 'unauthorized', sent true).
                                        outcome 'refused'      any other HTTP status (reason 'refused', sent true, status).
                                        outcome 'unknown'      network error or timeout after sending (reason 'timeout' |
                                                               'network', sent true): Skio may have applied it.
                                        outcome 'none'         nothing sent (sent false). reason: off, bad_input,
                                                               unknown_reward, already_sent, busy, not_confirmed,
                                                               not_signed_up, no_token, error.
                                      After any sent result: invalidate('skio'). The id is marked sent the moment the
                                      request goes out: a second call for it in this page view is 'already_sent'.
     canClaim(rewardId) -> boolean    true when a claimReward() for it would pass every check that needs no click and no
                                      login token now: the switch on, a known id, not sent yet, no write running, Klaviyo
                                      accepted its channel in this page view, and (1.4.1) the customer logged in now is
                                      the one whose press Klaviyo accepted (read now, no wait). false = offer today's
                                      claim link instead (the widgets first forget a sign-up of another customer).
   4. send a return request (R11, 1.6.0; Jim's OK 2026-10-27)
     sendReturn({order, items, reason, note, outcome, confirm}) -> Promise<result>  (never rejects)
                                      order '#1061'; items [{key, quantity}] (the service's item keys, 1..20, each once,
                                      quantity 1..99); reason damaged | wrong_item | taste | not_needed | other; note a
                                      string ('' allowed, at most 250 characters after control characters and runs of
                                      spaces are folded; Other needs 3 letters or digits); outcome refund | replacement;
                                      no other keys. Order of the checks (nothing is sent unless every one passes):
                                        writeEnabled('return') -> input -> no other write running (shared with the three
                                        others) -> the token for exactly these choices -> a login token -> an earlier
                                        send of this page for this order that may still arrive (returnHeld): read again
                                        only (3 reads 2 s apart), never send; the request found is ok via 'reread' only
                                        when the same items, reason and outcome were asked (review fix); another choice:
                                        'held' while nothing shows, 'changed' (state 'held_seen', the hold ends) once the
                                        earlier request shows -> a fresh read: the order is "returnable" and
                                        every item has at least that many left -> a fresh random requestId -> send ONCE.
                                      After the service's answer:
                                        200 {ok, return: {name}} -> re-read at once, then every 2 s, 5 more times: that
                                          request is there: ok via 'answer'; else 'pending' (accepted; held for the page view).
                                        401 (refused before anything happened) -> a renewed login token, the SAME body and
                                          requestId once more (the service never makes two requests for one id); 401
                                          again: 'unauthorized'.
                                        409 with another code than busy, 404 unknown_order -> 'changed' (state: the code):
                                          nothing was made; the screen reads again. 403 writes_off -> 'off'. 429 ->
                                          'rate_limited'. Other 4xx (422 shopify_rejected, 400, 403) -> 'refused'.
                                        202 pending, 409 busy, 5xx, network, timeout -> 3 re-reads: a new request for the
                                          order -> ok via 'reread'; the service's own 502 / 503 (it made nothing) ->
                                          'refused'; else 'unknown' (held for resendHoldMs, 60 s).
                                      The whole call ends within 25 s (deadline: true; 'unknown' if it had sent, else
                                      'error'; nothing is sent after it). invalidate('returns') after every result.
                                      result: {ok: true, request: {name, status, createdAt, prefers, items}, via:
                                      'answer' | 'reread', sent} | {ok: false, reason, outcome, sent[, state, accepted,
                                      held, deadline, code, status]}. outcome 'none' (nothing made): off, bad_input, busy,
                                      not_confirmed, no_token, precheck_failed, changed, held, error; 'refused': refused,
                                      unauthorized, rate_limited; 'unknown' (Shopify may still have it; never send again
                                      without a new click, and the next call re-reads first): unknown, pending.
     nudgeSkio() -> boolean           read only: when the page is visible, window.dispatchEvent(new Event(
                                      'visibilitychange')), so Skio's own react-query refetches reward-state-info,
                                      tier-progress-info and customer-info (refetchOnWindowFocus 'always'). At most one per
                                      1.25 s (else false, nothing dispatched); false with the gate closed or the page hidden.
   Klaviyo (Rewards sign-up)
     klaviyo.subscribeEmail(email, {confirm}) -> Promise<{ok, reason?, status?}>
     klaviyo.subscribeSms(phone, {consent: true, confirm, email?}) -> Promise<{ok, reason?, status?}>
                                      (consent_required unless the caller passes consent: true AND the token
                                      recorded the ticked data-mk-consent="sms" box in its pop-up)
                                      POST https://a.klaviyo.com/client/subscriptions?company_id=<public key>,
                                      revision 2026-07-15, one channel per call, to that channel's list;
                                      custom_source 'MKS portal - Rewards page - Email v1' / '- SMS v1'. SMS sends
                                      sms.marketing only (+1 E.164); email (optional) only identifies the profile.
                                      Lists (r6): email 'RHNWWp' (single opt-in), sms 'VxmYUP' (DOUBLE opt-in:
                                      Klaviyo texts a confirmation first, so a 202 means accepted, not subscribed).
                                      ok on 2xx (Klaviyo answers 202). reason: off, invalid_email, invalid_phone,
                                      consent_required, not_configured (list switched off:
                                      MKPortalConfig.klaviyo.<channel>ListId false),
                                      busy, not_confirmed (nothing sent); bad_request (400), rate_limited (429),
                                      refused (other 4xx), error (5xx, network, timeout).
     klaviyo.validEmail(v) -> string|null / normalizePhone(v) -> '+1XXXXXXXXXX'|null / formatPhone(v) ->
                                      '(512) 555-0142' / ready(channel) -> boolean / source(channel) -> string
   Track package (pure; network only through tracking())
     track.hasMore(order) -> boolean  a /v1/orders shipment shows the full 10 scans (offer "Show all")
     track.merge(order, trackingData) -> shipments[]
                                      /v1/orders shipments with the /v1/tracking scans, matched by tracking
                                      number (by position only when neither side has numbers); each a copy
                                      with events, moreScans, full. Scan objects are copied as they are, so
                                      worker 2.3's events[].note reaches the delivery module unchanged
     track.url(shipment) -> string|null    first https tracking URL ("View on the tracking site")
     track.numbers(shipment) -> string[]
   Misc
     log(...args)                     console output only when localStorage
                                      'mk-portal-debug' === '1' (never pass tokens)
   ===================================================================== */
(function () {
  'use strict';
  if (window.MKPortal && window.MKPortal.__core) return;

  var VERSION = '1.6.0';          /* 1.0.1 (2026-10-11): tracking() for /v1/tracking, landing hash #mk-tracking;
                                     1.0.2 (2026-10-12, r4): input modality classes mk-kbd / mk-pointer (no focus ring after a
                                     mouse or touch press; rings everywhere else);
                                     1.1.0 (2026-10-12, batch 3): dialog (the shared pop-up), the one guarded write
                                     addSubscriptionLine(), klaviyo (sign-up), track (pure helpers for Track package);
                                     1.2.0 (2026-10-13, r6): dialog scrim and screen-reader text keep display:block under the
                                     store theme's :empty rule; Klaviyo list ids built in (email RHNWWp, sms VxmYUP) with
                                     the emailListId / smsListId === false kill switch; source 'MKS portal - Rewards page';
                                     r6 review (2026-10-13): the pop-up scroll lock sets html hidden and body clip (not body
                                     hidden), so the sticky menu and title bands stay put while a pop-up is open;
                                     1.2.1 (2026-10-15, r7): deliveryStatus() (how the last /v1/orders read ended, codes only),
                                     so the delivery module can wait out a 429 and say why a card is absent (E1);
                                     1.3.0 (2026-10-15, r8): the SECOND guarded write, changeNextOrderDate() (Skio's own
                                     updateNextBillingDateAction, verbatim), off unless MKPortalConfig.writes.nextDate === true;
                                     postWrite(token, kind, input) takes a kind from the frozen WRITES table of two, never a
                                     document; next-date confirmation tokens; read-only nextOrderDate(); pure helpers orderDay(),
                                     nextDateRange(), nextDateInstant(), nextDateEligible(), schedule.addYmd(), writeEnabled();
                                     r8 review (2026-10-16): the kill switch's load-time value is a boolean recorded once at
                                     load (an in-place edit of MKPortalConfig.writes cannot turn it on); confirm() reads the
                                     click through the browser's own Event getters (a look-alike object gets no token) and
                                     mints at most one token per click;
                                     1.4.0 (2026-10-16, r9, C9 option c): the THIRD write, claimReward(), Skio's own
                                     claim-credit-link request (REST, api.skio.com, the third token host, for that one
                                     URL), off unless MKPortalConfig.writes.claim === true (recorded at load like nextDate),
                                     only for the two reward ids of the claim guard snippet, only after Klaviyo accepted
                                     that channel in this page view, once per reward per page view, never retried;
                                     canClaim(), nudgeSkio() (visibilitychange: Skio refetches its own reward state);
                                     confirmation purpose {kind: 'claim', rewardId};
                                     1.4.1 (2026-10-17, r9 review): the sign-up is recorded for the customer logged in at
                                     its PRESS, and only when the same customer is still logged in at Klaviyo's answer
                                     (else none); canClaim() also asks that the customer logged in now is that one; a
                                     change of customer (cacheUserKey) forgets both records;
                                     1.5.0 (2026-10-18, r10, "Add products" catalogue): screen 'add-products' (hash
                                     #add-products, owned by the home module); catalogEnabled() (the loader's mk_portal_catalog
                                     -> MKPortalConfig.modules.catalog, exactly true, recorded at load); skioPages() (a read in
                                     pages of 24 with a page cap, through skio(): mutations refused); el() also passes
                                     placeholder, autocomplete, enterkeyhint, inputmode, spellcheck, autocapitalize, maxlength.
                                     The three writes and their guards are unchanged;
                                     1.5.1 (2026-10-19, r10 review): a pop-up's button counts only once the customer could
                                     see it: confirm() refuses the 2nd+ press of a double click (detail > 1), a press less
                                     than DLG_ARM_MS after the pop-up opened or its buttons were swapped (set() with body or
                                     actions), and a pointer press that began before that (a double click or two quick taps on
                                     a card's Add can no longer land on the pop-up's main button; set({arm: false}) redraws
                                     the same step in place without re-arming). Keyboard presses (detail
                                     0) count once the time has passed. detail and timeStamp are read through the browser's
                                     own getters (as isTrusted). The write documents and their guards are unchanged;
                                     1.5.2 (2026-10-26, R11, the add-on for customers without a subscription): deliveryStatus()
                                     also says 401 as soon as the FIRST /v1/orders answer is a 401, while the retry with a
                                     renewed token (up to 15 s) is still running, so Order history's one pass can tell "the
                                     service cannot match this customer" from "still waiting". Nothing else changed: the same
                                     read, retry, cache and result; the writes and their guards are unchanged;
                                     1.6.0 (2026-10-27, R11, returns in the portal; Jim's OK the same morning): the returns
                                     service as the FOURTH token host (one URL, RETURNS_URL), returns() / checkReturn() /
                                     returnHeld() (reads), sendReturn() (the fourth write, postReturn() its one exit), the
                                     switches modules.returns and writes.returns (recorded at load), the screens 'returns'
                                     and 'return'. The three other writes, their tables, guards and requests are unchanged */

  /* ---- fixed endpoints and identifiers (never parameters) ---- */
  var SKIO_URL = 'https://graphql.skio.com/v1/graphql';
  var DELIVERY_URL = 'https://mks-delivery-status.mksupps.workers.dev/v1/orders';
  var TRACKING_URL = 'https://mks-delivery-status.mksupps.workers.dev/v1/tracking';
  /* r9 (1.4.0): Skio's own claim request (claim page chunk app/(_authed)/credits/claim, helper 78109.U; chunk 3335 maps
     it to g("claim-credit-link","http") = https://api.skio.com/storefront-v2-http/claim-credit-link). The ONLY URL of
     api.skio.com this file ever sends to. */
  var CLAIM_URL = 'https://api.skio.com/storefront-v2-http/claim-credit-link';
  /* R11 (1.6.0): the returns service (Cloudflare Worker mks-returns, the same account as the delivery service). The ONLY
     URL of that host this file ever sends to: GET (returns()) and POST (postReturn()). */
  var RETURNS_URL = 'https://mks-returns.mksupps.workers.dev/v1/returns';
  /* every host the login token may ever go to, explicit (r9: three; R11: exactly four) */
  var TOKEN_HOSTS = Object.freeze(['graphql.skio.com', 'mks-delivery-status.mksupps.workers.dev', 'api.skio.com', 'mks-returns.mksupps.workers.dev']);
  var USER_POOL_ID = 'us-west-2_ky2hNEMAZ';
  var REGION = 'us-west-2';
  var CLIENT_ID = '26kili0dth6djs3e0a7digs3e2';
  var ISSUER = 'https://cognito-idp.us-west-2.amazonaws.com/' + USER_POOL_ID;
  var STORAGE_PREFIX = 'CognitoIdentityServiceProvider.' + CLIENT_ID + '.';

  /* ---- tunables ---- */
  var TOKEN_WAIT_MS = 15000;      /* poll this long for a usable token */
  var TOKEN_POLL_MS = 250;
  var TOKEN_MIN_LIFE_S = 30;      /* a token must be valid this much longer to be used */
  var TOKEN_MAX_LEN = 8000;
  var REQUEST_TIMEOUT_MS = 12000;
  var SKIO_TTL_MS = 60000;
  var DELIVERY_TTL_MS = 120000;
  var ROUTE_POLL_MS = 250;
  var POINTS_PER_DOLLAR = 100;    /* Skio > Loyalty > Credits: 1 USD = 100 points */
  var TIER_PREFIX = /^\s*Rewards Tier:\s*/i;

  /* Tier program (Skio > Loyalty, handoff 2.2). Modules prefer Skio's TierLevel data;
     this table is the fallback and the source for art / short names. */
  var TIERS = [
    { key: 'bone', name: 'Bone', minStreak: 0, pointsPerDollar: 1, perks: [] },
    { key: 'stone', name: 'Stone', minStreak: 3, pointsPerDollar: 3, perks: [] },
    { key: 'bronze', name: 'Bronze', minStreak: 6, pointsPerDollar: 5, perks: [] },
    { key: 'iron', name: 'Iron', minStreak: 9, pointsPerDollar: 8, perks: ['Streak Restoration'] },
    { key: 'steel', name: 'Steel', minStreak: 12, pointsPerDollar: 10, perks: ['Streak Restoration', 'Price Lock'] }
  ];
  var ART_FALLBACK = 'https://mksupps.com/cdn/shop/t/57/assets/mk-tier-';

  var config = (window.MKPortalConfig && typeof window.MKPortalConfig === 'object') ? window.MKPortalConfig : {};
  /* optional override from the loader config (tests use a short wait) */
  if (typeof config.tokenWaitMs === 'number' && config.tokenWaitMs >= 0 && config.tokenWaitMs <= 60000) TOKEN_WAIT_MS = config.tokenWaitMs;
  if (typeof config.requestTimeoutMs === 'number' && config.requestTimeoutMs >= 200 && config.requestTimeoutMs <= 60000) REQUEST_TIMEOUT_MS = config.requestTimeoutMs;
  /* r8 kill switch: read ONCE at load, as a plain boolean (later in-place edits of MKPortalConfig.writes cannot
     turn it on; `config` is the same object as window.MKPortalConfig, not a copy). writeEnabled() uses it. */
  var NEXT_DATE_AT_LOAD = switchSaysOn(config);
  /* r9 (1.4.0): the claim write's own kill switch (writes.claim), recorded the same way; and the two reward ids the
     claim guard snippet holds, read once now (the guard is in <head>, before this deferred file runs) */
  var CLAIM_AT_LOAD = claimSwitchOn(config);
  var CLAIM_IDS = readClaimGuardIds();
  /* r10 (1.5.0): the catalogue switch (the loader's mk_portal_catalog -> MKPortalConfig.modules.catalog), recorded the
     same way: exactly true at load; catalogEnabled() also asks for it in the loaded config and in window.MKPortalConfig now */
  var CATALOG_AT_LOAD = catalogSwitchOn(config);
  /* R11 (1.6.0): the returns switches (the loader's mk_portal_returns -> modules.returns, mk_portal_returns_writes ->
     writes.returns), recorded the same way: exactly true at load; returnsEnabled() / writeEnabled('return') also ask
     for them in the loaded config and in window.MKPortalConfig now */
  var RETURNS_AT_LOAD = returnsSwitchOn(config);
  var RETURN_WRITES_AT_LOAD = returnWritesSwitchOn(config);

  /* ------------------------------------------------------------------ */
  /* gate + debug log                                                     */
  /* ------------------------------------------------------------------ */
  function on() {
    try { return document.documentElement.classList.contains('mk-portal-on'); } catch (e) { return false; }
  }
  function debugOn() {
    try { return window.localStorage.getItem('mk-portal-debug') === '1'; } catch (e) { return false; }
  }
  function log() {
    if (!debugOn()) return;
    try { console.log.apply(console, ['[mk-portal]'].concat([].slice.call(arguments))); } catch (e) { /* ignore */ }
  }
  function report(where, err) {
    log(where + ' failed:', err && err.message ? err.message : String(err));
  }

  /* ------------------------------------------------------------------ */
  /* small utils                                                          */
  /* ------------------------------------------------------------------ */
  function copy(v) {
    if (v == null || typeof v !== 'object') return v;
    try { return JSON.parse(JSON.stringify(v)); } catch (e) { return null; }
  }
  function sleep(ms) { return new Promise(function (r) { setTimeout(r, ms); }); }
  function now() { return Date.now(); }

  /* ------------------------------------------------------------------ */
  /* login token                                                          */
  /* ------------------------------------------------------------------ */
  function getItem(k) { try { return window.localStorage.getItem(k); } catch (e) { return null; } }

  function decodeJwtPayload(token) {
    try {
      var parts = token.split('.');
      if (parts.length !== 3) return null;
      for (var i = 0; i < 3; i++) if (!/^[A-Za-z0-9_-]+$/.test(parts[i])) return null;
      var p = parts[1].replace(/-/g, '+').replace(/_/g, '/');
      p += '==='.slice((p.length + 3) % 4);
      var bin = atob(p);
      var bytes = new Uint8Array(bin.length);
      for (var j = 0; j < bin.length; j++) bytes[j] = bin.charCodeAt(j);
      var obj = JSON.parse(new TextDecoder().decode(bytes));
      return obj && typeof obj === 'object' && !Array.isArray(obj) ? obj : null;
    } catch (e) { return null; }
  }

  function usable(payload, nowSec) {
    return !!payload &&
      payload.iss === ISSUER &&
      payload.aud === CLIENT_ID &&
      payload.token_use === 'id' &&
      typeof payload.exp === 'number' && payload.exp > nowSec + TOKEN_MIN_LIFE_S;
  }

  /* Reads the current token without waiting. Returns {token, payload} or null. */
  function readToken(exclude) {
    var token = null;
    var last = getItem(STORAGE_PREFIX + 'LastAuthUser');
    if (last) token = getItem(STORAGE_PREFIX + last + '.idToken');
    if (!token) {
      var keys = [];
      try {
        for (var i = 0; i < window.localStorage.length; i++) {
          var k = window.localStorage.key(i);
          if (k && k.indexOf(STORAGE_PREFIX) === 0 && /\.idToken$/.test(k)) keys.push(k);
        }
      } catch (e) { keys = []; }
      if (keys.length === 1) token = getItem(keys[0]);   /* more than one = ambiguous: none */
    }
    if (!token || typeof token !== 'string' || token.length > TOKEN_MAX_LEN) return null;
    if (exclude && token === exclude) return null;
    var payload = decodeJwtPayload(token);
    if (!usable(payload, now() / 1000)) return null;
    return { token: token, payload: payload };
  }

  var pendingToken = null;
  function waitForToken(opts) {
    opts = opts || {};
    var timeout = typeof opts.timeout === 'number' && opts.timeout >= 0 ? opts.timeout : TOKEN_WAIT_MS;
    var exclude = opts.exclude || null;
    return new Promise(function (resolve) {
      var deadline = now() + timeout;
      (function poll() {
        var t = null;
        try { t = readToken(exclude); } catch (e) { t = null; }
        if (t) return resolve(t);
        if (now() >= deadline) return resolve(null);
        setTimeout(poll, Math.min(TOKEN_POLL_MS, Math.max(0, deadline - now())));
      })();
    });
  }
  function tokenRecord(opts) {
    if (!on()) return Promise.resolve(null);
    opts = opts || {};
    if (opts.exclude || typeof opts.timeout === 'number') return waitForToken(opts);
    if (!pendingToken) {
      pendingToken = waitForToken({}).then(function (r) { pendingToken = null; return r; });
    }
    return pendingToken;
  }
  function getIdToken(opts) {
    return tokenRecord(opts).then(function (r) { return r ? r.token : null; }, function () { return null; });
  }
  function claims(opts) {
    return tokenRecord(opts).then(function (r) { return r ? copy(r.payload) : null; }, function () { return null; });
  }
  function storefrontUserId(opts) {
    return claims(opts).then(function (c) {
      var id = c && c['custom:storefrontUserId'];
      return typeof id === 'string' && /^[0-9a-f-]{36}$/i.test(id) ? id : null;
    });
  }

  /* ------------------------------------------------------------------ */
  /* memory cache                                                         */
  /* ------------------------------------------------------------------ */
  var store = {};      /* key -> {value, at} */
  var inflight = {};   /* key -> Promise */
  function cached(key, ttl, fresh, load, keep) {
    var hit = store[key];
    if (!fresh && hit && now() - hit.at < ttl) return Promise.resolve(copy(hit.value));
    if (!fresh && inflight[key]) return inflight[key].then(copy);
    var gen = cacheGen;
    var p = Promise.resolve().then(load).then(function (value) {
      if (inflight[key] === p) delete inflight[key];
      if (keep(value) && gen === cacheGen) store[key] = { value: copy(value), at: now() };
      return value;
    }, function (err) {
      if (inflight[key] === p) delete inflight[key];
      throw err;
    });
    inflight[key] = p;
    return p.then(copy);
  }
  var cacheGen = 0;
  /* Cache keys start with the customer's id (custom:storefrontUserId from the token), so one
     customer can never be served another's answers on a shared device. Skio's logout is a
     client-side route change (no page load): when the token's customer changes, everything
     cached is dropped. */
  var cacheUser = null;
  function cacheUserKey(rec) {
    var p = rec && rec.payload ? rec.payload : {};
    var id = String(p['custom:storefrontUserId'] || p.sub || '').slice(0, 80);
    if (cacheUser !== null && cacheUser !== id) {
      invalidate();
      /* r9 review (1.4.1): another customer: the sign-ups recorded in this page view are not theirs */
      try { signedUp.email = signedUp.sms = null; } catch (e) { /* ignore */ }
    }
    cacheUser = id;
    return id;
  }
  function invalidate(kind) {
    cacheGen++;
    Object.keys(store).forEach(function (k) { if (!kind || k.indexOf(kind + ':') === 0) delete store[k]; });
    Object.keys(inflight).forEach(function (k) { if (!kind || k.indexOf(kind + ':') === 0) delete inflight[k]; });
  }

  /* ------------------------------------------------------------------ */
  /* network                                                              */
  /* ------------------------------------------------------------------ */
  /* fetch + read the body as JSON -> {status, body}. The REQUEST_TIMEOUT_MS limit covers the
     whole exchange, body included (a stalled body used to hang the request, and every identical
     request sharing it, for the rest of the page view). On timeout the request is aborted and the
     promise rejects, so the shared in-flight entry is dropped and a retry makes a new request. */
  function timedRequest(url, init) {
    var ctrl = typeof AbortController === 'function' ? new AbortController() : null;
    if (ctrl) init.signal = ctrl.signal;
    var timer = null;
    var limit = new Promise(function (resolve, reject) {
      timer = setTimeout(function () {
        try { if (ctrl) ctrl.abort(); } catch (e) { /* ignore */ }
        reject(new Error('timeout'));
      }, REQUEST_TIMEOUT_MS);
    });
    var work = Promise.resolve().then(function () { return window.fetch(url, init); }).then(function (res) {
      return readJson(res).then(function (body) { return { status: res.status, body: body }; });
    });
    return Promise.race([work, limit]).then(function (r) {
      clearTimeout(timer);
      return r;
    }, function (err) {
      clearTimeout(timer);
      throw err;
    });
  }
  function readJson(res) {
    return res.text().then(function (t) { try { return JSON.parse(t); } catch (e) { return undefined; } }, function () { return undefined; });
  }
  function gqlFail(message, code) { return { data: null, errors: [{ message: message, extensions: { code: code || 'mk-portal' } }] }; }

  /* true when the document holds a mutation or subscription operation.
     One left-to-right pass, like GraphQL's own lexer: comments ('#' to end of line), block
     strings ('"""', with \""" escapes) and strings are skipped where they START, so a quote
     inside a comment (or a '#' inside a string) cannot hide an operation keyword. Only words at
     brace depth 0 (operation keywords and names) are looked at. */
  function isWriteOperation(query) {
    var s = String(query), n = s.length, i = 0, depth = 0, c, j, word;
    while (i < n) {
      c = s.charAt(i);
      if (c === '#') {
        while (i < n && s.charAt(i) !== '\n' && s.charAt(i) !== '\r') i++;
        continue;
      }
      if (c === '"') {
        if (s.substr(i, 3) === '"""') {
          j = i + 3;
          while (j < n && !(s.substr(j, 3) === '"""' && s.charAt(j - 1) !== '\\')) j++;
          i = j < n ? j + 3 : n;   /* unterminated: the server refuses the document */
          continue;
        }
        j = i + 1;
        while (j < n && s.charAt(j) !== '"' && s.charAt(j) !== '\n' && s.charAt(j) !== '\r') j += s.charAt(j) === '\\' ? 2 : 1;
        i = j + 1;
        continue;
      }
      if (c === '{' || c === '(' || c === '[') { depth++; i++; continue; }
      if (c === '}' || c === ')' || c === ']') { depth--; i++; continue; }
      if (/[A-Za-z_]/.test(c)) {
        word = /^[A-Za-z_][A-Za-z0-9_]*/.exec(s.slice(i, i + 64))[0];
        if (depth === 0 && (word === 'mutation' || word === 'subscription')) return true;
        i += word.length;
        while (i < n && /[A-Za-z0-9_]/.test(s.charAt(i))) i++;
        continue;
      }
      i++;
    }
    return false;
  }

  function isAuthFailure(status, body) {
    if (status === 401 || status === 403) return true;
    var errs = body && Array.isArray(body.errors) ? body.errors : [];
    for (var i = 0; i < errs.length; i++) {
      var e = errs[i] || {};
      var code = e.extensions && e.extensions.code;
      /* Hasura: {extensions:{code:'invalid-jwt'}, message:'Could not verify JWT: JWTExpired'} */
      if (code === 'invalid-jwt' || /\bJW[TS]/.test(String(e.message || ''))) return true;
    }
    return false;
  }

  function postSkio(token, query, variables) {
    return timedRequest(SKIO_URL, {
      method: 'POST',
      /* header NAMES must stay lowercase: capitalised Region / Userpoolid over HTTP/1.1
         silently give the anonymous role (verified live 2026-10-10) */
      headers: {
        'content-type': 'application/json',
        'region': REGION,
        'userpoolid': USER_POOL_ID,
        'authorization': 'Bearer ' + token
      },
      body: JSON.stringify({ query: query, variables: variables || {} }),
      credentials: 'omit',
      cache: 'no-store',
      mode: 'cors',
      redirect: 'error',
      referrerPolicy: 'no-referrer'
    });
  }

  function skio(query, variables, opts) {
    opts = opts || {};
    try {
      if (!on()) return Promise.resolve(gqlFail('portal modules are off', 'mk-off'));
      if (typeof query !== 'string' || !query.trim()) return Promise.resolve(gqlFail('empty query', 'mk-bad-query'));
      if (isWriteOperation(query)) return Promise.resolve(gqlFail('read-only: mutations are not sent from theme modules', 'mk-read-only'));
      var vars = variables && typeof variables === 'object' ? variables : {};
      var ttl = typeof opts.ttl === 'number' ? opts.ttl : SKIO_TTL_MS;
      return tokenRecord().then(function (rec) {
        if (!rec) return gqlFail('no login token', 'mk-no-token');
        var key = 'skio:' + cacheUserKey(rec) + '\u0000' + query + '\u0000' + JSON.stringify(vars);
        return cached(key, ttl, !!opts.fresh, function () {
          var used = rec.token;
          return postSkio(used, query, vars).then(function (r) {
            if (!isAuthFailure(r.status, r.body)) return r;
            return waitForToken({ exclude: used }).then(function (rec2) {
              used = null;
              return rec2 ? postSkio(rec2.token, query, vars) : r;
            });
          }).then(function (r) {
            var b = r.body;
            if (!b || typeof b !== 'object' || Array.isArray(b)) return gqlFail('HTTP ' + r.status, 'mk-http');
            var errors = Array.isArray(b.errors) && b.errors.length ? b.errors : null;
            if (r.status >= 400 && !errors) errors = [{ message: 'HTTP ' + r.status, extensions: { code: 'mk-http' } }];
            return { data: b.data === undefined ? null : b.data, errors: errors };
          });
        }, function (v) { return v && !v.errors && v.data != null; });
      }).then(null, function (err) {
        report('skio', err);
        return gqlFail('network error', 'mk-network');
      });
    } catch (e) {
      report('skio', e);
      return Promise.resolve(gqlFail('error', 'mk-error'));
    }
  }

  /* r10 (1.5.0): a list read in pages, through skio() (so mutations are refused, the login token goes only to Skio's
     GraphQL host, answers are cached per customer and identical requests in flight are shared). The query must declare
     $limit and $offset; this function sets them (offset = page x pageSize). Pages are read one after another until a
     short page; the last allowed page asks for one more item, so `capped` is true only when there really is more. */
  var PAGE_SIZE = 24, PAGE_MAX = 5;
  function pagesFail(code, partial) {
    return { ok: false, items: partial || [], pages: 0, capped: false, data: null, errors: gqlFail(code, code).errors };
  }
  function boundedInt(v, lo, hi, dflt) {
    var n = typeof v === 'number' && isFinite(v) ? Math.floor(v) : dflt;
    return Math.max(lo, Math.min(hi, n));
  }
  function skioPages(query, variables, opts) {
    opts = opts || {};
    try {
      if (!on()) return Promise.resolve(pagesFail('mk-off'));
      if (typeof query !== 'string' || !/\$limit\s*:/.test(query) || !/\$offset\s*:/.test(query)) return Promise.resolve(pagesFail('mk-bad-query'));
      if (isWriteOperation(query)) return Promise.resolve(pagesFail('mk-read-only'));
      if (typeof opts.pick !== 'function') return Promise.resolve(pagesFail('mk-bad-input'));
      var size = boundedInt(opts.pageSize, 1, 100, PAGE_SIZE), max = boundedInt(opts.maxPages, 1, 10, PAGE_MAX);
      var base = {};
      if (variables && typeof variables === 'object' && !Array.isArray(variables)) {
        Object.keys(variables).forEach(function (k) { if (k !== 'limit' && k !== 'offset') base[k] = variables[k]; });
      }
      var ropts = { fresh: !!opts.fresh };
      if (typeof opts.ttl === 'number') ropts.ttl = opts.ttl;
      var items = [], seen = {}, pages = 0, first = null;
      var page = function (i) {
        var last = i === max - 1, vars = {};
        Object.keys(base).forEach(function (k) { vars[k] = base[k]; });
        vars.limit = last ? size + 1 : size;
        vars.offset = i * size;
        return skio(query, vars, ropts).then(function (r) {
          if (!r || r.errors || r.data == null) return { ok: false, items: items, pages: pages, capped: false, data: first, errors: (r && r.errors) || gqlFail('no answer', 'mk-error').errors };
          var list = null;
          try { list = opts.pick(r.data); } catch (e) { list = null; }
          if (!Array.isArray(list)) return { ok: false, items: items, pages: pages, capped: false, data: first, errors: gqlFail('unexpected answer', 'mk-bad-answer').errors };
          if (i === 0) first = r.data;
          pages++;
          list.slice(0, size).forEach(function (x) {
            var id = x && typeof x === 'object' && typeof x.id === 'string' ? x.id : null;
            if (id !== null) { if (seen[id]) return; seen[id] = true; }
            items.push(x);
          });
          if (list.length < size || (last && list.length <= size)) return { ok: true, items: items, pages: pages, capped: false, data: first, errors: null };
          if (last) return { ok: true, items: items, pages: pages, capped: true, data: first, errors: null };
          return page(i + 1);
        });
      };
      return page(0).then(null, function (err) {
        report('skioPages', err);
        return pagesFail('mk-network', items);
      });
    } catch (e) {
      report('skioPages', e);
      return Promise.resolve(pagesFail('mk-error'));
    }
  }

  /* r10 (1.5.0): the catalogue ("Add products") switch. true only with the gate on, MKPortalConfig.modules.catalog
     exactly true when the core loaded (CATALOG_AT_LOAD) and still now (loaded config and window.MKPortalConfig), and
     the home module not switched off (modules.home !== false; the home module owns the screen). Missing key = off. */
  function catalogSwitchOn(c) {
    try {
      return !!c && typeof c === 'object' && !!c.modules && typeof c.modules === 'object' && c.modules.catalog === true && c.modules.home !== false;
    } catch (e) { return false; }
  }
  function catalogEnabled() {
    try { return on() && CATALOG_AT_LOAD === true && catalogSwitchOn(config) && catalogSwitchOn(window.MKPortalConfig); } catch (e) { return false; }
  }

  function validDelivery(b) {
    return !!b && typeof b === 'object' && Array.isArray(b.orders) &&
      b.orders.every(function (o) { return o && typeof o === 'object' && typeof o.status === 'string'; });
  }
  function getDelivery(token, limit) {
    return timedRequest(DELIVERY_URL + '?limit=' + limit, {
      method: 'GET',
      headers: { 'authorization': 'Bearer ' + token },
      credentials: 'omit',
      cache: 'no-store',
      mode: 'cors',
      redirect: 'error',
      referrerPolicy: 'no-referrer'
    });
  }
  /* r7 (1.2.1): how the last network read of /v1/orders ended (codes only, never a body or the token) */
  var lastDelivery = null;
  function noteDelivery(status) { lastDelivery = { status: status, at: now() }; }
  function deliveryStatus() { return lastDelivery ? { status: lastDelivery.status, at: lastDelivery.at } : null; }
  function delivery(limit, opts) {
    opts = opts || {};
    try {
      if (!on()) return Promise.resolve(null);
      var n = parseInt(limit, 10);
      if (isNaN(n)) n = 3;
      n = Math.max(1, Math.min(5, n));
      var ttl = typeof opts.ttl === 'number' ? opts.ttl : DELIVERY_TTL_MS;
      return tokenRecord().then(function (rec) {
        if (!rec) { noteDelivery('no-token'); return null; }
        var prefix = 'delivery:' + cacheUserKey(rec) + ':';
        if (!opts.fresh) {
          /* a cached answer for a larger limit also answers a smaller one */
          for (var m = 5; m > n; m--) {
            var hit = store[prefix + m];
            if (hit && now() - hit.at < ttl) {
              var v = copy(hit.value);
              v.orders = v.orders.slice(0, n);
              return v;
            }
          }
        }
        return cached(prefix + n, ttl, !!opts.fresh, function () {
          var used = rec.token;
          return getDelivery(used, n).then(function (r) {
            if (r.status !== 401) return r;
            noteDelivery(401);   /* 1.5.2: known now; the retry below may wait up to 15 s for a renewed token */
            return waitForToken({ exclude: used }).then(function (rec2) {
              used = null;
              return rec2 ? getDelivery(rec2.token, n) : r;
            });
          }).then(function (r) {
            var ok = r.status === 200 && validDelivery(r.body);
            noteDelivery(ok || r.status !== 200 ? r.status : 'bad-body');
            return ok ? r.body : null;
          });
        }, validDelivery);
      }).then(null, function (err) {
        noteDelivery(err && err.message === 'timeout' ? 'timeout' : 'network');
        report('delivery', err);
        return null;
      });
    } catch (e) {
      report('delivery', e);
      return Promise.resolve(null);
    }
  }

  /* GET /v1/tracking?order=<digits> (service 2.2): every scan of ONE of the customer's own orders.
     Same token, header, timeout, 401-retry and cache rules as delivery(); only answers with
     ok: true are cached. One request at a time: a second order waits until the first settled
     (the service also runs one uncached order per customer at a time). Callers ask only when the
     customer presses "Show all tracking updates", never in advance. */
  var trackingQueue = Promise.resolve();
  function validTracking(b) {
    return !!b && typeof b === 'object' && !Array.isArray(b) && Array.isArray(b.fulfillments) &&
      b.fulfillments.every(function (f) { return f && typeof f === 'object' && Array.isArray(f.events); });
  }
  function getTracking(token, digits) {
    return timedRequest(TRACKING_URL + '?order=' + digits, {
      method: 'GET',
      headers: { 'authorization': 'Bearer ' + token },
      credentials: 'omit',
      cache: 'no-store',
      mode: 'cors',
      redirect: 'error',
      referrerPolicy: 'no-referrer'
    });
  }
  function trackingDigits(order) {
    var s = typeof order === 'number' ? String(order) : (typeof order === 'string' ? order.trim().replace(/^#/, '') : '');
    return /^[1-9][0-9]{0,11}$/.test(s) ? s : null;
  }
  function trackingReason(status) {
    if (status === 404) return 'not_found';
    if (status === 429) return 'rate_limited';
    if (status === 400) return 'bad_request';
    if (status === 401 || status === 403) return 'unauthorized';
    return 'error';
  }
  function tracking(order, opts) {
    opts = opts || {};
    try {
      if (!on()) return Promise.resolve({ ok: false, reason: 'off' });
      var digits = trackingDigits(order);
      if (!digits) return Promise.resolve({ ok: false, reason: 'bad_request' });
      var ttl = typeof opts.ttl === 'number' ? opts.ttl : DELIVERY_TTL_MS;
      return tokenRecord().then(function (rec) {
        if (!rec) return { ok: false, reason: 'no_token' };
        var key = 'tracking:' + cacheUserKey(rec) + ':' + digits;
        return cached(key, ttl, !!opts.fresh, function () {
          /* one at a time: wait for the request before this one (whatever its outcome) */
          var run = trackingQueue.then(function () {
            var used = rec.token;
            return getTracking(used, digits).then(function (r) {
              if (r.status !== 401) return r;
              return waitForToken({ exclude: used }).then(function (rec2) {
                used = null;
                return rec2 ? getTracking(rec2.token, digits) : r;
              });
            }).then(function (r) {
              if (r.status === 200 && validTracking(r.body)) return { ok: true, data: r.body };
              return { ok: false, reason: r.status === 200 ? 'error' : trackingReason(r.status) };
            });
          });
          trackingQueue = run.then(function () {}, function () {});
          return run;
        }, function (v) { return !!v && v.ok === true; });
      }).then(null, function (err) {
        report('tracking', err);
        return { ok: false, reason: 'error' };
      });
    } catch (e) {
      report('tracking', e);
      return Promise.resolve({ ok: false, reason: 'error' });
    }
  }

  /* ------------------------------------------------------------------ */
  /* returns (R11, 1.6.0): GET RETURNS_URL from the returns service.     */
  /* Off unless the loader's mk_portal_returns is exactly true           */
  /* (returnsEnabled). The login token goes to RETURNS_URL only, as a    */
  /* Bearer header, exactly as to the delivery service.                  */
  /* ------------------------------------------------------------------ */
  var RETURNS_TTL_MS = 60000;
  function returnsSwitchOn(c) {
    try { return !!c && typeof c === 'object' && !!c.modules && typeof c.modules === 'object' && c.modules.returns === true; } catch (e) { return false; }
  }
  function returnWritesSwitchOn(c) {
    try { return !!c && typeof c === 'object' && !!c.writes && typeof c.writes === 'object' && c.writes.returns === true; } catch (e) { return false; }
  }
  function returnsEnabled() {
    try { return on() && RETURNS_AT_LOAD === true && returnsSwitchOn(config) && returnsSwitchOn(window.MKPortalConfig); } catch (e) { return false; }
  }
  function validReturns(b) {
    return !!b && typeof b === 'object' && !Array.isArray(b) && Array.isArray(b.orders) && Array.isArray(b.reasons) &&
      b.orders.every(function (o) {
        return o && typeof o === 'object' && typeof o.name === 'string' && typeof o.state === 'string' &&
          Array.isArray(o.items) && Array.isArray(o.unavailable) && Array.isArray(o.requests);
      });
  }
  function getReturns(token) {
    if (!tokenHostOk(RETURNS_URL)) return Promise.reject(new Error('host not allowed'));
    return timedRequest(RETURNS_URL, {
      method: 'GET',
      headers: { 'authorization': 'Bearer ' + token },
      credentials: 'omit',
      cache: 'no-store',
      mode: 'cors',
      redirect: 'error',
      referrerPolicy: 'no-referrer'
    });
  }
  function returnsReason(r) {
    if (r.status === 401) return 'unauthorized';
    if (r.status === 403 && r.body && typeof r.body === 'object' && r.body.code === 'no_match') return 'no_match';
    if (r.status === 429) return 'rate_limited';
    return 'error';
  }
  function returns(opts) {
    opts = opts || {};
    try {
      if (!returnsEnabled()) return Promise.resolve({ ok: false, reason: 'off' });
      var ttl = typeof opts.ttl === 'number' ? opts.ttl : RETURNS_TTL_MS;
      return tokenRecord().then(function (rec) {
        if (!rec) return { ok: false, reason: 'no_token' };
        return cached('returns:' + cacheUserKey(rec), ttl, !!opts.fresh, function () {
          var used = rec.token;
          return getReturns(used).then(function (r) {
            if (r.status !== 401) return r;
            return waitForToken({ exclude: used }).then(function (rec2) {
              used = null;
              return rec2 ? getReturns(rec2.token) : r;
            });
          }).then(function (r) {
            if (r.status === 200 && validReturns(r.body)) return { ok: true, data: r.body };
            return { ok: false, reason: r.status === 200 ? 'error' : returnsReason(r), status: r.status };
          });
        }, function (v) { return !!v && v.ok === true; });
      }).then(function (v) {
        /* a hold whose request this answer shows is over (sendReturn() keeps its own while it runs) */
        if (v && v.ok === true && !writing) dropSeenHolds(v.data);
        return v;
      }).then(null, function (err) {
        report('returns', err);
        return { ok: false, reason: 'error' };
      });
    } catch (e) {
      report('returns', e);
      return Promise.resolve({ ok: false, reason: 'error' });
    }
  }

  /* fresh data after the customer changes something through Skio's own UI */
  function watchActions() {
    var handler = function (ev) {
      try {
        if (!on()) return;
        var t = ev.target && ev.target.closest ? ev.target : null;
        if (!t) return;
        if (t.closest('[data-mk-invalidate]')) { invalidate(); return; }
        var b = t.closest('button, [role="button"], [role="menuitem"], [role="option"], input[type="submit"]');
        if (!b || !b.closest('#skio-root, #skio-portal')) return;
        if (b.closest('[data-mk-module]')) return;
        /* logging out: nothing cached may outlive the session (Skio's logout reloads nothing) */
        if (b.closest('[data-skio-name="navbar-logout-button"]')) { invalidate(); return; }
        if (b.closest('[data-skio-name^="navbar"]')) return;
        invalidate();
      } catch (e) { /* ignore */ }
    };
    document.addEventListener('click', handler, true);
  }

  /* ------------------------------------------------------------------ */
  /* input modality (r4): html.mk-pointer right after a press, html.mk-kbd after a key   */
  /* ------------------------------------------------------------------ */
  var KBD_CLASS = 'mk-kbd', PTR_CLASS = 'mk-pointer';
  var KBD_KEYS = /^(Tab|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Up|Down|Left|Right|Enter| |Spacebar|Escape|Esc|Home|End|PageUp|PageDown)$/;
  var PTR_QUIET_MS = 1000;        /* focus this long after a press counts as keyboard focus when it is :focus-visible */
  var lastPointer = 0;
  function hasClass(c) { try { return document.documentElement.classList.contains(c); } catch (e) { return false; } }
  function keyboard() { return hasClass(KBD_CLASS); }
  function pointer() { return hasClass(PTR_CLASS); }
  function setKeyboard(onOff) {
    try {
      var c = document.documentElement.classList;
      if (c.contains(KBD_CLASS) !== onOff) c[onOff ? 'add' : 'remove'](KBD_CLASS);
      if (c.contains(PTR_CLASS) !== !onOff) c[onOff ? 'remove' : 'add'](PTR_CLASS);
    } catch (e) { /* ignore */ }
  }
  function watchModality() {
    var opts = { capture: true, passive: true };
    var onKey = function (ev) {
      try {
        if (!ev || ev.metaKey || ev.ctrlKey || ev.altKey) return;
        if (KBD_KEYS.test(String(ev.key))) setKeyboard(true);
      } catch (e) { /* ignore */ }
    };
    var onPointer = function () { lastPointer = Date.now(); setKeyboard(false); };
    /* keyboard focus the page saw no key for (first Tab from the browser's toolbar, assistive tech):
       the browser says :focus-visible and no press came just before it */
    var onFocusIn = function (ev) {
      try {
        if (!pointer() || Date.now() - lastPointer < PTR_QUIET_MS) return;
        var t = ev && ev.target;
        if (!t || t.nodeType !== 1 || t.getAttribute('tabindex') === '-1') return;
        if (t.matches(':focus-visible')) setKeyboard(true);
      } catch (e) { /* no :focus-visible support: leave it */ }
    };
    window.addEventListener('keydown', onKey, opts);
    ['pointerdown', 'mousedown', 'touchstart'].forEach(function (t) { window.addEventListener(t, onPointer, opts); });
    document.addEventListener('focusin', onFocusIn, true);
  }

  /* ------------------------------------------------------------------ */
  /* routes                                                               */
  /* ------------------------------------------------------------------ */
  var PAGE_ROOTS = [
    ['subscription-details-page', 'subscription'],
    ['subscriptions-page', 'subscriptions'],
    ['loyalty-page', 'loyalty'],
    ['orders-page', 'orders'],
    ['profile-page', 'profile'],
    ['login-layout', 'login']
  ];
  function parsePath(pathname) {
    try {
      var p = String(pathname || '').split(/[?#]/)[0].replace(/\/+$/, '');
      var m = /\/a\/account(?:\/(.*))?$/.exec(p);
      if (!m) return null;
      var parts = (m[1] || '').split('/').filter(Boolean);
      if (!parts.length) return { page: 'subscriptions', subscriptionId: null };
      var head = parts[0].toLowerCase();
      if (head === 'subscriptions' && parts.length === 1) return { page: 'subscriptions', subscriptionId: null };
      if (head === 'subscriptions' && parts.length === 2) {
        var id = null;
        try { id = decodeURIComponent(parts[1]); } catch (e) { id = null; }
        return { page: 'subscription', subscriptionId: id && /^[A-Za-z0-9-]{1,80}$/.test(id) ? id : null };
      }
      if (parts.length === 1 && /^(loyalty|orders|profile|login)$/.test(head)) return { page: head, subscriptionId: null };
      return { page: 'other', subscriptionId: null };
    } catch (e) { return null; }
  }
  function pageFromDom() {
    for (var i = 0; i < PAGE_ROOTS.length; i++) {
      if (document.querySelector('#skio-root [data-skio-name="' + PAGE_ROOTS[i][0] + '"]')) return PAGE_ROOTS[i][1];
    }
    return null;
  }
  var routeSeq = 0;
  var lastPath = null;
  function route() {
    var path = location.pathname;
    var r = parsePath(path) || { page: pageFromDom() || 'other', subscriptionId: null };
    return { page: r.page, subscriptionId: r.subscriptionId, path: path, seq: routeSeq };
  }
  var routeListeners = [];
  var routeTimer = null;
  var prevRoute = null;
  function checkRoute() {
    var path = location.pathname;
    if (path === lastPath) return;
    lastPath = path;
    routeSeq++;
    var r = route(), prev = prevRoute;
    prevRoute = r;
    if (r.page === 'login') invalidate();
    routeListeners.slice().forEach(function (cb) { try { cb(r, prev); } catch (e) { report('onRoute callback', e); } });
  }
  function startRouteWatch() {
    if (routeTimer) return;
    lastPath = location.pathname;
    prevRoute = route();
    routeTimer = setInterval(checkRoute, ROUTE_POLL_MS);
    window.addEventListener('popstate', checkRoute);
    /* back/forward cache: the page comes back exactly as it was left, possibly before a change
       the customer made in Skio's dialog (skip, date). Drop the cache and re-announce the route
       so every module starts again with fresh data. */
    window.addEventListener('pageshow', function (e) {
      if (e && e.persisted) { invalidate(); lastPath = null; }
      checkRoute();
    });
  }
  function onRoute(cb) {
    if (typeof cb !== 'function') return function () {};
    if (!on()) return function () {};
    startRouteWatch();
    routeListeners.push(cb);
    setTimeout(function () {
      if (routeListeners.indexOf(cb) < 0) return;
      try { cb(route(), null); } catch (e) { report('onRoute callback', e); }
    }, 0);
    return function () { var i = routeListeners.indexOf(cb); if (i >= 0) routeListeners.splice(i, 1); };
  }
  function waitFor(selector, opts) {
    opts = opts || {};
    var timeout = typeof opts.timeout === 'number' ? opts.timeout : 10000;
    var root = opts.root || document;
    var sameRoute = opts.sameRoute !== false;
    if (sameRoute && on()) startRouteWatch();
    var seq = routeSeq;
    return new Promise(function (resolve) {
      var done = false, obs = null, timer = null, routeTick = null;
      function finish(v) {
        if (done) return;
        done = true;
        if (obs) obs.disconnect();
        if (timer) clearTimeout(timer);
        if (routeTick) clearInterval(routeTick);
        resolve(v);
      }
      function check() {
        if (sameRoute && seq !== routeSeq) return finish(null);
        var e = q(selector, root);
        if (e) finish(e);
      }
      check();
      if (done) return;
      try {
        obs = new MutationObserver(check);
        obs.observe(root === document ? document.documentElement : root, { childList: true, subtree: true });
      } catch (e) { return finish(null); }
      if (sameRoute) routeTick = setInterval(function () { if (seq !== routeSeq) finish(null); }, ROUTE_POLL_MS);
      timer = setTimeout(function () { finish(null); }, timeout);
    });
  }

  /* ------------------------------------------------------------------ */
  /* DOM helpers (text only)                                              */
  /* ------------------------------------------------------------------ */
  function q(sel, root) { try { return (root || document).querySelector(sel); } catch (e) { return null; } }
  function qa(sel, root) { try { return Array.prototype.slice.call((root || document).querySelectorAll(sel)); } catch (e) { return []; } }
  function byName(name, root) {
    if (typeof name !== 'string' || !/^[A-Za-z0-9_-]+$/.test(name)) return null;
    return q('[data-skio-name="' + name + '"]', root);
  }
  function esc(v) {
    return String(v == null ? '' : v).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }
  function safeUrl(u, opts) {
    opts = opts || {};
    if (typeof u !== 'string') return null;
    var s = u.trim();
    if (!s || s.length > 2048 || /[\u0000-\u001F\u007F]/.test(s)) return null;
    if (opts.absolute && !/^https?:\/\//i.test(s)) return null;
    var x;
    try { x = new URL(s, opts.base || location.href); } catch (e) { return null; }
    if (x.protocol !== 'https:' && x.protocol !== 'http:') return null;
    if (opts.httpsOnly && x.protocol !== 'https:') return null;
    if (x.username || x.password) return null;
    if (opts.sameOrigin) {
      var baseOrigin;
      try { baseOrigin = new URL(opts.base || location.href).origin; } catch (e) { return null; }
      if (x.origin !== baseOrigin) return null;
    }
    return x.href;
  }
  var SAFE_ATTRS = /^(id|class|role|title|alt|type|name|value|for|tabindex|hidden|disabled|loading|decoding|width|height|datetime|target|rel|colspan|rowspan|placeholder|autocomplete|enterkeyhint|inputmode|spellcheck|autocapitalize|maxlength|aria-[a-z-]+|data-[a-z0-9-]+)$/;
  var SAFE_STYLE_VALUE = /^[-#.,%()\w\s\/+*]*$/;
  function setAttrs(node, attrs) {
    if (!attrs) return;
    Object.keys(attrs).forEach(function (k) {
      var v = attrs[k];
      if (v == null || v === false) return;
      var key = k === 'className' ? 'class' : k;
      if (key === 'text') { node.textContent = String(v); return; }
      if (key === 'style') {
        if (typeof v !== 'object') return;
        Object.keys(v).forEach(function (p) {
          var val = v[p];
          if (val == null) return;
          val = String(val);
          if (!/^(--)?[a-zA-Z-]+$/.test(p) || !SAFE_STYLE_VALUE.test(val) || /url\s*\(|expression|image-set/i.test(val)) return;
          node.style.setProperty(p.replace(/[A-Z]/g, function (c) { return '-' + c.toLowerCase(); }), val);
        });
        return;
      }
      if (key === 'href' || key === 'src') {
        var safe = safeUrl(String(v), { base: attrs.urlBase });
        if (safe) node.setAttribute(key, safe);
        return;
      }
      if (key === 'urlBase') return;
      key = String(key).toLowerCase();
      if (!SAFE_ATTRS.test(key)) return;
      if (key === 'target' && v !== '_blank' && v !== '_self') return;
      node.setAttribute(key, v === true ? '' : String(v));
    });
    /* after every attribute, so a caller's own 'rel' cannot undo it */
    if (node.getAttribute('target') === '_blank') node.setAttribute('rel', 'noopener noreferrer');
  }
  function appendChildren(node, kids) {
    kids.forEach(function (c) {
      if (c == null || c === false || c === true) return;
      if (Array.isArray(c)) { appendChildren(node, c); return; }
      if (typeof c === 'object' && c.nodeType) { node.appendChild(c); return; }
      node.appendChild(document.createTextNode(String(c)));
    });
  }
  function el(tag, attrs) {
    var t = String(tag || 'div').toLowerCase();
    if (!/^[a-z][a-z0-9]*$/.test(t) || /^(script|style|iframe|object|embed|link|meta|base|frame|frameset|template|svg|math)$/.test(t)) t = 'div';
    var node = document.createElement(t);
    var a = attrs;
    var kids = [].slice.call(arguments, 2);
    if (a && (typeof a !== 'object' || a.nodeType || Array.isArray(a))) { kids.unshift(a); a = null; }
    setAttrs(node, a);
    appendChildren(node, kids);
    return node;
  }
  var SVG_NS = 'http://www.w3.org/2000/svg';
  function svg(viewBox, paths, attrs) {
    var s = document.createElementNS(SVG_NS, 'svg');
    s.setAttribute('viewBox', /^[\d.\s-]+$/.test(String(viewBox)) ? String(viewBox) : '0 0 24 24');
    s.setAttribute('aria-hidden', 'true');
    s.setAttribute('focusable', 'false');
    s.setAttribute('fill', 'none');
    s.setAttribute('stroke', 'currentColor');
    s.setAttribute('stroke-width', '1.8');
    s.setAttribute('stroke-linecap', 'round');
    s.setAttribute('stroke-linejoin', 'round');
    if (attrs && attrs['class']) s.setAttribute('class', String(attrs['class']));
    (paths || []).forEach(function (d) {
      if (!/^[MmLlHhVvCcSsQqTtAaZz\d\s.,-]+$/.test(String(d))) return;
      var p = document.createElementNS(SVG_NS, 'path');
      p.setAttribute('d', String(d));
      s.appendChild(p);
    });
    return s;
  }
  function text(node, v) { if (node) node.textContent = v == null ? '' : String(v); return node; }

  function placedAt(node, anchor, where) {
    if (!node.isConnected) return false;
    if (where === 'before') return node.nextElementSibling === anchor;
    if (where === 'after') return node.previousElementSibling === anchor;
    if (where === 'prepend') return node.parentNode === anchor && anchor.firstElementChild === node;
    return node.parentNode === anchor;
  }
  function unmount(id) {
    try { var n = document.getElementById(id); if (n && n.hasAttribute('data-mk-module')) n.remove(); } catch (e) { /* ignore */ }
  }
  function mount(spec) {
    var id = spec && spec.id;
    try {
      if (!id || typeof id !== 'string') return null;
      var anchor = spec.anchor;
      if (!on() || !anchor || anchor.nodeType !== 1 || !anchor.isConnected) { unmount(id); return null; }
      var where = /^(before|after|prepend|append)$/.test(spec.where) ? spec.where : 'append';
      if ((where === 'before' || where === 'after') && !anchor.parentNode) { unmount(id); return null; }
      var existing = document.getElementById(id);
      var sig = spec.sig == null ? null : String(spec.sig);
      if (existing && sig !== null && existing.getAttribute('data-mk-sig') === sig && placedAt(existing, anchor, where)) return existing;
      var node = typeof spec.build === 'function' ? spec.build() : null;
      if (!node || node.nodeType !== 1) { unmount(id); return null; }
      node.id = id;
      node.setAttribute('data-mk-module', String(spec.module || id));
      if (sig !== null) node.setAttribute('data-mk-sig', sig);
      if (existing && existing !== node) existing.remove();
      if (where === 'before') anchor.parentNode.insertBefore(node, anchor);
      else if (where === 'after') anchor.parentNode.insertBefore(node, anchor.nextSibling);
      else if (where === 'prepend') anchor.insertBefore(node, anchor.firstChild);
      else anchor.appendChild(node);
      return node;
    } catch (e) {
      report('mount ' + id, e);
      if (id) unmount(id);
      return null;
    }
  }
  var hidden = [];   /* {el, owner, prev, prevPriority} */
  function hide(node, owner) {
    try {
      if (!node || node.nodeType !== 1 || !on()) return false;
      for (var i = 0; i < hidden.length; i++) if (hidden[i].el === node) return true;
      hidden.push({ el: node, owner: String(owner || ''), prev: node.style.getPropertyValue('display'), prevPriority: node.style.getPropertyPriority('display') });
      node.style.setProperty('display', 'none', 'important');
      node.setAttribute('data-mk-hidden-by', String(owner || ''));
      return true;
    } catch (e) { return false; }
  }
  function unhideAll(owner) {
    hidden = hidden.filter(function (h) {
      if (owner && h.owner !== String(owner)) return true;
      try {
        if (h.prev) h.el.style.setProperty('display', h.prev, h.prevPriority);
        else h.el.style.removeProperty('display');
        h.el.removeAttribute('data-mk-hidden-by');
      } catch (e) { /* ignore */ }
      return false;
    });
  }
  function guard(fn, onError) {
    return function () {
      var self = this, args = arguments;
      function fail(e) { report('guarded call', e); if (typeof onError === 'function') { try { onError(e); } catch (x) { /* ignore */ } } return null; }
      try {
        var r = fn.apply(self, args);
        return r && typeof r.then === 'function' ? r.then(null, fail) : r;
      } catch (e) { return fail(e); }
    };
  }

  /* ------------------------------------------------------------------ */
  /* dates                                                                */
  /* ------------------------------------------------------------------ */
  var DATE_ONLY = /^(\d{4})-(\d{2})-(\d{2})$/;
  var UTC_MIDNIGHT = /^(\d{4})-(\d{2})-(\d{2})T00:00(?::00(?:\.0+)?)?(?:Z|[+-]00(?::?00)?)$/i;
  function validYmd(y, m, d) {
    var t = new Date(Date.UTC(y, m - 1, d));
    return t.getUTCFullYear() === y && t.getUTCMonth() === m - 1 && t.getUTCDate() === d;
  }
  function toDate(v) {
    try {
      if (v == null || v === '') return null;
      if (v instanceof Date) return isNaN(v.getTime()) ? null : new Date(v.getTime());
      if (typeof v === 'number') { var dn = new Date(v); return isNaN(dn.getTime()) ? null : dn; }
      if (typeof v !== 'string') return null;
      var s = v.trim(), m = DATE_ONLY.exec(s);
      if (m) {
        var y = +m[1], mo = +m[2], d = +m[3];
        return validYmd(y, mo, d) ? new Date(y, mo - 1, d) : null;
      }
      if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(s)) return null;
      var dt = new Date(s);
      return isNaN(dt.getTime()) ? null : dt;
    } catch (e) { return null; }
  }
  var dtfCache = {};
  function partsIn(date, timeZone) {
    var key = timeZone || '';
    var f = dtfCache[key];
    if (!f) {
      f = new Intl.DateTimeFormat('en-US', { timeZone: timeZone || undefined, year: 'numeric', month: 'numeric', day: 'numeric' });
      dtfCache[key] = f;
    }
    var out = {};
    f.formatToParts(date).forEach(function (p) { if (p.type === 'year' || p.type === 'month' || p.type === 'day') out[p.type] = parseInt(p.value, 10); });
    return out.year && out.month && out.day ? { y: out.year, m: out.month, d: out.day } : null;
  }
  function calendarDay(v, opts) {
    try {
      opts = opts || {};
      if (v && typeof v === 'object' && !(v instanceof Date) && typeof v.y === 'number') return validYmd(v.y, v.m, v.d) ? { y: v.y, m: v.m, d: v.d } : null;
      if (typeof v === 'string') {
        var s = v.trim(), m = DATE_ONLY.exec(s) || UTC_MIDNIGHT.exec(s);
        if (m) return validYmd(+m[1], +m[2], +m[3]) ? { y: +m[1], m: +m[2], d: +m[3] } : null;
      }
      var d = toDate(v);
      if (!d) return null;
      if (!opts.timeZone) return { y: d.getFullYear(), m: d.getMonth() + 1, d: d.getDate() };
      return partsIn(d, opts.timeZone);
    } catch (e) { return null; }
  }
  var STYLES = {
    short: { month: 'short', day: 'numeric' },
    weekday: { weekday: 'short', month: 'short', day: 'numeric' },
    long: { weekday: 'long', month: 'long', day: 'numeric' },
    full: { month: 'short', day: 'numeric', year: 'numeric' },
    monthYear: { month: 'short', year: 'numeric' },
    monthDayLong: { month: 'long', day: 'numeric' }
  };
  function today(opts) {
    opts = opts || {};
    return calendarDay(opts.now != null ? toDate(opts.now) : new Date(), { timeZone: opts.timeZone });
  }
  function formatDate(v, style, opts) {
    try {
      opts = opts || {};
      var cd = calendarDay(v, opts);
      if (!cd) return '';
      style = style || 'short';
      if (style === 'iso') return cd.y + '-' + (cd.m < 10 ? '0' : '') + cd.m + '-' + (cd.d < 10 ? '0' : '') + cd.d;
      var base = STYLES[style] || STYLES.short;
      var o = {};
      Object.keys(base).forEach(function (k) { o[k] = base[k]; });
      var withYear = opts.year;
      if (withYear === undefined) withYear = 'auto';
      if (style !== 'full' && style !== 'monthYear') {
        if (withYear === true) o.year = 'numeric';
        else if (withYear === 'auto') { var t = today(opts); if (t && t.y !== cd.y) o.year = 'numeric'; }
      }
      o.timeZone = 'UTC';
      return new Intl.DateTimeFormat('en-US', o).format(new Date(Date.UTC(cd.y, cd.m - 1, cd.d, 12)));
    } catch (e) { return ''; }
  }
  function dayNumber(cd) { return Math.round(Date.UTC(cd.y, cd.m - 1, cd.d) / 86400000); }
  function daysBetween(a, b, opts) {
    var x = calendarDay(a, opts), y = calendarDay(b, opts);
    return x && y ? dayNumber(y) - dayNumber(x) : null;
  }
  function daysFromToday(v, opts) {
    opts = opts || {};
    var t = today(opts), d = calendarDay(v, opts);
    return t && d ? dayNumber(d) - dayNumber(t) : null;
  }
  function addDays(v, n) {
    var cd = calendarDay(v);
    if (!cd || typeof n !== 'number' || !isFinite(n)) return null;
    return new Date(cd.y, cd.m - 1, cd.d + Math.round(n));
  }
  function addInterval(v, interval, count) {
    var cd = calendarDay(v);
    var c = parseInt(count, 10);
    if (!cd || isNaN(c)) return null;
    var unit = String(interval || '').toUpperCase().replace(/S$/, '');
    if (unit === 'DAY') return new Date(cd.y, cd.m - 1, cd.d + c);
    if (unit === 'WEEK') return new Date(cd.y, cd.m - 1, cd.d + 7 * c);
    if (unit === 'MONTH' || unit === 'YEAR') {
      var months = unit === 'YEAR' ? 12 * c : c;
      var first = new Date(cd.y, cd.m - 1 + months, 1);
      var last = new Date(first.getFullYear(), first.getMonth() + 1, 0).getDate();
      return new Date(first.getFullYear(), first.getMonth(), Math.min(cd.d, last));
    }
    return null;
  }

  /* ------------------------------------------------------------------ */
  /* money, points, tiers                                                 */
  /* ------------------------------------------------------------------ */
  function num(v) {
    if (typeof v === 'number') return isFinite(v) ? v : null;
    if (typeof v !== 'string') return null;
    var s = v.replace(/[$,\s]/g, '');
    if (!/^-?\d+(\.\d+)?$/.test(s)) return null;
    var n = parseFloat(s);
    return isFinite(n) ? n : null;
  }
  var moneyFmt = {};
  function money(amount, currency) {
    try {
      var n = num(amount);
      if (n === null) return '';
      var cur = typeof currency === 'string' && /^[A-Z]{3}$/.test(currency) ? currency : 'USD';
      if (!moneyFmt[cur]) moneyFmt[cur] = new Intl.NumberFormat('en-US', { style: 'currency', currency: cur });
      return moneyFmt[cur].format(Math.round(n * 100) / 100);
    } catch (e) { return ''; }
  }
  function wholePoints(n) {
    var x = num(n);
    return x === null ? null : Math.floor(x + 1e-9);
  }
  function points(n) {
    var x = wholePoints(n);
    return x === null ? '' : String(x).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }
  function pointsText(n) {
    var x = wholePoints(n);
    if (x === null) return '';
    return points(x) + (x === 1 ? ' point' : ' points');
  }
  function pointsValue(p) {
    var x = num(p);
    return x === null ? null : Math.round(x) / POINTS_PER_DOLLAR;
  }
  function earnPoints(amount, rate) {
    var a = num(amount), r = num(rate);
    if (a === null || r === null || a <= 0 || r <= 0) return 0;
    var cents = Math.round(a * 100);
    var scaledRate = Math.round(r * 1000);
    return Math.floor((cents * scaledRate) / 100000 + 1e-9);
  }
  function tierName(name) { return String(name == null ? '' : name).replace(TIER_PREFIX, '').trim(); }
  function tierFor(v) {
    if (typeof v === 'number' && isFinite(v)) {
      var best = null;
      TIERS.forEach(function (t) { if (v >= t.minStreak) best = t; });
      return best ? copy(best) : null;
    }
    var n = tierName(v).toLowerCase();
    for (var i = 0; i < TIERS.length; i++) if (TIERS[i].key === n || TIERS[i].name.toLowerCase() === n) return copy(TIERS[i]);
    return null;
  }
  function nextTier(t) {
    var k = t && (t.key || tierName(t).toLowerCase());
    for (var i = 0; i < TIERS.length - 1; i++) if (TIERS[i].key === k) return copy(TIERS[i + 1]);
    return null;
  }
  function tierArt(key) {
    var k = String(key || '').toLowerCase();
    if (!/^(bone|stone|bronze|iron|steel)$/.test(k)) return '';
    var raw = config.tierArt && typeof config.tierArt[k] === 'string' ? config.tierArt[k] : '';
    var fromLoader = raw ? safeUrl(raw.indexOf('//') === 0 ? 'https:' + raw : raw, { httpsOnly: true }) : null;
    return fromLoader || (ART_FALLBACK + k + '.webp');
  }

  /* ------------------------------------------------------------------ */
  /* schedule: a subscription's upcoming order dates (pure: data in, data  */
  /* out, no network). Moved here unchanged from mk-portal-delivery.js     */
  /* (round 2, spec BC 4.2) so the delivery timeline's "Coming up" and the */
  /* home module's Upcoming orders screen use ONE rule and never disagree. */
  /* ------------------------------------------------------------------ */
  var SCHED_UNITS = { DAY: 'day', WEEK: 'week', MONTH: 'month', YEAR: 'year' };
  var SCHED_MAX = 6;
  function sStr(v, max) { return typeof v === 'string' && v.trim() ? v.trim().slice(0, max || 200) : null; }
  function sMs(v) { if (typeof v !== 'string') return null; var t = Date.parse(v); return isFinite(t) ? t : null; }
  function sInt(v) { var n = typeof v === 'number' ? v : (typeof v === 'string' && /^-?\d+$/.test(v.trim()) ? parseInt(v, 10) : NaN); return isFinite(n) ? Math.round(n) : null; }
  function ymdOf(d) {
    if (!(d instanceof Date) || isNaN(d.getTime())) return null;
    var m = d.getMonth() + 1, day = d.getDate();
    return d.getFullYear() + '-' + (m < 10 ? '0' : '') + m + '-' + (day < 10 ? '0' : '') + day;
  }
  /* Skio timestamp -> 'YYYY-MM-DD' in the store's time zone. Skio's header converts the instant
     (moment.tz); calendarDay() would keep a UTC-midnight timestamp's UTC day. */
  function siteYmd(v, tz) {
    if (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(v)) return v;
    var t = sMs(v);
    if (t === null) return null;
    try {
      var p = {};
      new Intl.DateTimeFormat('en-US', { timeZone: tz || undefined, year: 'numeric', month: '2-digit', day: '2-digit' })
        .formatToParts(new Date(t)).forEach(function (x) { p[x.type] = x.value; });
      return /^\d{4}$/.test(p.year) && /^\d{2}$/.test(p.month) && /^\d{2}$/.test(p.day) ? p.year + '-' + p.month + '-' + p.day : null;
    } catch (e) { return null; }
  }
  /* 'Every month' / 'Every 2 weeks'; '' when the schedule cannot be read */
  function scheduleLabel(interval, count) {
    var u = String(interval || '').toUpperCase().replace(/S$/, '');
    var c = typeof count === 'number' ? count : sInt(count);
    if (!SCHED_UNITS[u] || !(c >= 1)) return '';
    return c === 1 ? 'Every ' + SCHED_UNITS[u] : 'Every ' + c + ' ' + SCHED_UNITS[u] + 's';
  }
  function sAttrs(list) {
    var out = {};
    (Array.isArray(list) ? list : []).forEach(function (a) { if (a && typeof a.key === 'string') out[a.key] = a.value; });
    return out;
  }
  function normLine(l) {
    if (!l || typeof l !== 'object') return null;
    var pv = l.ProductVariant || {}, p = pv.Product || {};
    var title = sStr(l.titleOverride, 160) || sStr(p.title, 160);
    if (!title) return null;
    var a = sAttrs(l.customAttributes), skip = [];
    if (typeof a._skipProduct === 'string') {
      try { var s = JSON.parse(a._skipProduct); if (Array.isArray(s)) skip = s.map(sInt).filter(function (n) { return n !== null; }); } catch (e) { skip = null; }
    }
    var q = sInt(l.quantity);
    return {
      title: title, productTitle: sStr(p.title, 160), quantity: q && q > 0 ? q : 1, skip: skip,
      oneTime: Object.prototype.hasOwnProperty.call(a, '_isOneTimeUpsell') || Object.prototype.hasOwnProperty.call(a, 'isOneTimeUpsell'),
      prepaid: l.isPrepaid === true,
      ordersRemaining: l.ordersRemaining == null ? null : sInt(l.ordersRemaining)
    };
  }
  /* full = the answer has the policy, streak and prepaid fields (mkDeliverySub, mkHome); otherwise a list query */
  function normSub(s, full) {
    if (!s || typeof s !== 'object' || typeof s.id !== 'string') return null;
    var bp = s.BillingPolicy || {};
    var lines = (Array.isArray(s.SubscriptionLines) ? s.SubscriptionLines : []).map(normLine).filter(Boolean);
    var out = {
      id: s.id, full: !!full, status: sStr(s.status, 30), statusContext: sStr(s.statusContext, 40),
      next: sMs(s.nextBillingDate) ? s.nextBillingDate : null, cycles: sInt(s.cyclesCompleted) || 0,
      interval: sStr(bp.interval, 10), count: sInt(bp.intervalCount),
      digital: s.isDigital === true, gift: !!s.prepaidGiftRecipientId, lines: lines
    };
    if (full) {
      out.streak = sInt(s.streakCount);
      out.prepaid = !!s.prepaidDeliveryPolicyId || lines.some(function (l) { return l.prepaid; });
      out.anchors = Array.isArray(bp.Anchors) ? bp.Anchors.length : null;
      out.maxCycles = bp.maxCycles == null ? null : sInt(bp.maxCycles);
      out.maxCycleV2 = bp.isMaxCycleV2 === true;
      out.skipCycle = s.skipCycleBillingDate != null;
    }
    return out;
  }
  function openSub(s) {
    return !!s && s.status === 'ACTIVE' && !/^(TEMPORARILY_PAUSED|3DS_PENDING|DUNNING|UNDER_FRAUD_REVIEW)$/.test(s.statusContext || '') &&
      !!s.next && !s.digital && !s.gift;
  }
  /* how many upcoming orders can be listed (spec 11.6), at most `max` (default 6); 1 = the next date only */
  function scheduleCount(sub, max) {
    if (!openSub(sub)) return 0;
    if (!sub.full) return 1;
    if (sub.prepaid) return 0;
    var n = typeof max === 'number' && max >= 1 ? Math.floor(max) : SCHED_MAX;
    if (sub.maxCycles !== null) {
      var left = sub.maxCycles - sub.cycles;
      if (!(left > 0)) return 0;
      n = Math.min(n, left);
    }
    if (sub.anchors !== 0 || sub.skipCycle || sub.maxCycleV2) return Math.min(n, 1);
    if (!scheduleLabel(sub.interval, sub.count)) return Math.min(n, 1);
    return n;
  }
  /* the lines that ship in upcoming order k (k = 1 is the next order) */
  function linesFor(sub, k) {
    var c = sub.cycles + k;
    return sub.lines.filter(function (l) {
      if (l.skip === null) return false;
      if (l.skip.indexOf(c) >= 0) return false;
      if (l.oneTime && k !== 1) return false;
      if (l.ordersRemaining !== null && k > l.ordersRemaining) return false;
      return true;
    });
  }
  /* Upcoming dates. Not known: whether Skio counts each month from the PREVIOUS order date
     (Jan 30 -> Feb 28 -> Mar 28) or from the original day (Jan 30 -> Feb 28 -> Mar 30). Both rules
     are computed and a row is listed only while they agree, so the list stops after the first
     month-end clamp instead of showing a date that may be two days off. (Week and day schedules
     always agree.) opts.max: rows at most (default 6); opts.until 'YYYY-MM-DD': no row after it,
     except the next order (k = 1), which is always kept. */
  function upcomingDates(sub, tz, opts) {
    opts = opts || {};
    var n = scheduleCount(sub, opts.max);
    var until = typeof opts.until === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(opts.until) ? opts.until : null;
    var first = siteYmd(sub.next, tz);
    var out = [], ymd = first;
    for (var k = 1; k <= n && ymd; k++) {
      var anchored = k === 1 ? first : ymdOf(addInterval(first, sub.interval, sub.count * (k - 1)));
      if (anchored !== ymd) break;
      if (k > 1 && until && ymd > until) break;
      out.push({ k: k, ymd: ymd, lines: linesFor(sub, k) });
      ymd = k < n ? ymdOf(addInterval(ymd, sub.interval, sub.count)) : null;
    }
    return out;
  }
  /* ------------------------------------------------------------------ */
  /* next order date (r8, 1.3.0): store days for the change-date pop-up  */
  /* and changeNextOrderDate(). Pure. A store day is 'YYYY-MM-DD' in the */
  /* store's zone (Site.ianaTimezone); days are added with UTC           */
  /* arithmetic on plain calendar days; the browser's own zone is never  */
  /* used for a day (02-safety-design sec. 4.5).                          */
  /* ------------------------------------------------------------------ */
  var HOLD_CONTEXTS = /^(TEMPORARILY_PAUSED|3DS_PENDING|DUNNING|UNDER_FRAUD_REVIEW)$/;
  var ISO_STAMP = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/;
  function validTz(tz) {
    if (typeof tz !== 'string' || !tz || tz.length > 64 || !/^[A-Za-z][A-Za-z0-9_+\-\/]*$/.test(tz)) return null;
    try { new Intl.DateTimeFormat('en-US', { timeZone: tz }).format(0); return tz; } catch (e) { return null; }
  }
  function ymdValid(s) {
    var m = typeof s === 'string' ? DATE_ONLY.exec(s) : null;
    return !!m && validYmd(+m[1], +m[2], +m[3]);
  }
  function addYmd(ymd, n) {
    if (!ymdValid(ymd) || typeof n !== 'number' || !isFinite(n) || Math.floor(n) !== n) return null;
    var t = new Date(Date.UTC(+ymd.slice(0, 4), +ymd.slice(5, 7) - 1, +ymd.slice(8, 10) + n));
    var y = t.getUTCFullYear();
    if (!(y >= 1000 && y <= 9999)) return null;
    return t.toISOString().slice(0, 10);
  }
  function plainObj(v) {
    if (typeof v === 'string' && v.length < 200000) { try { v = JSON.parse(v); } catch (e) { return null; } }
    return v && typeof v === 'object' && !Array.isArray(v) ? v : null;
  }
  /* the wall clock of instant ms in zone tz -> {y, m, d, h, mi, s} | null */
  var wallFmt = {};
  function wallClock(ms, tz) {
    try {
      var f = wallFmt[tz];
      if (!f) {
        f = new Intl.DateTimeFormat('en-US', { timeZone: tz, hourCycle: 'h23', year: 'numeric', month: 'numeric', day: 'numeric',
          hour: 'numeric', minute: 'numeric', second: 'numeric' });
        wallFmt[tz] = f;
      }
      var p = {};
      f.formatToParts(new Date(ms)).forEach(function (x) { if (x.type !== 'literal') p[x.type] = parseInt(x.value, 10); });
      if (p.hour === 24) p.hour = 0;
      return [p.year, p.month, p.day, p.hour, p.minute, p.second].every(function (n) { return typeof n === 'number' && isFinite(n); }) ?
        { y: p.year, m: p.month, d: p.day, h: p.hour, mi: p.minute, s: p.second } : null;
    } catch (e) { return null; }
  }
  /* the instant whose wall clock in tz is ymd h:mi:s.ms, or null (a time the zone skips) */
  function zonedInstant(ymd, h, mi, s, ms, tz) {
    var y = +ymd.slice(0, 4), mo = +ymd.slice(5, 7), d = +ymd.slice(8, 10);
    var want = Date.UTC(y, mo - 1, d, h, mi, s, ms);
    var off = function (t) {
      var w = wallClock(t, tz);
      return w ? Date.UTC(w.y, w.m - 1, w.d, w.h, w.mi, w.s) - Math.floor(t / 1000) * 1000 : NaN;
    };
    var t1 = want - off(want), t2 = want - off(t1);
    var same = function (t) {
      var w = isFinite(t) ? wallClock(t, tz) : null;
      return !!w && w.y === y && w.m === mo && w.d === d && w.h === h && w.mi === mi && w.s === s;
    };
    return same(t1) ? t1 : same(t2) ? t2 : null;
  }
  /* ONE day rule (02 sec. 4.5 rule 5): the store day of a Skio timestamp. Never the browser's zone. */
  function orderDay(v, tz) {
    tz = validTz(tz);
    return tz ? siteYmd(v, tz) : null;
  }
  /* the latest-day limit Skio's own window uses, or null (none) */
  function futureLimit(cpv3) {
    var c = plainObj(cpv3), t = c && plainObj(c.timing);
    var v = t ? t.nextBillingDateDaysIntoFutureLimit : null;
    if (typeof v === 'string' && /^\d{1,6}$/.test(v.trim())) v = parseInt(v.trim(), 10);
    return typeof v === 'number' && isFinite(v) && v >= 1 && Math.floor(v) === v ? v : null;
  }
  function nextDateRange(tz, cpv3Settings, opts) {
    try {
      tz = validTz(tz);
      if (!tz) return null;
      opts = opts || {};
      var at = opts.now instanceof Date ? opts.now.getTime() : (typeof opts.now === 'number' ? opts.now : now());
      if (!isFinite(at)) return null;
      var day = siteYmd(new Date(at).toISOString(), tz);
      if (!day) return null;
      var limit = futureLimit(cpv3Settings);
      return { today: day, min: addYmd(day, 1), max: limit ? addYmd(day, limit - 1) : null, limit: limit };
    } catch (e) { return null; }
  }
  function inDateRange(r, ymd) { return !!r && ymdValid(ymd) && ymd >= r.min && (r.max === null || ymd <= r.max); }
  /* the instant to send for store day ymd (02 sec. 4.5 rule 2): ymd at the current billing wall-clock time, else
     12:00 store time; each only when its store day AND its UTC day are ymd and it is no midnight (UTC or store) */
  function instantFits(t, ymd, tz) {
    if (t === null || !isFinite(t)) return false;
    var d = new Date(t), iso = d.toISOString(), w = wallClock(t, tz);
    return iso.slice(0, 10) === ymd && siteYmd(iso, tz) === ymd && !(d.getUTCHours() === 0 && d.getUTCMinutes() === 0) &&
      !!w && !(w.h === 0 && w.mi === 0);
  }
  function nextDateInstant(current, ymd, tz) {
    try {
      tz = validTz(tz);
      if (!tz || !ymdValid(ymd) || typeof current !== 'string' || !ISO_STAMP.test(current)) return null;
      var cur = sMs(current);
      var w = cur === null ? null : wallClock(cur, tz);
      if (!w) return null;
      var cands = [zonedInstant(ymd, w.h, w.mi, w.s, ((cur % 1000) + 1000) % 1000, tz), zonedInstant(ymd, 12, 0, 0, 0, tz)];
      for (var i = 0; i < cands.length; i++) if (instantFits(cands[i], ymd, tz)) return new Date(cands[i]).toISOString();
      return null;
    } catch (e) { return null; }
  }
  /* Skio's "Next order date" action switch, strictly: on only when the setting is truthy (Skio's own header rule) */
  function dateSwitchOn(cpv3) {
    var c = plainObj(cpv3), a = c && plainObj(c.actionButtonToggleSettings);
    var v = a ? a['Next order date'] : undefined;
    if (v && typeof v === 'object') return !(v.enabled === false || v.show === false || v.visible === false || v.hidden === true);
    return !!v;
  }
  /* the pop-up's gate (02 sec. 3.1), on Skio's own objects; fails closed */
  function nextDateEligible(sub, site, opts) {
    try {
      var no = function (rule) { return { ok: false, rule: rule }; };
      if (!sub || typeof sub !== 'object' || typeof sub.id !== 'string' || !UUID_RE.test(sub.id)) return no('bad_id');
      site = site && typeof site === 'object' ? site : {};
      if (sub.status !== 'ACTIVE') return no('status');
      if (sub.statusContext != null && HOLD_CONTEXTS.test(String(sub.statusContext))) return no('hold');
      if (sub.isDigital === true) return no('digital');
      var lines = Array.isArray(sub.SubscriptionLines) ? sub.SubscriptionLines : [];
      if (sub.prepaidDeliveryPolicyId != null || sub.PrepaidDeliveryPolicy != null ||
          lines.some(function (l) { return l && (l.isPrepaid === true || l.prepaidSubscriptionId != null); })) return no('prepaid');
      if (sub.prepaidGiftRecipientId != null) return no('gift');
      var tz = validTz(site.ianaTimezone);
      if (!tz) return no('timezone');
      if (!dateSwitchOn(site.cpv3Settings)) return no('switch_off');
      var settings = plainObj(site.settings) || {};
      var bp = sub.BillingPolicy && typeof sub.BillingPolicy === 'object' ? sub.BillingPolicy : null;
      if (!bp || !Array.isArray(bp.Anchors) || (bp.Anchors.length && settings.allowAnyNextBillingDate !== true)) return no('anchors');
      if (settings.disableBillingDateChangeIncompleteMinCycles === true && bp.minCycles !== null) {
        var minC = sInt(bp.minCycles), done = sInt(sub.cyclesCompleted);
        if (minC === null || (minC > 0 && (done === null || done < minC))) return no('min_cycles');
      }
      var next = typeof sub.nextBillingDate === 'string' && ISO_STAMP.test(sub.nextBillingDate) && sMs(sub.nextBillingDate) !== null ? sub.nextBillingDate : null;
      var day = next ? orderDay(next, tz) : null;
      if (!day) return no('no_date');
      var range = nextDateRange(tz, site.cpv3Settings, opts);
      if (!range) return no('timezone');
      if (day < range.min) return no('due_today');
      return { ok: true, day: day, tz: tz, range: range };
    } catch (e) { return { ok: false, rule: 'error' }; }
  }

  var schedule = {
    siteYmd: siteYmd, ymd: ymdOf, label: scheduleLabel, normalize: normSub, normLine: normLine, open: openSub,
    count: scheduleCount, linesFor: linesFor, upcoming: upcomingDates, addYmd: addYmd
  };
  try { Object.freeze(schedule); } catch (e) { /* ignore */ }

  /* ------------------------------------------------------------------ */
  /* screen: one full-page screen of ours at a time over Skio's page       */
  /* (round 2 shared contract). <html data-mk-screen> is '' or the name of */
  /* the open screen ('upcoming' and, r10, 'add-products' owned by the    */
  /* home module, 'support' by the Support module); every change is       */
  /* announced on window with                                             */
  /* 'mk-portal:screen' {screen}. Owners set their own name and clear only */
  /* their own (a stale clear never closes another screen). The menu only */
  /* reads it.                                                            */
  /* ------------------------------------------------------------------ */
  var SCREENS = /^(upcoming|support|add-products|returns|return)$/;
  function screenGet() {
    try { var v = document.documentElement.getAttribute('data-mk-screen') || ''; return SCREENS.test(v) ? v : ''; } catch (e) { return ''; }
  }
  function screenFire(v) {
    try { window.dispatchEvent(new CustomEvent('mk-portal:screen', { detail: { screen: v } })); } catch (e) { /* ignore */ }
  }
  function screenSet(name) {
    try {
      if (!on() || !SCREENS.test(String(name))) return false;
      if (screenGet() === name) return true;
      document.documentElement.setAttribute('data-mk-screen', name);
      screenFire(name);
      return true;
    } catch (e) { return false; }
  }
  function screenClear(name) {
    try {
      if (!SCREENS.test(String(name)) || screenGet() !== name) return false;
      document.documentElement.setAttribute('data-mk-screen', '');
      screenFire('');
      return true;
    } catch (e) { return false; }
  }
  /* drop "#<name>" from the address bar (URL only, no navigation, no new history entry). {} as the
     state: Next copies its router state into it and treats the call as a URL sync (spec 1.3/1.4), the
     same call dropCombineHash() makes. */
  function screenDropHash(name) {
    try {
      if (!SCREENS.test(String(name)) || location.hash !== '#' + name) return false;
      window.history.replaceState({}, '', location.pathname + location.search);
      return true;
    } catch (e) { return false; }
  }
  /* add "#<name>" as a new entry WITH Next's router state (a copy of the current entry's state: __NA and
     the tree), marked mkScreen: name. Next passes a state that already has __NA straight through, and on
     Back / Forward it traverses to that tree, so the page always matches the address (round 2 review
     R2-R1: a plain location.hash entry has state null, which Next ignores). If the current state has no
     __NA (e.g. after a skip-link hash), Next's URL sync applies; worst case one reload on Back. */
  function screenPushHash(name) {
    try {
      if (!on() || !SCREENS.test(String(name)) || location.hash === '#' + name) return false;
      var cur = window.history.state, st = {};
      if (cur && typeof cur === 'object') Object.keys(cur).forEach(function (k) { st[k] = cur[k]; });
      st.mkScreen = name;
      window.history.pushState(st, '', location.pathname + location.search + '#' + name);
      return location.hash === '#' + name;
    } catch (e) { return false; }
  }
  /* landing hashes of other modules (one use): "#mk-tracking" (the delivery banner's and the orders
     page's "Tracking updates" link opens the timeline's tracking list). hashIs(name) reads it;
     dropHash(name) removes it from the address bar exactly as screenDropHash() does. */
  var LANDING = /^mk-tracking$/;
  function hashIs(name) {
    try { return on() && LANDING.test(String(name)) && location.hash === '#' + name; } catch (e) { return false; }
  }
  function landingDropHash(name) {
    try {
      if (!LANDING.test(String(name)) || location.hash !== '#' + name) return false;
      window.history.replaceState({}, '', location.pathname + location.search);
      return true;
    } catch (e) { return false; }
  }
  function screenOwnEntry(name) {
    try {
      if (!SCREENS.test(String(name)) || location.hash !== '#' + name) return false;
      var cur = window.history.state;
      return cur === null || !!(cur && cur.mkScreen === name);
    } catch (e) { return false; }
  }
  var screen = { get: screenGet, set: screenSet, clear: screenClear, dropHash: screenDropHash, pushHash: screenPushHash, ownEntry: screenOwnEntry };
  try { Object.freeze(screen); } catch (e) { /* ignore */ }

  /* ------------------------------------------------------------------ */
  /* "#mk-combine": the home's "Review and combine" link lands on the      */
  /* subscription page with this hash. Wait (up to COMBINE_WAIT_MS) for    */
  /* the Combine card (Skio custom extension) to show its title            */
  /* #mk-x-combine-title (tabindex -1), scroll it into view below the      */
  /* sticky page header and move focus to it. Content that mounts above    */
  /* it afterwards (the delivery timeline waits for its own service) moves */
  /* the card down: until COMBINE_WAIT_MS after page load (and at least   */
  /* COMBINE_SETTLE_MS after the last correction) the card's position is  */
  /* re-measured and, if it moved more than COMBINE_DRIFT_PX, scrolled to */
  /* again; the customer scrolling, clicking or typing ends this at once. */
  /* Card never shown: nothing moves. Either way the                      */
  /* hash is removed (one use). Reads and scrolls only; never presses      */
  /* anything in the card.                                                 */
  /* ------------------------------------------------------------------ */
  var COMBINE_HASH = '#mk-combine';
  var COMBINE_TITLE_ID = 'mk-x-combine-title';
  var COMBINE_WAIT_MS = 8000;
  var COMBINE_POLL_MS = 150;
  var COMBINE_SETTLE_MS = 1500;   /* keep following the card at least this long after the last scroll */
  var COMBINE_DRIFT_PX = 24;
  var combineRun = 0;
  function reducedMotion() {
    try { return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches); } catch (e) { return false; }
  }
  function shownEl(n) {
    if (!n || !n.isConnected || (n.closest && n.closest('[hidden]'))) return false;
    try { return n.getClientRects().length > 0; } catch (e) { return false; }
  }
  function dropCombineHash() {
    try {
      if (location.hash !== COMBINE_HASH) return;
      /* pass {} (Next copies its router state into it and treats the call as a URL sync, spec 1.3/1.4:
         the page stays); avoids depending on how Next handles a null state. INFERRED; checked live in H5. */
      window.history.replaceState({}, '', location.pathname + location.search);
    } catch (e) { /* ignore */ }
  }
  function stickyTop() {
    var hdr = byName('subscription-details-header-container');
    if (!hdr) return 0;
    try {
      var cs = getComputedStyle(hdr);
      if (cs.position !== 'sticky' && cs.position !== 'fixed') return 0;
      return (parseFloat(cs.top) || 0) + hdr.getBoundingClientRect().height;
    } catch (e) { return 0; }
  }
  function focusCombineCard(r) {
    r = r || route();
    if (!on() || r.page !== 'subscription' || location.hash !== COMBINE_HASH) return Promise.resolve(false);
    var mine = ++combineRun, seq = routeSeq, path = location.pathname, t0 = now(), used = false;
    /* the customer started scrolling, typing or clicking: leave the page to them (also ends the settle phase) */
    var EV = ['wheel', 'touchstart', 'keydown', 'pointerdown'];
    function onUse() { used = true; }
    EV.forEach(function (t) { try { window.addEventListener(t, onUse, { capture: true, passive: true }); } catch (e) { /* ignore */ } });
    function unlisten() {
      EV.forEach(function (t) { try { window.removeEventListener(t, onUse, { capture: true }); } catch (e) { /* ignore */ } });
    }
    function current() { return mine === combineRun && seq === routeSeq && location.pathname === path; }
    function cardOf(title) { return (title.closest && title.closest('[data-mk-ext="combine"]')) || title; }
    function docTop(n) { return n.getBoundingClientRect().top + (window.pageYOffset || 0); }
    function scrollToCard(card) {
      var top = docTop(card);
      var y = Math.max(0, top - stickyTop() - 16);
      var behavior = reducedMotion() ? 'auto' : 'smooth';
      try { window.scrollTo({ top: y, behavior: behavior }); } catch (e) { window.scrollTo(0, y); }
      return top;
    }
    /* after the first scroll: re-measure; content mounted above the card moves it, so follow it */
    function settle(title, card, top, tScroll) {
      var tFix = tScroll;
      (function watch() {
        try {
          var t = now();
          var quiet = t - tFix >= COMBINE_SETTLE_MS && t - t0 >= COMBINE_WAIT_MS;
          if (used || !current() || !shownEl(title) || quiet || t - tScroll >= COMBINE_WAIT_MS) { unlisten(); return; }
          var nowTop = docTop(card);
          if (Math.abs(nowTop - top) > COMBINE_DRIFT_PX) { top = scrollToCard(card); tFix = t; }
          setTimeout(watch, 100);
        } catch (e) { unlisten(); }
      })();
    }
    return new Promise(function (resolve) {
      (function poll() {
        try {
          if (!current()) { unlisten(); return resolve(false); }
          if (used) { unlisten(); dropCombineHash(); return resolve(false); }
          var title = document.getElementById(COMBINE_TITLE_ID);
          if (shownEl(title)) {
            dropCombineHash();
            var card = cardOf(title);
            var top = scrollToCard(card);
            try { title.focus({ preventScroll: true }); } catch (e) { try { title.focus(); } catch (e2) { /* ignore */ } }
            settle(title, card, top, now());
            return resolve(true);
          }
          if (now() - t0 >= COMBINE_WAIT_MS) { unlisten(); dropCombineHash(); return resolve(false); }
          setTimeout(poll, COMBINE_POLL_MS);
        } catch (e) { unlisten(); dropCombineHash(); resolve(false); }
      })();
    });
  }


  /* ------------------------------------------------------------------ */
  /* dialog (batch 3, 1.1.0): the one shared pop-up of the portal. The    */
  /* approved batch 3 design (Add to your <date> order, the sign-up pop-up, */
  /* Track package): a centred dialog on desktop and tablet, a bottom     */
  /* sheet on phones (and on short landscape screens); the head (close,   */
  /* icon, title) never scrolls, the body scrolls, the actions are pinned */
  /* at the foot (or follow the body: pin false). Native <dialog> +       */
  /* showModal() where available (top layer, page behind inert); else a   */
  /* div with role=dialog and inert on everything else in <body>. Tab and */
  /* Shift+Tab loop inside, Escape / the scrim / the X close it, focus    */
  /* starts on the title (tabindex -1, never a ring) and returns to the   */
  /* opener. The phone keyboard: a visualViewport listener sets --mk-kb  */
  /* (the keyboard's height) and the dialog's bottom moves up by it; a   */
  /* focused field is scrolled into view. Our 2px brown ring shows for    */
  /* keyboard focus only (html:not(.mk-pointer), r4); the store theme's   */
  /* focus box is neutralised inside the dialog.                          */
  /* ------------------------------------------------------------------ */
  var DLG_ID_RE = /^mk-[a-z0-9-]{1,40}$/;
  var DLG_CLASS_RE = /^[a-z][a-z0-9-]{0,40}$/;
  var DLG_BUSY_MAX_MS = 30000;    /* busy() releases itself after this long (a feature that forgot cannot trap the customer);
                                     longer than the whole guarded write (WRITE_DEADLINE_MS, at most 25 s), so a pop-up
                                     cannot be closed while addSubscriptionLine() is still running */
  var DLG_FIELD_SCROLL_MS = 300;  /* after a field gets focus, scroll it into view once the phone keyboard is up */
  var DLG_ARM_MS = 450;           /* 1.5.1: a press on a pop-up's button counts only this long after the pop-up opened or
                                     its buttons were swapped, and only when the press itself began after that (no click-through) */
  var CONFIRM_TTL_MS = 10000;     /* a confirmation token is good for this long, once */
  if (typeof config.confirmTtlMs === 'number' && config.confirmTtlMs >= 100 && config.confirmTtlMs <= CONFIRM_TTL_MS) CONFIRM_TTL_MS = config.confirmTtlMs;
  /* Styles of the dialog shell and its shared parts (buttons, fields, alert, fine print). Written without
     !important for reading; important() adds it to every declaration when the sheet is injected, since the
     store theme styles dialog, button, input and :focus globally. Every selector starts with
     html.mk-portal-on (the loader's gate). Injected once, on the first open, as <style id="mk-dlg-css">,
     unless a stylesheet already declares html.mk-portal-on { --mk-dlg-css: 1 } (then that sheet is used).
     r6 (1.2.0): the store theme's base.css hides every EMPTY div/p/a/ul/section/h1-h6 (display:none, specificity
     0,1,1). Dialogs live in <body>, outside #skio-root, where Skio's own '#skio-root :empty{display:block}' does not
     reach. Every part that may be empty and must still show (the scrim, screen-reader-only text and live regions)
     states its display here; a sheet that replaces this one (--mk-dlg-css) must carry the same two display rules. */
  var DIALOG_CSS = [
    'html.mk-portal-on .mk-dlg {',
    '  --d-brown: #4C2B1D; --d-brown-deep: #3C2116; --d-ink: #211C1A; --d-text: #2D2827; --d-muted: #6B5C3E;',
    '  --d-cream: #F2ECD5; --d-paper: #FBF8EF; --d-gold: #B98A2C; --d-gold-2: #C9A344; --d-gold-3: #D9B656; --d-gold-text: #805F1E;',
    '  --d-ox: #7F2C21; --d-line: rgba(76, 43, 29, .14); --d-line-strong: rgba(76, 43, 29, .28);',
    '  --d-head: "new-kansas", Georgia, "Times New Roman", serif; --d-body: Rubik, system-ui, -apple-system, "Segoe UI", sans-serif;',
    '  position: fixed; inset: 0 0 var(--mk-kb, 0px) 0; z-index: 2147483000; display: none; align-items: center; justify-content: center;',
    '  width: auto; height: auto; min-width: 0; max-width: none; min-height: 0; max-height: none; margin: 0; padding: 24px; border: 0; border-radius: 0;',
    '  background: transparent; box-shadow: none; overflow: visible; color: var(--d-text); text-align: left; letter-spacing: normal; text-transform: none;',
    '  font: 400 15px/1.45 var(--d-body); -webkit-font-smoothing: antialiased; transform: none; opacity: 1; visibility: visible;',
    '}',
    'html.mk-portal-on .mk-dlg[data-mk-open] { display: flex; }',
    'html.mk-portal-on .mk-dlg::backdrop { background: transparent; }',
    'html.mk-portal-on .mk-dlg *, html.mk-portal-on .mk-dlg *::before, html.mk-portal-on .mk-dlg *::after { box-sizing: border-box; }',
    'html.mk-portal-on .mk-dlg .mk-dlg-scrim { display: block; position: fixed; inset: 0; margin: 0; padding: 0; border: 0; background: rgba(33, 28, 26, .52); backdrop-filter: blur(1.5px); }',
    'html.mk-portal-on .mk-dlg .mk-dlg-sr { display: block; position: absolute; width: 1px; height: 1px; margin: -1px; padding: 0; overflow: hidden; clip: rect(0 0 0 0); white-space: nowrap; border: 0; }',
    'html.mk-portal-on .mk-dlg .mk-dlg-panel {',
    '  position: relative; width: min(500px, 100%); max-height: 100%; display: flex; flex-direction: column; overflow: hidden; margin: 0; padding: 0;',
    '  background: #fff; border-radius: 22px; border: 1px solid var(--d-line);',
    '  box-shadow: 0 30px 70px -24px rgba(33, 28, 26, .55), 0 2px 8px rgba(76, 43, 29, .08);',
    '}',
    'html.mk-portal-on .mk-dlg.mk-dlg--wide .mk-dlg-panel { width: min(560px, 100%); }',
    'html.mk-portal-on .mk-dlg .mk-dlg-head { flex: none; position: relative; margin: 0; padding: 28px 28px 4px; background: #fff; }',
    'html.mk-portal-on .mk-dlg .mk-dlg-body { flex: 1 1 auto; min-height: 0; overflow: auto; overscroll-behavior: contain; margin: 0; padding: 0 28px 2px; background: #fff; }',
    'html.mk-portal-on .mk-dlg .mk-dlg-panel.is-scrolled .mk-dlg-head { box-shadow: 0 1px 0 var(--d-line); z-index: 1; }',
    'html.mk-portal-on .mk-dlg .mk-dlg-body > * { scroll-margin: 14px 0; }',
    'html.mk-portal-on .mk-dlg .mk-dlg-x {',
    '  position: absolute; top: 14px; right: 14px; width: 44px; height: 44px; min-width: 0; min-height: 0; margin: 0; padding: 0; border: 0; border-radius: 50%;',
    '  background: transparent; display: grid; place-items: center; color: var(--d-brown); cursor: pointer; box-shadow: none;',
    '}',
    'html.mk-portal-on .mk-dlg .mk-dlg-x[aria-disabled="true"] { opacity: .4; cursor: default; }',
    'html.mk-portal-on .mk-dlg .mk-dlg-x svg { width: 20px; height: 20px; }',
    'html.mk-portal-on .mk-dlg svg { stroke: currentColor; fill: none; stroke-width: 2; stroke-linecap: round; stroke-linejoin: round; }',
    'html.mk-portal-on .mk-dlg .mk-dlg-title {',
    '  margin: 0 44px 6px 0; padding: 0; border: 0; font: 600 25px/1.15 var(--d-head); letter-spacing: -.01em; text-transform: none;',
    '  color: var(--d-brown); text-wrap: balance; outline: none; box-shadow: none; background: transparent;',
    '}',
    'html.mk-portal-on .mk-dlg .mk-dlg-icon {',
    '  width: 48px; height: 48px; border-radius: 14px; display: grid; place-items: center; margin: 0 0 14px;',
    '  background: var(--d-paper); border: 1px solid var(--d-line); color: var(--d-brown);',
    '}',
    'html.mk-portal-on .mk-dlg .mk-dlg-icon svg { width: 24px; height: 24px; }',
    'html.mk-portal-on .mk-dlg .mk-dlg-icon--gold { background: linear-gradient(180deg, #FFF3CF, #F4E3B0); border-color: rgba(185, 138, 44, .35); color: var(--d-gold-text); }',
    'html.mk-portal-on .mk-dlg .mk-dlg-icon--done { width: 56px; height: 56px; border-radius: 50%; background: var(--d-brown); border: 0; color: var(--d-cream); box-shadow: 0 0 0 6px #F1E5C2; }',
    'html.mk-portal-on .mk-dlg .mk-dlg-icon--done svg { width: 28px; height: 28px; stroke-width: 2.6; }',
    'html.mk-portal-on .mk-dlg .mk-dlg-lead { margin: 0 0 18px; font-size: 15px; color: var(--d-text); }',
    'html.mk-portal-on .mk-dlg .mk-dlg-lead b { color: var(--d-brown); font-weight: 700; }',
    'html.mk-portal-on .mk-dlg .mk-dlg-note { margin: 0 0 4px; font-size: 14px; color: var(--d-muted); }',
    'html.mk-portal-on .mk-dlg .mk-dlg-note b { color: var(--d-brown); font-weight: 600; }',
    'html.mk-portal-on .mk-dlg fieldset { border: 0; margin: 0 0 16px; padding: 0; min-width: 0; }',
    'html.mk-portal-on .mk-dlg :is(legend, .mk-dlg-label) {',
    '  display: block; padding: 0; margin: 0 0 8px; font: 700 12px/1.3 var(--d-body); letter-spacing: .08em; text-transform: uppercase; color: var(--d-gold-text);',
    '}',
    'html.mk-portal-on .mk-dlg .mk-dlg-input {',
    '  display: block; width: 100%; height: 50px; margin: 0; padding: 0 16px; border: 0; border-radius: 12px; background: #fff;',
    '  box-shadow: inset 0 0 0 1px var(--d-line-strong); font: 500 16px/1 var(--d-body); color: var(--d-ink); outline: none;',
    '}',
    'html.mk-portal-on .mk-dlg .mk-dlg-input[aria-invalid="true"] { box-shadow: inset 0 0 0 2px var(--d-ox); }',
    'html.mk-portal-on .mk-dlg .mk-dlg-hint { margin: 7px 2px 0; font-size: 13px; color: var(--d-muted); }',
    'html.mk-portal-on .mk-dlg .mk-dlg-check {',
    '  position: relative; display: grid; grid-template-columns: 24px minmax(0, 1fr); gap: 12px; align-items: start; margin: 18px 0 10px; padding: 12px 14px;',
    '  border-radius: 14px; background: var(--d-paper); box-shadow: inset 0 0 0 1px var(--d-line); cursor: pointer; font: 600 15px/1.35 var(--d-body); color: var(--d-brown);',
    '}',
    'html.mk-portal-on .mk-dlg .mk-dlg-check input { position: absolute; opacity: 0; width: 1px; height: 1px; margin: 0; }',
    'html.mk-portal-on .mk-dlg .mk-dlg-box { width: 22px; height: 22px; margin-top: 1px; border-radius: 6px; background: #fff; box-shadow: inset 0 0 0 2px var(--d-line-strong); display: grid; place-items: center; color: transparent; }',
    'html.mk-portal-on .mk-dlg .mk-dlg-box svg { width: 15px; height: 15px; stroke-width: 3; }',
    'html.mk-portal-on .mk-dlg .mk-dlg-check:has(input:checked) .mk-dlg-box { background: var(--d-brown); box-shadow: none; color: var(--d-cream); }',
    'html.mk-portal-on .mk-dlg .mk-dlg-check.is-invalid { box-shadow: inset 0 0 0 2px var(--d-ox); background: #FDF5F3; }',
    'html.mk-portal-on .mk-dlg .mk-dlg-check.is-invalid .mk-dlg-box { box-shadow: inset 0 0 0 2px var(--d-ox); }',
    'html.mk-portal-on .mk-dlg .mk-dlg-field-msg { display: flex; align-items: center; gap: 6px; margin: -4px 2px 10px; font: 600 13.5px/1.35 var(--d-body); color: var(--d-ox); }',
    'html.mk-portal-on .mk-dlg .mk-dlg-field-msg svg { width: 16px; height: 16px; flex: none; }',
    'html.mk-portal-on .mk-dlg .mk-dlg-legal { margin: 0 0 6px; font: 400 12.5px/1.5 var(--d-body); color: var(--d-muted); }',
    'html.mk-portal-on .mk-dlg :is(.mk-dlg-legal, .mk-dlg-lead, .mk-dlg-note, .mk-dlg-cross, .mk-dlg-alert, .mk-dlg-fine, .mk-dlg-after) a {',
    '  color: var(--d-brown); font-weight: 600; text-decoration: underline; text-decoration-color: var(--d-gold-2); text-underline-offset: 2px; background: transparent;',
    '}',
    'html.mk-portal-on .mk-dlg .mk-dlg-alert {',
    '  display: grid; grid-template-columns: 22px minmax(0, 1fr); gap: 10px; align-items: start; margin: 16px 0 0; padding: 12px 14px; border-radius: 12px;',
    '  background: #FBEDEA; border: 1px solid rgba(127, 44, 33, .3); color: var(--d-ox); font-size: 14px;',
    '}',
    'html.mk-portal-on .mk-dlg .mk-dlg-alert svg { width: 20px; height: 20px; margin-top: 1px; }',
    'html.mk-portal-on .mk-dlg .mk-dlg-alert b { display: block; font-weight: 700; }',
    'html.mk-portal-on .mk-dlg .mk-dlg-alert span { color: #5E2A22; }',
    'html.mk-portal-on .mk-dlg .mk-dlg-acts { flex: none; display: flex; flex-wrap: wrap; align-items: center; gap: 10px; margin: 0; padding: 18px 28px 24px; background: #fff; }',
    'html.mk-portal-on .mk-dlg.mk-dlg--nopin .mk-dlg-acts { padding: 18px 0 24px; }',
    'html.mk-portal-on .mk-dlg .mk-dlg-acts--stack { flex-direction: column; align-items: stretch; }',
    'html.mk-portal-on .mk-dlg .mk-dlg-btn {',
    '  display: inline-flex; align-items: center; justify-content: center; gap: 8px; min-width: 0; min-height: 48px; margin: 0; padding: 12px 22px; border: 0;',
    '  border-radius: 999px; font: 700 15px/1.2 var(--d-body); letter-spacing: normal; text-transform: none; text-decoration: none; cursor: pointer; white-space: nowrap;',
    '}',
    'html.mk-portal-on .mk-dlg .mk-dlg-btn svg { width: 17px; height: 17px; stroke-width: 2.4; }',
    'html.mk-portal-on .mk-dlg .mk-dlg-btn--primary { flex: 1 1 auto; background: var(--d-brown); color: var(--d-cream); box-shadow: none; }',
    'html.mk-portal-on .mk-dlg .mk-dlg-btn--gold {',
    '  flex: 1 1 auto; background: linear-gradient(180deg, var(--d-gold-3), var(--d-gold)); color: var(--d-ink);',
    '  box-shadow: 0 8px 20px -10px rgba(185, 138, 44, .6), inset 0 1px 0 rgba(255, 255, 255, .35);',
    '}',
    'html.mk-portal-on .mk-dlg .mk-dlg-btn--ghost { background: #fff; color: var(--d-brown); box-shadow: inset 0 0 0 1px var(--d-line-strong); }',
    'html.mk-portal-on .mk-dlg .mk-dlg-btn:is([disabled], [aria-disabled="true"]) { opacity: .6; cursor: default; }',
    'html.mk-portal-on .mk-dlg .mk-dlg-after { margin: 0; font-size: 13px; color: var(--d-muted); text-align: center; }',
    'html.mk-portal-on .mk-dlg .mk-dlg-fine { flex: 1 1 100%; margin: 0 2px 2px; font-size: 13px; color: var(--d-muted); }',
    'html.mk-portal-on .mk-dlg .mk-dlg-fine b { font-weight: 600; color: var(--d-brown); }',
    'html.mk-portal-on .mk-dlg .mk-dlg-cross { margin: 2px 0 0; padding: 12px 14px; border-radius: 12px; background: var(--d-paper); border: 1px solid var(--d-line); font-size: 14px; color: var(--d-text); }',
    'html.mk-portal-on .mk-dlg.mk-dlg--center .mk-dlg-panel { text-align: center; }',
    'html.mk-portal-on .mk-dlg.mk-dlg--center .mk-dlg-icon { margin-left: auto; margin-right: auto; }',
    'html.mk-portal-on .mk-dlg.mk-dlg--center .mk-dlg-title { margin-left: 44px; }',
    'html.mk-portal-on .mk-dlg.mk-dlg--center .mk-dlg-acts { justify-content: center; }',
    /* the store theme's focus box (outline, ring shadow, background) never shows inside the dialog; our own
       2px brown ring (11:1 on white) shows for keyboard focus only (r4: html:not(.mk-pointer)) */
    'html.mk-portal-on .mk-dlg :is(a, button, input, select, textarea, summary, [tabindex]):is(:focus, :focus-visible) { outline: none; }',
    'html.mk-portal-on .mk-dlg :is(a, button, select, textarea, summary, [tabindex]):not(.mk-dlg-btn--ghost):not(.mk-dlg-btn--gold):not(.mk-dlg-input):is(:focus, :focus-visible) { box-shadow: none; }',
    'html.mk-portal-on .mk-dlg :is(a:not(.mk-dlg-btn), .mk-dlg-x, .mk-dlg-title, .mk-dlg-body, [tabindex="-1"]):is(:focus, :focus-visible) { background-color: transparent; }',
    'html.mk-portal-on .mk-dlg .mk-dlg-input:focus { box-shadow: inset 0 0 0 2px var(--d-brown); background-color: #fff; }',
    'html.mk-portal-on:not(.mk-pointer) .mk-dlg :is(a, button, select, summary, [tabindex]:not([tabindex="-1"])):focus-visible { outline: 2px solid var(--d-brown); outline-offset: 2px; }',
    'html.mk-portal-on:not(.mk-pointer) .mk-dlg .mk-dlg-btn--primary:focus-visible { outline-offset: 3px; }',
    'html.mk-portal-on:not(.mk-pointer) .mk-dlg .mk-dlg-body[tabindex="0"]:focus-visible { outline: 2px solid var(--d-brown); outline-offset: -2px; }',
    'html.mk-portal-on:not(.mk-pointer) .mk-dlg :is(.mk-dlg-check, .mk-dlg-choice):has(input:focus-visible) { outline: 2px solid var(--d-brown); outline-offset: 2px; }',
    'html.mk-portal-on .mk-dlg :is(.mk-dlg-title, [tabindex="-1"]):is(:focus, :focus-visible) { outline: none; box-shadow: none; }',
    /* the lock goes on the root only; body is clipped, not hidden, so it does not become a scroll container and
       position:sticky keeps working (browsers without overflow:clip drop the body declaration: body stays non-scrolling) */
    'html.mk-portal-on.mk-dlg-open { overflow: hidden; } html.mk-portal-on.mk-dlg-open body { overflow: clip; }',
    '@media (max-width: 1023.98px) { html.mk-portal-on .mk-dlg .mk-dlg-panel { width: min(480px, 100%); } html.mk-portal-on .mk-dlg.mk-dlg--wide .mk-dlg-panel { width: min(560px, 100%); } }',
    /* phones and short landscape screens: a bottom sheet; head fixed, body scrolls, actions pinned at its foot */
    '@media (max-width: 767.98px), (max-height: 500px) {',
    '  html.mk-portal-on .mk-dlg { align-items: flex-end; padding: 28px 0 0; }',
    '  html.mk-portal-on .mk-dlg .mk-dlg-panel, html.mk-portal-on .mk-dlg.mk-dlg--wide .mk-dlg-panel { width: 100%; border-radius: 22px 22px 0 0; border-bottom: 0; }',
    '  html.mk-portal-on .mk-dlg .mk-dlg-head { padding: 22px 18px 0; }',
    '  html.mk-portal-on .mk-dlg.mk-dlg--handle .mk-dlg-head::before { content: ""; display: block; width: 40px; height: 4px; border-radius: 4px; background: var(--d-line-strong); margin: -10px auto 14px; }',
    '  html.mk-portal-on .mk-dlg .mk-dlg-body { padding: 0 18px 2px; }',
    '  html.mk-portal-on .mk-dlg .mk-dlg-title { font-size: 22px; }',
    '  html.mk-portal-on .mk-dlg .mk-dlg-x { top: 8px; right: 6px; }',
    '  html.mk-portal-on .mk-dlg .mk-dlg-acts { padding: 12px 18px calc(14px + env(safe-area-inset-bottom)); border-top: 1px solid var(--d-line); }',
    '  html.mk-portal-on .mk-dlg.mk-dlg--nopin .mk-dlg-acts { padding: 12px 0 calc(14px + env(safe-area-inset-bottom)); border-top: 0; }',
    '  html.mk-portal-on .mk-dlg .mk-dlg-acts .mk-dlg-btn { flex: 1 1 100%; }',
    '  html.mk-portal-on .mk-dlg .mk-dlg-acts .mk-dlg-btn--ghost { min-height: 44px; }',
    '  html.mk-portal-on .mk-dlg.mk-dlg--center .mk-dlg-acts { justify-content: stretch; }',
    '}',
    '@media (max-height: 500px) and (min-width: 640px) { html.mk-portal-on .mk-dlg .mk-dlg-panel, html.mk-portal-on .mk-dlg.mk-dlg--wide .mk-dlg-panel { width: 640px; } html.mk-portal-on .mk-dlg { justify-content: center; } }',
    '@media (forced-colors: active) {',
    '  html.mk-portal-on .mk-dlg .mk-dlg-panel { border: 1px solid CanvasText; }',
    '  html.mk-portal-on .mk-dlg .mk-dlg-btn { border: 1px solid ButtonText; }',
    '  html.mk-portal-on:not(.mk-pointer) .mk-dlg :focus-visible { outline: 2px solid Highlight; }',
    '}'
  ].join('\n');
  /* adds !important to every declaration (innermost blocks only: @media preludes are left alone) */
  function important(css) {
    return css.replace(/\{([^{}]*)\}/g, function (m, body) {
      var out = [];
      body.split(';').forEach(function (d) {
        var t = d.trim();
        if (!t) return;
        out.push(t.indexOf(':') > 0 && !/!important$/.test(t) ? t + ' !important' : t);
      });
      return '{ ' + out.join('; ') + '; }';
    });
  }
  function injectDialogCss() {
    try {
      if (document.getElementById('mk-dlg-css')) return;
      if (getComputedStyle(document.documentElement).getPropertyValue('--mk-dlg-css').trim()) return;
      var s = document.createElement('style');
      s.id = 'mk-dlg-css';
      s.textContent = important(DIALOG_CSS);
      (document.head || document.documentElement).appendChild(s);
    } catch (e) { report('dialog css', e); }
  }
  function nativeModal() {
    try { return typeof HTMLDialogElement === 'function' && typeof HTMLDialogElement.prototype.showModal === 'function'; } catch (e) { return false; }
  }
  var FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), summary, [tabindex]:not([tabindex="-1"])';
  function tabbables(root) {
    return qa(FOCUSABLE, root).filter(function (n) {
      try {
        if (n.closest('[hidden], [inert]') || n.getAttribute('tabindex') === '-1') return false;
        return n.getClientRects().length > 0;
      } catch (e) { return false; }
    });
  }
  function focusable(n) {
    try {
      if (!n || n.nodeType !== 1 || !n.isConnected || n.closest('[hidden], [inert]')) return false;
      if (n.disabled) return false;
      return n.getClientRects().length > 0;
    } catch (e) { return false; }
  }
  function focusEl(n) {
    try { n.focus({ preventScroll: true }); } catch (e) { try { n.focus(); } catch (e2) { /* ignore */ } }
    return document.activeElement === n;
  }
  function nodesOf(v) {
    var out = [];
    (function add(x) {
      if (x == null || x === false) return;
      if (Array.isArray(x)) { x.forEach(add); return; }
      if (typeof x === 'object' && x.nodeType === 1) out.push(x);
      else if (typeof x === 'string') out.push(document.createTextNode(x));
    })(v);
    return out;
  }
  function dlgFire(open, id) {
    try { window.dispatchEvent(new CustomEvent('mk-portal:dialog', { detail: { open: open, id: id } })); } catch (e) { /* ignore */ }
  }

  var dlg = null;        /* the open dialog's private state, or null */
  var dlgSeq = 0;
  var tokens = new Map();   /* confirmation token (a frozen empty object) -> {key, exp, seq} */

  function dlgFinish(st, reason, giveFocusBack) {
    if (!st || st.done) return;
    st.done = true;
    if (dlg === st) dlg = null;
    tokens.forEach(function (rec, tok) { if (rec.seq === st.seq) tokens.delete(tok); });
    if (st.busyTimer) clearTimeout(st.busyTimer);
    st.offs.forEach(function (off) { try { off(); } catch (e) { /* ignore */ } });
    st.offs = [];
    try { if (st.native && st.root.open) st.root.close(); } catch (e) { /* ignore */ }
    try { st.root.remove(); } catch (e) { /* ignore */ }
    st.inerted.forEach(function (n) { try { n.removeAttribute('inert'); n.removeAttribute('data-mk-dlg-inert'); } catch (e) { /* ignore */ } });
    st.inerted = [];
    if (!dlg) { try { document.documentElement.classList.remove('mk-dlg-open'); } catch (e) { /* ignore */ } }
    if (giveFocusBack) {
      var target = st.opener;
      if (!focusable(target)) {
        try { target = typeof st.returnFocus === 'function' ? st.returnFocus() : st.returnFocus; } catch (e) { target = null; }
      }
      if (focusable(target)) focusEl(target);
    }
    dlgFire(false, st.id);
    if (typeof st.onClose === 'function') { try { st.onClose(reason); } catch (e) { report('dialog onClose', e); } }
  }

  function dlgParts(st, parts, first) {
    var panel = st.panel, root = st.root;
    if (parts.title != null) {
      var t = String(parts.title).trim();
      if (t) text(st.titleEl, t);
    }
    if (parts.icon !== undefined || first) {
      if (st.iconEl) { st.iconEl.remove(); st.iconEl = null; }
      var icon = nodesOf(parts.icon)[0];
      if (icon && icon.nodeType === 1) { st.iconEl = icon; st.head.insertBefore(icon, st.titleEl); }
    }
    if (parts.pin !== undefined || first) st.pin = parts.pin !== false;
    if (parts.center !== undefined || first) root.classList.toggle('mk-dlg--center', !!parts.center);
    root.classList.toggle('mk-dlg--nopin', !st.pin);
    if (parts.body !== undefined || first) {
      while (st.body.firstChild) st.body.removeChild(st.body.firstChild);
      nodesOf(parts.body).forEach(function (n) { st.body.appendChild(n); });
    }
    if (parts.stack !== undefined) st.stack = !!parts.stack;
    if (parts.actions !== undefined || first) {
      if (st.acts) st.acts.remove();
      st.acts = null;
      var kids = nodesOf(parts.actions);
      if (kids.length) {
        st.acts = el('div', { 'class': 'mk-dlg-acts' });
        kids.forEach(function (n) { st.acts.appendChild(n); });
      }
    }
    if (st.acts) {
      st.acts.classList.toggle('mk-dlg-acts--stack', !!st.stack);
      /* pinned: the panel's last child; not pinned: the body's last child (it follows the body in the scroll) */
      var home = st.pin ? panel : st.body;
      if (home.lastChild !== st.acts) home.appendChild(st.acts);
    }
    if (parts.describedBy !== undefined || first) {
      var d = typeof parts.describedBy === 'string' && /^[A-Za-z][\w-]{0,60}(\s+[A-Za-z][\w-]{0,60}){0,4}$/.test(parts.describedBy) ? parts.describedBy : null;
      if (d) root.setAttribute('aria-describedby', d); else root.removeAttribute('aria-describedby');
    }
    try { st.panel.classList.toggle('is-scrolled', st.body.scrollTop > 2); } catch (e) { /* ignore */ }
  }

  /* open({title, body?, actions?, icon?, id?, opener?, returnFocus?, onClose?, pin?, center?, stack?, wide?,
           handle?, className?, describedBy?, bodyLabel?, closeLabel?, dismissible?, module?, host?}) -> handle | null */
  function dialogOpen(o) {
    var st = null;
    try {
      o = o || {};
      if (!on() || !document.body) return null;
      var title = typeof o.title === 'string' ? o.title.trim() : '';
      if (!title) return null;
      var id = typeof o.id === 'string' && DLG_ID_RE.test(o.id) ? o.id : 'mk-dlg';
      var active = document.activeElement;
      var opener = o.opener && o.opener.nodeType === 1 ? o.opener : (active && active !== document.body && active !== document.documentElement ? active : null);
      if (dlg) {
        var prev = dlg;
        if (prev.root.contains(opener) || !opener) opener = prev.opener;
        dlgFinish(prev, 'replaced', false);
      }
      injectDialogCss();
      var useNative = nativeModal();
      var seq = ++dlgSeq;
      var root = document.createElement(useNative ? 'dialog' : 'div');
      var cls = ['mk-dlg'];
      if (o.wide) cls.push('mk-dlg--wide');
      if (o.handle !== false) cls.push('mk-dlg--handle');
      String(o.className || '').split(/\s+/).forEach(function (c) { if (DLG_CLASS_RE.test(c)) cls.push(c); });
      root.className = cls.join(' ');
      root.id = id;
      root.setAttribute('data-mk-module', String(o.module || 'dialog').replace(/[^\w-]/g, '').slice(0, 40) || 'dialog');
      if (!useNative) { root.setAttribute('role', 'dialog'); root.setAttribute('aria-modal', 'true'); }
      root.setAttribute('aria-labelledby', id + '-title');
      var scrim = el('div', { 'class': 'mk-dlg-scrim', 'aria-hidden': 'true' });
      var x = el('button', { type: 'button', 'class': 'mk-dlg-x', 'aria-label': typeof o.closeLabel === 'string' && o.closeLabel.trim() ? o.closeLabel.trim().slice(0, 40) : 'Close' },
        svg('0 0 24 24', ['M18 6 6 18', 'M6 6l12 12']));
      var titleEl = el('h2', { 'class': 'mk-dlg-title', id: id + '-title', tabindex: '-1' });
      titleEl.setAttribute('autofocus', '');   /* showModal() focuses the title, never the X first */
      var head = el('div', { 'class': 'mk-dlg-head' }, x, titleEl);
      var body = el('div', { 'class': 'mk-dlg-body' });
      if (typeof o.bodyLabel === 'string' && o.bodyLabel.trim()) {
        body.setAttribute('tabindex', '0'); body.setAttribute('role', 'region'); body.setAttribute('aria-label', o.bodyLabel.trim().slice(0, 80));
      }
      var panel = el('div', { 'class': 'mk-dlg-panel' }, head, body);
      root.appendChild(scrim);
      root.appendChild(panel);
      st = {
        seq: seq, id: id, root: root, panel: panel, head: head, body: body, titleEl: titleEl, x: x, iconEl: null, acts: null,
        pin: true, stack: !!o.stack, native: useNative, opener: opener, returnFocus: o.returnFocus, onClose: o.onClose,
        dismissible: o.dismissible !== false, busy: false, busyTimer: null, done: false, offs: [], inerted: [],
        armedAt: Infinity, downAt: -1
      };
      dlgParts(st, { title: title, icon: o.icon, body: o.body, actions: o.actions, pin: o.pin, center: o.center, stack: o.stack, describedBy: o.describedBy }, true);
      var host = document.body;
      if (o.host === 'skio-root' && useNative) host = document.getElementById('skio-root') || document.body;
      host.appendChild(root);
      root.setAttribute('data-mk-open', '');
      dlg = st;
      document.documentElement.classList.add('mk-dlg-open');
      if (useNative) {
        root.showModal();
      } else {
        /* everything else in <body> is out of reach for the keyboard and screen readers while it is open */
        Array.prototype.forEach.call(document.body.children, function (n) {
          if (n !== root && !n.hasAttribute('inert')) { n.setAttribute('inert', ''); n.setAttribute('data-mk-dlg-inert', ''); st.inerted.push(n); }
        });
      }
      function listen(target, type, fn, opts) {
        target.addEventListener(type, fn, opts || false);
        st.offs.push(function () { target.removeEventListener(type, fn, opts || false); });
      }
      /* 1.5.1: from now on the customer can see the pop-up; a press inside its panel records when it began */
      st.armedAt = perfNow();
      var pressIn = function (e) { try { if (st.panel.contains(EV_GET ? EV_GET.target.call(e) : e.target)) st.downAt = evTime(e); } catch (x4) { /* ignore */ } };
      listen(root, 'pointerdown', pressIn, true);
      listen(root, 'mousedown', pressIn, true);
      var tryDismiss = function (reason) {
        if (!st.dismissible || st.busy) return false;
        dlgFinish(st, reason, true);
        return true;
      };
      listen(root, 'keydown', function (e) {
        try {
          if (e.key === 'Escape' || e.key === 'Esc') { e.preventDefault(); e.stopPropagation(); tryDismiss('escape'); return; }
          if (e.key !== 'Tab' || e.altKey || e.ctrlKey || e.metaKey) return;
          var list = tabbables(root);
          if (!list.length) { e.preventDefault(); return; }
          var i = list.indexOf(document.activeElement);
          var next = e.shiftKey ? (i <= 0 ? list[list.length - 1] : null) : (i < 0 || i === list.length - 1 ? list[0] : null);
          if (next) { e.preventDefault(); focusEl(next); }
        } catch (err) { /* ignore */ }
      });
      if (useNative) {
        /* Escape (and the browser's close request): ours decides; a forced close still cleans up */
        listen(root, 'cancel', function (e) { e.preventDefault(); tryDismiss('escape'); });
        listen(root, 'close', function () { if (!st.done) dlgFinish(st, 'escape', true); });
      }
      /* the scrim closes only when the press also started on it (a text selection that ends there does not) */
      var downOnScrim = false;
      listen(root, 'pointerdown', function (e) { downOnScrim = e.target === scrim || e.target === root; });
      listen(root, 'click', function (e) {
        var t = e.target;
        if (t === scrim || t === root) { if (downOnScrim || e.detail === 0) tryDismiss('scrim'); downOnScrim = false; return; }
        if (t && t.closest && t.closest('.mk-dlg-x') === x) { e.preventDefault(); tryDismiss('close-button'); }
      });
      listen(body, 'scroll', function () { panel.classList.toggle('is-scrolled', body.scrollTop > 2); }, { passive: true });
      listen(root, 'focusin', function (e) {
        var t = e.target;
        if (t && /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName) && !/^(checkbox|radio)$/.test(t.type || '')) {
          setTimeout(function () { try { if (!st.done && document.activeElement === t) t.scrollIntoView({ block: 'nearest' }); } catch (x2) { /* ignore */ } }, DLG_FIELD_SCROLL_MS);
        }
      });
      /* fallback (no native modal): focus that leaves the dialog comes back to its title */
      if (!useNative) listen(document, 'focusin', function (e) { if (!st.done && e.target && !root.contains(e.target)) focusEl(titleEl); }, true);
      /* the phone keyboard (iOS lays it over the page): keep the dialog above it */
      var vv = window.visualViewport;
      if (vv && typeof vv.addEventListener === 'function') {
        var kb = function () {
          try {
            var h = Math.max(0, Math.round(window.innerHeight - vv.height - (vv.offsetTop || 0)));
            root.style.setProperty('--mk-kb', (h > 40 ? h : 0) + 'px');
          } catch (x3) { /* ignore */ }
        };
        listen(vv, 'resize', kb);
        listen(vv, 'scroll', kb);
        kb();
      }
      /* a client-side route change (Next) takes the page away: close without moving focus */
      if (on()) startRouteWatch();
      var onRouteChange = function () { if (!st.done) dlgFinish(st, 'route', false); };
      routeListeners.push(onRouteChange);
      st.offs.push(function () { var i = routeListeners.indexOf(onRouteChange); if (i >= 0) routeListeners.splice(i, 1); });
      focusEl(titleEl);
      dlgFire(true, id);
      return dlgHandle(st);
    } catch (e) {
      report('dialog open', e);
      if (st) dlgFinish(st, 'error', false);
      return null;
    }
  }

  function confirmKey(purpose) {
    var p = purpose && typeof purpose === 'object' ? purpose : null;
    if (!p) return null;
    if (p.kind === 'add-line') {
      if (!UUID_RE.test(String(p.subscriptionId)) || !UUID_RE.test(String(p.variantId)) || typeof p.oneTime !== 'boolean') return null;
      return 'add-line|' + String(p.subscriptionId).toLowerCase() + '|' + String(p.variantId).toLowerCase() + '|' + (p.oneTime ? '1' : '0');
    }
    /* r8: the exact day picked and the day shown as "Currently" are part of the purpose */
    if (p.kind === 'next-date') {
      if (typeof p.subscriptionId !== 'string' || !UUID_RE.test(p.subscriptionId) || !ymdValid(p.date) || !ymdValid(p.from) || p.date === p.from) return null;
      return 'next-date|' + p.subscriptionId.toLowerCase() + '|' + p.date + '|' + p.from;
    }
    if (p.kind === 'klaviyo' && (p.channel === 'email' || p.channel === 'sms')) return 'klaviyo|' + p.channel;
    /* R11: one return request (sendReturn): the order, the items and quantities, the reason, the outcome and the note */
    if (p.kind === 'return') {
      var rx = returnInput(p);
      return rx ? 'return|' + JSON.stringify([rx.order, rx.items, rx.reason, rx.outcome, rx.note]) : null;
    }
    /* r9: one reward's claim (claimReward) */
    if (p.kind === 'claim') {
      if (typeof p.rewardId !== 'string' || !UUID_RE.test(p.rewardId)) return null;
      return 'claim:' + p.rewardId.toLowerCase();
    }
    return null;
  }
  /* r8 review fix: the click is read through the browser's own Event getters, taken when the core loaded. They throw
     for anything that is not a real event (a plain object, or Object.create(MouseEvent.prototype) with an own
     isTrusted), and a real event's isTrusted is the browser's own unforgeable property. -> {target, currentTarget} | null */
  var EV_GET = (function () {
    try {
      var d = function (k) { var x = Object.getOwnPropertyDescriptor(Event.prototype, k); return x && typeof x.get === 'function' ? x.get : null; };
      var g = { type: d('type'), target: d('target'), currentTarget: d('currentTarget'), timeStamp: d('timeStamp') };
      /* 1.5.1: the click count (UIEvent.detail), through the browser's own getter too */
      var u = typeof UIEvent === 'function' ? Object.getOwnPropertyDescriptor(UIEvent.prototype, 'detail') : null;
      g.detail = u && typeof u.get === 'function' ? u.get : null;
      return g.type && g.target && g.currentTarget ? g : null;
    } catch (e) { return null; }
  })();
  /* 1.5.1: the page's monotonic clock (the time origin of event.timeStamp); an event's own time when it is on that clock */
  function perfNow() { try { return performance.now(); } catch (e) { return 0; } }
  function evTime(ev) {
    var t = NaN, p = perfNow();
    try { t = EV_GET && EV_GET.timeStamp ? EV_GET.timeStamp.call(ev) : ev.timeStamp; } catch (e) { t = NaN; }
    return typeof t === 'number' && isFinite(t) && t >= 0 && t <= p + 1000 ? t : p;
  }
  /* r8 review fix: one click mints at most one token (a listener on the page cannot mint a second, other purpose from
     the same press) */
  var usedClicks = typeof WeakSet === 'function' ? new WeakSet() : null;
  function realClick(ev) {
    try {
      if (!EV_GET || !ev || typeof ev !== 'object') return null;
      if (EV_GET.type.call(ev) !== 'click') return null;
      if (typeof MouseEvent === 'function' && !(ev instanceof MouseEvent)) return null;
      if (ev.isTrusted !== true) return null;
      var det = 0;
      try { det = EV_GET.detail ? EV_GET.detail.call(ev) | 0 : 0; } catch (e2) { det = 0; }
      return { target: EV_GET.target.call(ev), currentTarget: EV_GET.currentTarget.call(ev), detail: det, time: evTime(ev) };
    } catch (e) { return null; }
  }
  /* a fresh, single-use proof that the customer pressed a button in the open dialog just now */
  function dlgConfirm(st, ev, purpose) {
    try {
      if (st.done || dlg !== st || st.busy || !on()) return null;
      var key = confirmKey(purpose);
      var real = key ? realClick(ev) : null;
      if (!real) return null;
      /* 1.5.1: only a press the customer made on a button they could see. Refused (no side effect; the next deliberate
         press works): the 2nd+ press of a double click; a press less than DLG_ARM_MS after the pop-up opened or its
         buttons were swapped; a pointer press that began before that. Enter / Space (detail 0) count once the time passed. */
      if (real.detail > 1) return null;
      if (!(real.time - st.armedAt >= DLG_ARM_MS)) return null;
      if (real.detail >= 1 && !(st.downAt >= st.armedAt)) return null;
      if (usedClicks && usedClicks.has(ev)) return null;
      var t = real.target && real.target.nodeType === 1 ? real.target : (real.target && real.target.parentElement);
      var btn = t && t.closest ? t.closest('button') : null;
      if (!btn || !st.panel.contains(btn) || btn === st.x || btn.disabled || btn.getAttribute('aria-disabled') === 'true') return null;
      if (real.currentTarget && real.currentTarget !== btn && real.currentTarget.nodeType === 1 && !real.currentTarget.contains(btn)) return null;
      try { if (navigator.userActivation && navigator.userActivation.isActive === false) return null; } catch (e) { /* not supported */ }
      if (usedClicks) usedClicks.add(ev);
      var tok = Object.freeze({});
      var rec = { key: key, exp: now() + CONFIRM_TTL_MS, seq: st.seq, consent: false };
      /* SMS (review fix): the token itself records whether the confirming pop-up holds the customer's ticked consent
         box (input[type=checkbox][data-mk-consent="sms"], enabled) at the moment of the click; klaviyo.subscribeSms()
         sends nothing without it, whatever the caller passes */
      if (key === 'klaviyo|sms') {
        rec.consent = qa('input[type="checkbox"][data-mk-consent="sms"]', st.panel).some(function (b) {
          return b.checked === true && !b.disabled && b.isConnected;
        });
      }
      tokens.set(tok, rec);
      return tok;
    } catch (e) { return null; }
  }
  /* used once, whatever the outcome; valid only for the same purpose, while the same dialog is open.
     -> the token's record, or null */
  function useToken(tok, key) {
    var rec = tok && typeof tok === 'object' ? tokens.get(tok) : null;
    if (rec) tokens.delete(tok);
    return !!rec && !!key && rec.key === key && now() <= rec.exp && !!dlg && dlg.seq === rec.seq && !dlg.done ? rec : null;
  }

  function dlgHandle(st) {
    var h = {
      id: st.id,
      root: st.root,
      get body() { return st.body; },
      get actions() { return st.acts; },
      get title() { return st.titleEl; },
      isOpen: function () { return !st.done && dlg === st; },
      /* replace parts ({title, icon, body, actions, pin, center, stack, describedBy, arm}); focus moves to the
         (new) title unless focus: false. A step change (form -> claim -> claimed) is one set() call. */
      set: function (parts) {
        try {
          if (st.done) return false;
          parts = parts || {};
          dlgParts(st, parts, false);
          /* 1.5.1: new buttons (a new step): they count only DLG_ARM_MS from now, for a press that begins after now.
             arm: false = the same step redrawn in place (a day picked in the calendar; the buttons stay where they were) */
          if ((parts.actions !== undefined || parts.body !== undefined) && parts.arm !== false) st.armedAt = perfNow();
          if (parts.focus !== false) focusEl(st.titleEl);
          return true;
        } catch (e) { report('dialog set', e); return false; }
      },
      focusTitle: function () { return !st.done && focusEl(st.titleEl); },
      /* while busy (a request is running): Escape, the scrim and the X do nothing; confirm() refuses.
         Releases itself after 30 s. */
      busy: function (onOff) {
        if (st.done) return false;
        st.busy = !!onOff;
        if (st.busyTimer) { clearTimeout(st.busyTimer); st.busyTimer = null; }
        if (st.busy) st.busyTimer = setTimeout(function () { st.busy = false; st.panel.removeAttribute('aria-busy'); st.x.removeAttribute('aria-disabled'); }, DLG_BUSY_MAX_MS);
        if (st.busy) { st.panel.setAttribute('aria-busy', 'true'); st.x.setAttribute('aria-disabled', 'true'); }
        else { st.panel.removeAttribute('aria-busy'); st.x.removeAttribute('aria-disabled'); }
        return true;
      },
      isBusy: function () { return !st.done && st.busy; },
      confirm: function (ev, purpose) { return dlgConfirm(st, ev, purpose); },
      /* scroll a node inside the body into view (an alert after an error); the head stays put */
      reveal: function (node) {
        try { if (!st.done && node && st.body.contains(node)) { node.scrollIntoView({ block: 'nearest' }); st.panel.classList.toggle('is-scrolled', st.body.scrollTop > 2); } } catch (e) { /* ignore */ }
      },
      close: function (reason, opts) {
        dlgFinish(st, typeof reason === 'string' ? reason.slice(0, 30) : 'api', !(opts && opts.returnFocus === false));
      }
    };
    return Object.freeze(h);
  }
  var dialog = Object.freeze({
    open: dialogOpen,
    current: function () { return dlg ? dlgHandle(dlg) : null; },
    isOpen: function () { return !!dlg; },
    close: function (reason) { if (dlg) dlgFinish(dlg, typeof reason === 'string' ? reason.slice(0, 30) : 'api', true); }
  });

  /* ------------------------------------------------------------------ */
  /* the ONE write: Skio's addSubscriptionLine (batch 3, 1.1.0)          */
  /* skio() stays read-only. This path sends exactly one document, the  */
  /* one Skio's own "You might also like" carousel sends (bundle 9833,  */
  /* module 22824, verbatim; research 01 a.1), only when the call holds */
  /* a fresh single-use confirmation token minted by the open dialog    */
  /* from the customer's own click for this very subscription, variant  */
  /* and how-often choice. Quantity is 1 and no price is ever sent (Skio */
  /* prices the line). Before sending, the subscription is re-read; it  */
  /* is re-read again after the answer, and before any error is         */
  /* reported. Never retried blindly: only an auth failure (nothing was */
  /* applied) is sent once more, after a re-read.                       */
  /* ------------------------------------------------------------------ */
  var UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  var ADD_LINE_DOC = 'mutation addSubscriptionLineAction($input: AddSubscriptionLineInput!) {\n  addSubscriptionLine(input: $input) {\n    ok\n  }\n}';
  /* r8 (1.3.0): Skio's own date change (bundle 9833, module 22824, export Kd; research 01 F1), verbatim */
  var NEXT_DATE_DOC = 'mutation updateNextBillingDateAction($input: UpdateNextBillingDateInput!) {\n  updateNextBillingDate(input: $input) {\n    ok\n  }\n}';
  /* every write this file may ever send: its document, its operation name and the ONLY input keys it may carry
     (need: the ones it must carry). postWrite() takes a kind from this table, never a document from its caller. */
  var WRITES = Object.freeze({
    'add-line': Object.freeze({ doc: ADD_LINE_DOC, op: 'addSubscriptionLineAction',
      keys: Object.freeze(['subscriptionId', 'variantId', 'quantity', 'upsell']), need: Object.freeze(['subscriptionId', 'variantId', 'quantity']) }),
    'next-date': Object.freeze({ doc: NEXT_DATE_DOC, op: 'updateNextBillingDateAction',
      keys: Object.freeze(['subscriptionId', 'date']), need: Object.freeze(['subscriptionId', 'date']) })
  });
  var WRITE_ALLOWLIST = Object.freeze([ADD_LINE_DOC, NEXT_DATE_DOC]);   /* every document this file may ever send as a write */
  var LINE_CHECK_DOC = 'query mkAddLineCheck($subscriptionId: uuid!) {\n  SubscriptionByPk(id: $subscriptionId) {\n    id\n    status\n    SubscriptionLines(where: {removedAt: {_is_null: true}}) {\n      id\n      quantity\n      priceWithoutDiscount\n      productVariantId\n      customAttributes\n      ProductVariant {\n        id\n      }\n    }\n  }\n}';
  var WRITE_POLL_MS = 2000;       /* re-read every 2 s after Skio's ok, up to 5 more times (as Skio's own Add modal does) */
  var WRITE_POLLS = 5;
  if (typeof config.writePollMs === 'number' && config.writePollMs >= 20 && config.writePollMs <= 5000) WRITE_POLL_MS = config.writePollMs;
  var WRITE_DEADLINE_MS = 25000;  /* the whole write, end to end (pre-read, send, auth retry, re-reads): then outcome 'unknown'
                                     (or 'none' if nothing was sent yet; nothing is sent after it). Under DLG_BUSY_MAX_MS. */
  if (typeof config.writeDeadlineMs === 'number' && config.writeDeadlineMs >= 1000 && config.writeDeadlineMs <= 25000) WRITE_DEADLINE_MS = config.writeDeadlineMs;
  var RESEND_HOLD_MS = 60000;     /* after a send whose outcome is unknown: no new send of that variant for this long */
  if (typeof config.resendHoldMs === 'number' && config.resendHoldMs >= 2000 && config.resendHoldMs <= 600000) RESEND_HOLD_MS = config.resendHoldMs;
  var HELD_READS = 3;             /* a held attempt only looks again: up to 3 reads, WRITE_POLL_MS apart */
  var writing = false;
  /* subscription|variant -> {baseline: [line ids], oneTime, sent, sentAt, accepted, unresolved}: one record per variant
     (whatever the how-often choice), kept while an earlier send of this page view may still be applied by Skio.
     review fix (batch 3): Try again, or the other how-often choice, never sends a second add of a variant while an
     earlier one is unresolved: after Skio said ok: true, never again in this page view; after an unknown outcome,
     not for RESEND_HOLD_MS. */
  var attempts = {};

  /* the instant form Skio's own client sends (toISOString), never a midnight */
  var SENT_INSTANT = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z$/;
  function writeInputOk(kind, x) {
    if (typeof x.subscriptionId !== 'string' || !UUID_RE.test(x.subscriptionId)) return false;
    if (kind === 'add-line') return typeof x.variantId === 'string' && UUID_RE.test(x.variantId) && x.quantity === 1 && (x.upsell === undefined || x.upsell === true);
    if (kind === 'next-date') {
      var t = typeof x.date === 'string' && SENT_INSTANT.test(x.date) ? Date.parse(x.date) : NaN;
      return isFinite(t) && new Date(t).toISOString() === x.date && x.date.slice(11, 16) !== '00:00' && ymdValid(x.date.slice(0, 10));
    }
    return false;
  }
  function postWrite(token, kind, input) {
    /* the table and the allowlist are checked here, at the only place a write leaves this file */
    var w = typeof kind === 'string' && Object.prototype.hasOwnProperty.call(WRITES, kind) ? WRITES[kind] : null;
    if (!w || WRITE_ALLOWLIST.indexOf(w.doc) < 0 || !isWriteOperation(w.doc) || w.doc.indexOf('mutation ' + w.op + '(') !== 0) return Promise.reject(new Error('write not allowed'));
    var src = input && typeof input === 'object' && !Array.isArray(input) ? input : null;
    var clean = {};
    if (src) w.keys.forEach(function (k) { if (Object.prototype.hasOwnProperty.call(src, k) && src[k] !== undefined) clean[k] = src[k]; });
    if (!src || typeof token !== 'string' || !token || Object.keys(src).some(function (k) { return w.keys.indexOf(k) < 0; }) ||
        w.need.some(function (k) { return !Object.prototype.hasOwnProperty.call(clean, k); }) || !writeInputOk(kind, clean)) {
      return Promise.reject(new Error('write input not allowed'));
    }
    return timedRequest(SKIO_URL, {
      method: 'POST',
      headers: {
        'content-type': 'application/json',
        'region': REGION,
        'userpoolid': USER_POOL_ID,
        'authorization': 'Bearer ' + token,
        'x-hasura-from-skio': 'true'    /* what Skio's own client sends on every call (research 01 a.3) */
      },
      body: JSON.stringify({ operationName: w.op, query: w.doc, variables: { input: clean } }),
      credentials: 'omit',
      cache: 'no-store',
      mode: 'cors',
      redirect: 'error',
      referrerPolicy: 'no-referrer'
    });
  }
  function isOneTimeLine(l) {
    var a = Array.isArray(l && l.customAttributes) ? l.customAttributes : [];
    return a.some(function (x) { return x && (x.key === '_isOneTimeUpsell' || x.key === 'isOneTimeUpsell'); });
  }
  function lineVariant(l) {
    var v = l && (l.productVariantId || (l.ProductVariant && l.ProductVariant.id));
    return typeof v === 'string' ? v.toLowerCase() : '';
  }
  /* -> {status, lines:[...]} | null (could not read) */
  function readLines(subscriptionId) {
    return skio(LINE_CHECK_DOC, { subscriptionId: subscriptionId }, { fresh: true, ttl: 0 }).then(function (r) {
      var s = r && !r.errors && r.data ? r.data.SubscriptionByPk : null;
      if (!s || typeof s !== 'object' || String(s.id).toLowerCase() !== subscriptionId.toLowerCase() || !Array.isArray(s.SubscriptionLines)) return null;
      return { status: typeof s.status === 'string' ? s.status : '', lines: s.SubscriptionLines.filter(function (l) { return l && typeof l.id === 'string'; }) };
    }, function () { return null; });
  }
  /* a line of the variant that is not in the baseline; oneTime true / false = only that kind, undefined = either */
  function newLine(sub, variantId, oneTime, baseline) {
    if (!sub) return null;
    for (var i = 0; i < sub.lines.length; i++) {
      var l = sub.lines[i], once = isOneTimeLine(l);
      if (lineVariant(l) === variantId && (oneTime === undefined || once === oneTime) && baseline.indexOf(l.id) < 0) {
        return { id: l.id, quantity: sInt(l.quantity), oneTime: once, priceWithoutDiscount: num(l.priceWithoutDiscount) };
      }
    }
    return null;
  }
  function writeFail(reason, outcome, sent, extra) {
    var r = { ok: false, reason: reason, outcome: outcome, sent: !!sent };
    if (extra) Object.keys(extra).forEach(function (k) { r[k] = extra[k]; });
    return r;
  }
  /* an earlier send of this variant that Skio may still apply */
  function heldAttempt(a) {
    return !!a && a.sent && a.unresolved && (a.accepted || now() - a.sentAt < RESEND_HOLD_MS);
  }
  /* addSubscriptionLine({subscriptionId, variantId, oneTime, confirm[, quantity: 1]}) -> Promise<result>, never rejects */
  function addSubscriptionLine(input) {
    try {
      if (!on()) return Promise.resolve(writeFail('off', 'none', false));
      var inp = input && typeof input === 'object' && !Array.isArray(input) ? input : null;
      var allowed = { subscriptionId: 1, variantId: 1, oneTime: 1, confirm: 1, quantity: 1 };
      if (!inp || Object.keys(inp).some(function (k) { return !allowed[k]; }) ||
          !UUID_RE.test(String(inp.subscriptionId)) || !UUID_RE.test(String(inp.variantId)) ||
          typeof inp.oneTime !== 'boolean' || (inp.quantity !== undefined && inp.quantity !== 1)) {
        return Promise.resolve(writeFail('bad_input', 'none', false));
      }
      if (writing) return Promise.resolve(writeFail('busy', 'none', false));
      var subId = String(inp.subscriptionId).toLowerCase(), varId = String(inp.variantId).toLowerCase(), oneTime = inp.oneTime;
      var key = confirmKey({ kind: 'add-line', subscriptionId: subId, variantId: varId, oneTime: oneTime });
      if (!useToken(inp.confirm, key)) return Promise.resolve(writeFail('not_confirmed', 'none', false));
      var akey = subId + '|' + varId;
      writing = true;
      var sent = false;
      var run = { over: false };      /* set when the deadline answered for this call: nothing is sent or reported after it */
      var att = null;                 /* this call's attempt record, once it may send */
      var input = { subscriptionId: subId, variantId: varId, quantity: 1 };
      if (oneTime) input.upsell = true;
      /* re-read up to n times (first at once, then every WRITE_POLL_MS) until a new line of the variant shows
         (kind: true / false / undefined = either) */
      var findLine = function (baseline, n, kind) {
        var k = 0;
        return (function next() {
          if (run.over) return Promise.resolve(null);
          return readLines(subId).then(function (sub) {
            var line = newLine(sub, varId, kind, baseline);
            if (line || ++k >= n || run.over) return line;
            return sleep(WRITE_POLL_MS).then(next);
          });
        })();
      };
      var settle = function (rec) { if (rec && attempts[akey] === rec) delete attempts[akey]; };
      var ok = function (line, via, rec) { settle(rec); return { ok: true, line: line, via: via, sent: sent }; };
      var main = tokenRecord().then(function (rec) {
        if (!rec) return writeFail('no_token', 'none', false);
        return readLines(subId).then(function (sub) {
          if (!sub) return writeFail('precheck_failed', 'none', false);
          var prev = attempts[akey] || null;
          if (prev) {
            /* an earlier attempt of this page for this variant: if its line is there now, that is the answer */
            var landed = newLine(sub, varId, undefined, prev.baseline);
            if (landed && landed.oneTime === oneTime) return ok(landed, 'reread', prev);
            if (landed) { settle(prev); prev = null; }    /* it landed as the other kind: the duplicate rule below decides */
            else if (heldAttempt(prev)) {
              /* Skio may still apply the earlier send: look again, never send a second add */
              var held = prev;
              return sleep(WRITE_POLL_MS).then(function () { return findLine(held.baseline, HELD_READS - 1); }).then(function (line) {
                if (line && line.oneTime === oneTime) return ok(line, 'reread', held);
                if (line) { settle(held); return writeFail('already_in_order', 'none', false); }
                return writeFail('pending', 'unknown', false, { accepted: !!held.accepted, held: true });
              });
            }
          }
          if (sub.status !== 'ACTIVE') return writeFail('not_active', 'none', false);
          var dup = sub.lines.some(function (l) { return lineVariant(l) === varId && (!oneTime || isOneTimeLine(l)); });
          if (dup) return writeFail('already_in_order', 'none', false);
          var baseline = sub.lines.map(function (l) { return l.id; });
          att = attempts[akey] = { baseline: prev ? prev.baseline.concat(baseline) : baseline, oneTime: oneTime,
            sent: false, sentAt: 0, accepted: false, unresolved: false };
          var send = function (token) {
            if (run.over) return Promise.reject(new Error('deadline'));
            sent = true; att.sent = true; att.unresolved = true; att.sentAt = now();
            return postWrite(token, 'add-line', input);
          };
          return send(rec.token).then(function (r) {
            if (!isAuthFailure(r.status, r.body)) return r;
            /* refused before anything happened (expired login): new token, re-read, then send once more */
            att.unresolved = false;
            return waitForToken({ exclude: rec.token }).then(function (rec2) {
              if (!rec2 || run.over) return r;
              return readLines(subId).then(function (sub2) {
                var landed2 = newLine(sub2, varId, oneTime, att.baseline);
                if (landed2) return { landed: landed2 };
                if (!sub2) return { status: 0, body: undefined };
                return send(rec2.token);
              });
            });
          }).then(function (r) {
            if (r.landed) return ok(r.landed, 'reread', att);
            var b = r.body && typeof r.body === 'object' && !Array.isArray(r.body) ? r.body : null;
            var errs = b && Array.isArray(b.errors) && b.errors.length ? b.errors : null;
            var answer = b && b.data && b.data.addSubscriptionLine;
            if (r.status === 200 && !errs && answer && answer.ok === true) {
              /* accepted: show success only once Skio's own data has the new line */
              att.accepted = true; att.unresolved = true;
              return findLine(att.baseline, 1 + WRITE_POLLS, oneTime).then(function (line) {
                return line ? ok(line, 'answer', att) : writeFail('pending', 'unknown', true, { accepted: true });
              });
            }
            var auth = isAuthFailure(r.status, b);
            var refused = !auth && (r.status === 200 || (r.status >= 400 && r.status < 500)) && !!(errs || (answer && answer.ok === false));
            if (auth || refused) att.unresolved = false;
            /* every error is reported only after a re-read */
            return findLine(att.baseline, refused || auth ? 1 : 3, oneTime).then(function (line) {
              if (line) return ok(line, 'reread', att);
              if (auth) return writeFail('unauthorized', 'refused', true);
              if (refused) { settle(att); return writeFail('refused', 'refused', true); }
              return writeFail('unknown', 'unknown', true);
            });
          }, function (err) {
            if (!run.over) report('addSubscriptionLine', err);
            return findLine(att.baseline, 3, oneTime).then(function (line) {
              return line ? ok(line, 'reread', att) : writeFail('unknown', sent ? 'unknown' : 'none', sent);
            });
          });
        });
      }).then(null, function (err) {
        report('addSubscriptionLine', err);
        return writeFail('error', sent ? 'unknown' : 'none', sent);
      });
      /* one overall deadline: the dialog's busy state (30 s) always outlasts the write */
      var timer = null;
      var deadline = new Promise(function (resolve) {
        timer = setTimeout(function () {
          run.over = true;
          log('addSubscriptionLine: deadline (' + (sent ? 'sent, outcome unknown' : 'nothing sent') + ')');
          resolve(sent ? writeFail('unknown', 'unknown', true, { deadline: true }) : writeFail('error', 'none', false, { deadline: true }));
        }, WRITE_DEADLINE_MS);
      });
      return Promise.race([main, deadline]).then(function (r) {
        clearTimeout(timer);
        run.over = true;
        writing = false;
        invalidate('skio');
        log('addSubscriptionLine:', r.ok ? 'ok via ' + r.via : r.reason);
        return r;
      });
    } catch (e) {
      writing = false;
      report('addSubscriptionLine', e);
      return Promise.resolve(writeFail('error', 'none', false));
    }
  }

  /* ------------------------------------------------------------------ */
  /* the SECOND write: Skio's updateNextBillingDate (r8, 1.3.0)          */
  /* 02-safety-design sec. 4-5, under the same bar as add-line: Skio's  */
  /* own document verbatim (WRITES['next-date']); off unless the        */
  /* loader's MKPortalConfig.writes.nextDate is exactly true; only with */
  /* a fresh single-use token minted by the open dialog from the        */
  /* customer's own click for exactly this subscription, picked day and */
  /* "Currently" day; a fresh read first (her own subscription, every   */
  /* rule of the pop-up's gate, the range, stale and idempotent checks);*/
  /* sent once; success only when a re-read shows the picked store day; */
  /* never retried blindly (only an auth failure, after a re-read).     */
  /* ------------------------------------------------------------------ */
  var DATE_CHECK_DOC = 'query mkNextDateCheck($subscriptionId: uuid!, $storefrontUserId: uuid!) {\n  SubscriptionByPk(id: $subscriptionId) {\n    id\n    storefrontUserId\n    status\n    statusContext\n    nextBillingDate\n    cyclesCompleted\n    isDigital\n    prepaidDeliveryPolicyId\n    prepaidGiftRecipientId\n    BillingPolicy {\n      minCycles\n      Anchors {\n        id\n      }\n    }\n    PrepaidDeliveryPolicy {\n      interval\n      intervalCount\n    }\n  }\n  StorefrontUserByPk(id: $storefrontUserId) {\n    Site {\n      ianaTimezone\n      cpv3Settings\n      settings\n    }\n  }\n}';
  function switchSaysOn(c) {
    try { return !!c && typeof c === 'object' && !!c.writes && typeof c.writes === 'object' && c.writes.nextDate === true; } catch (e) { return false; }
  }
  /* the kill switch: exactly true when the core loaded (NEXT_DATE_AT_LOAD, a boolean recorded then, so an in-place
     edit of MKPortalConfig.writes later cannot turn it on) AND still true in the config object the core loaded with
     (turned off in place = off for good, live tool L11, even if window.MKPortalConfig is replaced later) AND in
     window.MKPortalConfig now */
  function writeEnabled(kind) {
    if (!on()) return false;
    if (kind === 'add-line') return true;
    if (kind === 'next-date') return NEXT_DATE_AT_LOAD === true && switchSaysOn(config) && switchSaysOn(window.MKPortalConfig);
    if (kind === 'claim') return CLAIM_AT_LOAD === true && claimSwitchOn(config) && claimSwitchOn(window.MKPortalConfig);
    if (kind === 'return') return RETURN_WRITES_AT_LOAD === true && returnsEnabled() && returnWritesSwitchOn(config) && returnWritesSwitchOn(window.MKPortalConfig);
    return false;
  }
  function loginUser(rec) {
    var id = rec && rec.payload ? rec.payload['custom:storefrontUserId'] : null;
    return typeof id === 'string' && UUID_RE.test(id) ? id.toLowerCase() : null;
  }
  /* one fresh read -> {sub, site, next, day} | null (unreadable, another subscription, or not the login's own) */
  function readNextDate(subId, userId) {
    return skio(DATE_CHECK_DOC, { subscriptionId: subId, storefrontUserId: userId }, { fresh: true, ttl: 0 }).then(function (r) {
      var d = r && !r.errors && r.data && typeof r.data === 'object' ? r.data : null;
      var s = d ? d.SubscriptionByPk : null, u = d ? d.StorefrontUserByPk : null, site = u && typeof u === 'object' ? u.Site : null;
      if (!s || typeof s !== 'object' || typeof s.id !== 'string' || s.id.toLowerCase() !== subId) return null;
      if (typeof s.storefrontUserId !== 'string' || s.storefrontUserId.toLowerCase() !== userId) return null;
      if (!site || typeof site !== 'object') return null;
      var next = typeof s.nextBillingDate === 'string' && ISO_STAMP.test(s.nextBillingDate) ? s.nextBillingDate : null;
      return { sub: s, site: site, next: next, day: next ? orderDay(next, site.ianaTimezone) : null };
    }, function () { return null; });
  }
  /* changeNextOrderDate({subscriptionId, date, from, confirm}) -> Promise<result>, never rejects */
  function changeNextOrderDate(input) {
    try {
      if (!writeEnabled('next-date')) return Promise.resolve(writeFail('off', 'none', false));
      var inp = input && typeof input === 'object' && !Array.isArray(input) ? input : null;
      var allowed = { subscriptionId: 1, date: 1, from: 1, confirm: 1 };
      if (!inp || Object.keys(inp).some(function (k) { return !allowed[k]; })) return Promise.resolve(writeFail('bad_input', 'none', false));
      /* each value is read once (a getter cannot answer one thing to the check and another to the send) */
      var sid = inp.subscriptionId, date = inp.date, from = inp.from, confirmTok = inp.confirm;
      if (typeof sid !== 'string' || !UUID_RE.test(sid) || typeof date !== 'string' || !ymdValid(date) ||
          typeof from !== 'string' || !ymdValid(from) || date === from) {
        return Promise.resolve(writeFail('bad_input', 'none', false));
      }
      if (writing) return Promise.resolve(writeFail('busy', 'none', false));
      var subId = sid.toLowerCase();
      var key = confirmKey({ kind: 'next-date', subscriptionId: subId, date: date, from: from });
      if (!useToken(confirmTok, key)) return Promise.resolve(writeFail('not_confirmed', 'none', false));
      var dkey = 'next-date|' + subId;
      writing = true;
      var sent = false;
      var run = { over: false };      /* set when the deadline answered for this call: nothing is sent or reported after it */
      var att = null, userId = null;
      /* read up to n times (first at once, then every WRITE_POLL_MS) until the order shows `target`; -> the last
         readable read, or null */
      var look = function (n, target) {
        var k = 0, last = null;
        return (function next() {
          if (run.over) return Promise.resolve(last);
          return readNextDate(subId, userId).then(function (rd) {
            if (rd && rd.day) last = rd;
            if ((rd && rd.day === target) || ++k >= n || run.over) return last;
            return sleep(WRITE_POLL_MS).then(next);
          });
        })();
      };
      var settle = function (rec) { if (rec && attempts[dkey] === rec) delete attempts[dkey]; };
      var ok = function (rd, via, rec) { settle(rec); return { ok: true, date: rd.day, nextBillingDate: rd.next, via: via, sent: sent }; };
      var cur = function (rd) { return { current: rd && rd.day ? rd.day : null }; };
      var proceed = function (pre, rec) {
        var el = nextDateEligible(pre.sub, pre.site);
        if (!el.ok) return writeFail('not_allowed', 'none', false, { rule: el.rule });
        if (!inDateRange(el.range, date)) return writeFail('out_of_range', 'none', false, { current: el.day });
        if (el.day === date) return ok(pre, 'already');
        if (el.day !== from) return writeFail('changed', 'none', false, { current: el.day });
        var when = nextDateInstant(pre.next, date, el.tz);
        var input = { subscriptionId: subId, date: when };
        /* postWrite() checks the same again; checked here first so a refusal there can never count as a send */
        if (!when || !writeInputOk('next-date', input)) return writeFail('bad_input', 'none', false);
        att = attempts[dkey] = { target: date, from: from, sent: false, sentAt: 0, accepted: false, unresolved: false };
        var send = function (token) {
          if (run.over) return Promise.reject(new Error('deadline'));
          sent = true; att.sent = true; att.unresolved = true; att.sentAt = now();
          return postWrite(token, 'next-date', input);
        };
        return send(rec.token).then(function (r) {
          if (!isAuthFailure(r.status, r.body)) return r;
          /* refused before anything happened (expired login): new token, re-read, then send once more */
          att.unresolved = false;
          return waitForToken({ exclude: rec.token }).then(function (rec2) {
            if (run.over) return { auth: true, read: null };
            return readNextDate(subId, userId).then(function (rd) {
              if (rd && rd.day === date) return { landed: rd };
              if (!rec2 || !rd || !rd.day) return { auth: true, read: rd };
              if (rd.day !== from) return { changed: rd };
              return send(rec2.token);
            });
          });
        }).then(function (r) {
          if (r.landed) return ok(r.landed, 'reread', att);
          if (r.changed) { settle(att); return writeFail('changed', 'none', true, cur(r.changed)); }
          if (r.auth) { settle(att); return writeFail('unauthorized', 'refused', true, cur(r.read)); }
          var b = r.body && typeof r.body === 'object' && !Array.isArray(r.body) ? r.body : null;
          var errs = b && Array.isArray(b.errors) && b.errors.length ? b.errors : null;
          var answer = b && b.data && typeof b.data === 'object' ? b.data.updateNextBillingDate : null;
          if (r.status === 200 && !errs && answer && answer.ok === true) {
            /* accepted: success only once Skio's own data shows the picked store day */
            att.accepted = true; att.unresolved = true;
            return look(1 + WRITE_POLLS, date).then(function (rd) {
              if (rd && rd.day === date) return ok(rd, 'answer', att);
              if (rd && rd.day !== from) { settle(att); return writeFail('mismatch', 'unknown', true, { accepted: true, current: rd.day }); }
              return writeFail('pending', 'unknown', true, { accepted: true, current: rd ? rd.day : null });
            });
          }
          var auth = isAuthFailure(r.status, b);
          var refused = !auth && ((r.status >= 400 && r.status < 500) || (r.status === 200 && !!(errs || (answer && answer.ok === false))));
          if (auth || refused) att.unresolved = false;
          /* every error is reported only after a re-read */
          return look(refused || auth ? 1 : 3, date).then(function (rd) {
            if (rd && rd.day === date) return ok(rd, 'reread', att);
            if (auth) { settle(att); return writeFail('unauthorized', 'refused', true, cur(rd)); }
            if (refused) { settle(att); return writeFail('refused', 'refused', true, cur(rd)); }
            return writeFail('unknown', 'unknown', true, cur(rd));
          });
        }, function (err) {
          if (!run.over) report('changeNextOrderDate', err);
          return look(3, date).then(function (rd) {
            if (rd && rd.day === date) return ok(rd, 'reread', att);
            if (!sent) settle(att);
            return writeFail(sent ? 'unknown' : 'error', sent ? 'unknown' : 'none', sent, cur(rd));
          });
        });
      };
      var main = tokenRecord().then(function (rec) {
        if (!rec) return writeFail('no_token', 'none', false);
        userId = loginUser(rec);
        if (!userId) return writeFail('precheck_failed', 'none', false);
        return readNextDate(subId, userId).then(function (pre) {
          if (!pre) return writeFail('precheck_failed', 'none', false);
          var prev = attempts[dkey] || null;
          if (prev && pre.day && pre.day === prev.target) {
            /* an earlier attempt of this page landed: that is the answer when the same day was asked */
            if (prev.target === date) return ok(pre, 'reread', prev);
            settle(prev);
          } else if (heldAttempt(prev)) {
            /* Skio may still apply the earlier send: look again, never send a second change */
            var held = prev;
            return sleep(WRITE_POLL_MS).then(function () { return look(HELD_READS - 1, held.target); }).then(function (rd) {
              if (rd && rd.day === held.target) {
                if (held.target === date) return ok(rd, 'reread', held);
                settle(held);
                return proceed(rd, rec);
              }
              return writeFail('pending', 'unknown', false, { accepted: !!held.accepted, held: true, current: rd ? rd.day : pre.day });
            });
          }
          return proceed(pre, rec);
        });
      }).then(null, function (err) {
        report('changeNextOrderDate', err);
        return writeFail('error', sent ? 'unknown' : 'none', sent);
      });
      /* one overall deadline: the dialog's busy state (30 s) always outlasts the write */
      var timer = null;
      var deadline = new Promise(function (resolve) {
        timer = setTimeout(function () {
          run.over = true;
          log('changeNextOrderDate: deadline (' + (sent ? 'sent, outcome unknown' : 'nothing sent') + ')');
          resolve(sent ? writeFail('unknown', 'unknown', true, { deadline: true }) : writeFail('error', 'none', false, { deadline: true }));
        }, WRITE_DEADLINE_MS);
      });
      return Promise.race([main, deadline]).then(function (r) {
        clearTimeout(timer);
        run.over = true;
        writing = false;
        invalidate('skio');
        log('changeNextOrderDate:', r.ok ? 'ok via ' + r.via : r.reason);
        return r;
      });
    } catch (e) {
      writing = false;
      report('changeNextOrderDate', e);
      return Promise.resolve(writeFail('error', 'none', false));
    }
  }
  /* nextOrderDate(subscriptionId) -> Promise<{date, nextBillingDate, status} | null>: read only ("Check again") */
  function nextOrderDate(subscriptionId) {
    try {
      if (!on() || typeof subscriptionId !== 'string' || !UUID_RE.test(subscriptionId)) return Promise.resolve(null);
      var subId = subscriptionId.toLowerCase();
      return tokenRecord().then(function (rec) {
        var uid = loginUser(rec);
        if (!uid) return null;
        return readNextDate(subId, uid).then(function (rd) {
          return rd && rd.day ? { date: rd.day, nextBillingDate: rd.next, status: typeof rd.sub.status === 'string' ? rd.sub.status : '' } : null;
        });
      }).then(null, function (err) { report('nextOrderDate', err); return null; });
    } catch (e) { return Promise.resolve(null); }
  }

  /* ------------------------------------------------------------------ */
  /* the THIRD write: Skio's own claim request (r9, 1.4.0; C9 option c)  */
  /* Skio's claim link (/a/account/credits/claim?id=...) grants nothing */
  /* by itself: its page's script sends ONE POST to api.skio.com with   */
  /* the reward id and the login token in the JSON body, then reloads   */
  /* the portal whatever the answer. claimReward() sends that same      */
  /* request from our pop-up (no new tab, no reload), under the same    */
  /* bar as the other writes plus: off unless writes.claim === true;    */
  /* only the two reward ids of the claim guard snippet; only after     */
  /* Klaviyo accepted that channel in this page view for the same      */
  /* customer; once per reward per page view; never retried. Skio       */
  /* refuses a second claim of a reward (Cowork L3, 2026-10-13), so the */
  /* fallback link (today's claim link) is safe after any outcome.      */
  /* ------------------------------------------------------------------ */
  /* every REST write this file may ever send: its URL and the ONLY input keys it carries (in this order, then token,
     region, userPoolId, as Skio's helper 78109.U builds the body). postRestWrite() takes a kind, never a URL. */
  var REST_WRITES = Object.freeze({
    'claim': Object.freeze({ url: CLAIM_URL, keys: Object.freeze(['id']) })
  });
  var REST_WRITE_ALLOWLIST = Object.freeze([CLAIM_URL]);   /* every URL this file may ever send a REST write to */
  var CLAIM_TOKEN_WAIT_MS = 5000;  /* a claim waits at most this long for a usable login token (then no_token) */
  var NUDGE_MIN_MS = 1250;         /* nudgeSkio(): at most one visibilitychange per 1.25 s */
  var claimSent = {};              /* reward id -> true once its request went out (this page view) */
  var signedUp = { email: null, sms: null };   /* channel -> the login's storefrontUserId when Klaviyo accepted it */
  var lastNudge = 0;

  function claimSwitchOn(c) {
    try { return !!c && typeof c === 'object' && !!c.writes && typeof c.writes === 'object' && c.writes.claim === true; } catch (e) { return false; }
  }
  /* the claim guard snippet's rule 3 (snippets/mk-portal-claim-guard.liquid, ids filled in by tools/set_claim_ids.py):
     ONE <style data-mk-portal="claim-guard"> in <head>, holding exactly two different reward ids, the newsletter's
     first, then the texts'. Anything else (no ids, one, three, two guards, outside <head>) -> null: no claim in place
     (the pop-up offers today's claim link). Never a list of ids of its own. */
  function readClaimGuardIds() {
    try {
      var st = document.querySelectorAll('style[data-mk-portal="claim-guard"]');
      if (st.length !== 1 || !document.head || st[0].parentNode !== document.head) return null;
      var css = String(st[0].textContent || ''), ids = [], m;
      var re = /a\[href\*="\/credits\/claim\?id="\]\[href\*="([0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12})" i\]/g;
      while ((m = re.exec(css))) ids.push(m[1].toLowerCase());
      if (ids.length !== 2 || ids[0] === ids[1]) return null;
      return Object.freeze({ newsletter: ids[0], sms: ids[1] });
    } catch (e) { return null; }
  }
  /* the Klaviyo channel of a known reward id, or null */
  function claimChannel(id) {
    if (!CLAIM_IDS || typeof id !== 'string') return null;
    if (id === CLAIM_IDS.newsletter) return 'email';
    if (id === CLAIM_IDS.sms) return 'sms';
    return null;
  }
  /* Klaviyo accepted this channel's sign-up: remember for whom. 1.4.1 (r9 review): uid = the login's storefrontUserId at
     the press (klaviyoSubscribe), recorded only when the same customer is still logged in now, else null (no claim) */
  function loginNowId() {
    try { return loginUser(readToken()); } catch (e) { return null; }
  }
  function noteSignedUp(channel, uid) {
    try { signedUp[channel] = typeof uid === 'string' && uid === loginNowId() ? uid : null; } catch (e) { signedUp[channel] = null; }
  }
  function tokenHostOk(url) {
    try {
      var u = new URL(url);
      return u.protocol === 'https:' && TOKEN_HOSTS.indexOf(u.host) >= 0 && !u.search && !u.hash && !u.username && !u.password;
    } catch (e) { return false; }
  }
  function restInputOk(kind, x) {
    if (!x || typeof x !== 'object' || Array.isArray(x)) return false;
    var w = Object.prototype.hasOwnProperty.call(REST_WRITES, kind) ? REST_WRITES[kind] : null;
    if (!w || Object.keys(x).length !== w.keys.length || w.keys.some(function (k) { return !Object.prototype.hasOwnProperty.call(x, k); })) return false;
    if (kind === 'claim') return typeof x.id === 'string' && UUID_RE.test(x.id) && claimChannel(x.id) !== null;
    return false;
  }
  function postRestWrite(token, kind, input) {
    /* the table, the allowlist and the token host list are checked here, at the only place a REST write leaves this file */
    var w = typeof kind === 'string' && Object.prototype.hasOwnProperty.call(REST_WRITES, kind) ? REST_WRITES[kind] : null;
    if (!w || REST_WRITE_ALLOWLIST.indexOf(w.url) < 0 || !tokenHostOk(w.url)) return Promise.reject(new Error('write not allowed'));
    if (typeof token !== 'string' || !token || !restInputOk(kind, input)) return Promise.reject(new Error('write input not allowed'));
    var body = {};
    w.keys.forEach(function (k) { body[k] = input[k]; });
    body.token = token;
    body.region = REGION;
    body.userPoolId = USER_POOL_ID;
    return timedRequest(w.url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },   /* the one header Skio's own helper sends */
      body: JSON.stringify(body),
      credentials: 'omit',
      cache: 'no-store',
      mode: 'cors',
      redirect: 'error',            /* a redirect would carry the body (and the token) elsewhere: refuse it */
      referrerPolicy: 'no-referrer'
    });
  }
  function claimResult(ok, outcome, reason, sent, status) {
    var r = { ok: !!ok, outcome: outcome, reason: reason, sent: !!sent };
    if (typeof status === 'number') r.status = status;
    return r;
  }
  /* canClaim(rewardId) -> boolean: claimReward() would pass every check that needs no click and no login token */
  function canClaim(rewardId) {
    try {
      if (!writeEnabled('claim') || typeof rewardId !== 'string' || !UUID_RE.test(rewardId)) return false;
      var id = rewardId.toLowerCase(), ch = claimChannel(id);
      /* 1.4.1 (r9 review): and the customer logged in now is the one Klaviyo accepted */
      return !!ch && !claimSent[id] && !writing && typeof signedUp[ch] === 'string' && signedUp[ch] === loginNowId();
    } catch (e) { return false; }
  }
  /* claimReward({rewardId, confirm}) -> Promise<result>, never rejects */
  function claimReward(input) {
    var locked = false;
    try {
      if (!writeEnabled('claim')) return Promise.resolve(claimResult(false, 'none', 'off', false));
      var inp = input && typeof input === 'object' && !Array.isArray(input) ? input : null;
      if (!inp || Object.keys(inp).length !== 2 || !Object.prototype.hasOwnProperty.call(inp, 'rewardId') || !Object.prototype.hasOwnProperty.call(inp, 'confirm')) {
        return Promise.resolve(claimResult(false, 'none', 'bad_input', false));
      }
      /* each value is read once (a getter cannot answer one thing to the check and another to the send) */
      var rid = inp.rewardId, tok = inp.confirm;
      if (typeof rid !== 'string' || !UUID_RE.test(rid)) return Promise.resolve(claimResult(false, 'none', 'bad_input', false));
      var id = rid.toLowerCase(), channel = claimChannel(id);
      if (!channel) return Promise.resolve(claimResult(false, 'none', 'unknown_reward', false));
      if (claimSent[id]) return Promise.resolve(claimResult(false, 'none', 'already_sent', false));
      if (writing) return Promise.resolve(claimResult(false, 'none', 'busy', false));
      if (!useToken(tok, confirmKey({ kind: 'claim', rewardId: id }))) return Promise.resolve(claimResult(false, 'none', 'not_confirmed', false));
      if (typeof signedUp[channel] !== 'string') return Promise.resolve(claimResult(false, 'none', 'not_signed_up', false));
      writing = true;
      locked = true;
      var sent = false;
      var main = tokenRecord({ timeout: Math.min(TOKEN_WAIT_MS, CLAIM_TOKEN_WAIT_MS) }).then(function (rec) {
        if (!rec) return claimResult(false, 'none', 'no_token', false);
        /* the customer logged in now is the one Klaviyo accepted (a logout / login without a page load drops it) */
        if (loginUser(rec) !== signedUp[channel]) return claimResult(false, 'none', 'not_signed_up', false);
        if (!writeEnabled('claim')) return claimResult(false, 'none', 'off', false);
        if (claimSent[id]) return claimResult(false, 'none', 'already_sent', false);
        var body = { id: id };
        /* postRestWrite() checks the same again; checked here first so a refusal there can never count as a send */
        if (!restInputOk('claim', body)) return claimResult(false, 'none', 'error', false);
        claimSent[id] = true;
        sent = true;
        return postRestWrite(rec.token, 'claim', body).then(function (r) {
          var s = r && typeof r.status === 'number' ? r.status : 0;
          if (s >= 200 && s < 300) return claimResult(true, 'ok', 'ok', true, s);
          if (s === 401 || s === 403) return claimResult(false, 'unauthorized', 'unauthorized', true, s);
          return claimResult(false, 'refused', 'refused', true, s);
        }, function (err) {
          return claimResult(false, 'unknown', err && err.message === 'timeout' ? 'timeout' : 'network', true);
        });
      }).then(null, function (err) {
        report('claimReward', err);
        return claimResult(false, sent ? 'unknown' : 'none', sent ? 'network' : 'error', sent);
      });
      return main.then(function (r) {
        writing = false;
        if (r.sent) invalidate('skio');
        log('claimReward:', r.outcome + ' (' + r.reason + (r.status ? ', HTTP ' + r.status : '') + ')');
        return r;
      });
    } catch (e) {
      if (locked) writing = false;
      report('claimReward', e);
      return Promise.resolve(claimResult(false, 'none', 'error', false));
    }
  }
  /* nudgeSkio() -> boolean: Skio's react-query refetches on the window's visibilitychange (focusManager; reward-state-info,
     tier-progress-info and customer-info use refetchOnWindowFocus 'always'). Read only: no request of ours. */
  function nudgeSkio() {
    try {
      if (!on() || document.visibilityState !== 'visible') return false;
      var t = now();
      if (lastNudge && t - lastNudge < NUDGE_MIN_MS) return false;
      lastNudge = t;
      window.dispatchEvent(new Event('visibilitychange'));
      return true;
    } catch (e) { return false; }
  }

  /* ------------------------------------------------------------------ */
  /* the FOURTH write: a return request (R11, 1.6.0; Jim's OK            */
  /* 2026-10-27). POST RETURNS_URL with the login token as a Bearer      */
  /* header (as for the read) and a JSON body of exactly RETURN_KEYS.    */
  /* Off unless the loader's mk_portal_returns AND                       */
  /* mk_portal_returns_writes are exactly true (recorded at load); only  */
  /* with a fresh single-use token minted by the open dialog from the    */
  /* customer's own click for exactly these choices; a fresh read first; */
  /* a fresh random requestId per call (the service never makes two      */
  /* requests for one id); sent once; success only once a re-read shows  */
  /* the request; a lost answer is only ever re-read, never re-sent; a   */
  /* 401 (refused before anything happened) is sent once more with a     */
  /* renewed login token and the SAME requestId.                         */
  /* ------------------------------------------------------------------ */
  var RETURN_KEYS = Object.freeze(['requestId', 'order', 'items', 'reason', 'note', 'outcome']);
  var RETURN_REASONS = Object.freeze(['damaged', 'wrong_item', 'taste', 'not_needed', 'other']);
  var RETURN_OUTCOMES = Object.freeze(['refund', 'replacement']);
  var RETURN_NOTE_MAX = 250;           /* the service's NOTE_MAX */
  var RETURN_ORDER_RE = /^#[1-9][0-9]{0,11}$/;
  var RETURN_KEY_RE = /^[A-Za-z0-9_-]{22}$/;
  /* order -> {before: [request names], name, sentAt, accepted, choice}: kept while this page's send for that order may
     still arrive. choice: the items, reason and outcome that send asked for (a held send answers only for those) */
  var returnHolds = {};
  /* review fix: every ok read (no send running) drops the holds whose request it shows, so a hold the service accepted
     ends on the Returns screen's own reads too (before, only checkReturn() and sendReturn() ended it) */
  function dropSeenHolds(data) {
    try {
      Object.keys(returnHolds).forEach(function (order) {
        var h = returnHolds[order];
        if (h && h.sentAt > 0 && findRequest(orderView(data, order), h.name, h.before)) delete returnHolds[order];
      });
    } catch (e) { /* ignore */ }
  }
  /* the part of a choice a held send is compared on (the note left out: the same items, reason and outcome are the
     same request) */
  function returnPick(x) { return JSON.stringify([x.items, x.reason, x.outcome]); }
  /* the customer's choices, checked as the service checks them -> {order, items: [{key, quantity}] sorted by key,
     reason, note (cleaned), outcome} | null. Reads only those five keys of x. */
  function returnInput(x) {
    try {
      if (!x || typeof x !== 'object') return null;
      var order = x.order, items = x.items, reason = x.reason, note = x.note, outcome = x.outcome;
      if (typeof order !== 'string' || !RETURN_ORDER_RE.test(order)) return null;
      if (!Array.isArray(items) || items.length < 1 || items.length > 20) return null;
      var seen = {}, list = [];
      for (var i = 0; i < items.length; i++) {
        var it = items[i];
        if (!it || typeof it !== 'object' || Array.isArray(it) || Object.keys(it).length !== 2 ||
            !Object.prototype.hasOwnProperty.call(it, 'key') || !Object.prototype.hasOwnProperty.call(it, 'quantity')) return null;
        var k = it.key, n = it.quantity;
        if (typeof k !== 'string' || !RETURN_KEY_RE.test(k) || seen[k] || typeof n !== 'number' || n !== Math.floor(n) || n < 1 || n > 99) return null;
        seen[k] = true;
        list.push({ key: k, quantity: n });
      }
      list.sort(function (a, b) { return a.key < b.key ? -1 : a.key > b.key ? 1 : 0; });
      if (RETURN_REASONS.indexOf(reason) < 0 || RETURN_OUTCOMES.indexOf(outcome) < 0 || typeof note !== 'string') return null;
      var t = note.replace(/[\u0000-\u001f\u007f-\u009f\u2028\u2029]/g, ' ').replace(/\s+/g, ' ').trim();
      if (t.length > RETURN_NOTE_MAX) return null;
      if (reason === 'other' && t.replace(/[^A-Za-z0-9]/g, '').length < 3) return null;
      return { order: order, items: list, reason: reason, note: t, outcome: outcome };
    } catch (e) { return null; }
  }
  /* a random v4 UUID (the browser's own generator), or null */
  function newRequestId() {
    try {
      if (window.crypto && typeof window.crypto.randomUUID === 'function') {
        var u = String(window.crypto.randomUUID()).toLowerCase();
        return UUID_RE.test(u) ? u : null;
      }
      if (!window.crypto || typeof window.crypto.getRandomValues !== 'function') return null;
      var b = window.crypto.getRandomValues(new Uint8Array(16)), h = '';
      b[6] = (b[6] & 15) | 64;
      b[8] = (b[8] & 63) | 128;
      for (var i = 0; i < 16; i++) h += (b[i] < 16 ? '0' : '') + b[i].toString(16);
      return h.slice(0, 8) + '-' + h.slice(8, 12) + '-' + h.slice(12, 16) + '-' + h.slice(16, 20) + '-' + h.slice(20);
    } catch (e) { return null; }
  }
  function postReturn(token, body) {
    /* the URL, the host list and the body's keys are checked here, at the only place a return leaves this file */
    if (!tokenHostOk(RETURNS_URL) || typeof token !== 'string' || !token) return Promise.reject(new Error('write not allowed'));
    var b = body && typeof body === 'object' && !Array.isArray(body) ? body : null;
    if (!b || Object.keys(b).join(',') !== RETURN_KEYS.join(',') || typeof b.requestId !== 'string' || !UUID_RE.test(b.requestId) ||
        !returnInput(b)) return Promise.reject(new Error('write input not allowed'));
    var out = {};
    RETURN_KEYS.forEach(function (k) { out[k] = b[k]; });
    return timedRequest(RETURNS_URL, {
      method: 'POST',
      headers: { 'content-type': 'application/json', 'authorization': 'Bearer ' + token },
      body: JSON.stringify(out),
      credentials: 'omit',
      cache: 'no-store',
      mode: 'cors',
      redirect: 'error',            /* a redirect would carry the token elsewhere: refuse it */
      referrerPolicy: 'no-referrer'
    });
  }
  function heldReturn(h) { return !!h && h.sentAt > 0 && (h.accepted || now() - h.sentAt < RESEND_HOLD_MS); }
  function returnHeld(order) {
    try { return typeof order === 'string' && Object.prototype.hasOwnProperty.call(returnHolds, order) && heldReturn(returnHolds[order]); } catch (e) { return false; }
  }
  function orderView(data, order) {
    var list = data && Array.isArray(data.orders) ? data.orders : [];
    for (var i = 0; i < list.length; i++) if (list[i] && list[i].name === order) return list[i];
    return null;
  }
  /* the request named `name`, or (name null) the newest one not in `before` */
  function findRequest(v, name, before) {
    var reqs = v && Array.isArray(v.requests) ? v.requests : [];
    for (var i = 0; i < reqs.length; i++) {
      var r = reqs[i];
      if (!r || typeof r.name !== 'string') continue;
      if (name ? r.name === name : (before || []).indexOf(r.name) < 0) return r;
    }
    return null;
  }
  function publicRequest(r) {
    return copy({ name: r.name, status: r.status, createdAt: r.createdAt || null, prefers: r.prefers || null, items: Array.isArray(r.items) ? r.items : [] });
  }
  /* checkReturn(order) -> Promise<{seen, request?, held, read} | null>: read only ("Check again"), never a write */
  function checkReturn(order) {
    try {
      if (!returnsEnabled() || typeof order !== 'string' || !RETURN_ORDER_RE.test(order)) return Promise.resolve(null);
      var h = Object.prototype.hasOwnProperty.call(returnHolds, order) ? returnHolds[order] : null;
      return returns({ fresh: true }).then(function (r) {
        if (!r || !r.ok) return { seen: false, held: heldReturn(h), read: false };
        var req = h ? findRequest(orderView(r.data, order), h.name, h.before) : null;
        if (req) {
          if (returnHolds[order] === h) delete returnHolds[order];
          return { seen: true, request: publicRequest(req), held: false, read: true };
        }
        return { seen: false, held: heldReturn(h), read: true };
      }).then(null, function (err) { report('checkReturn', err); return null; });
    } catch (e) { return Promise.resolve(null); }
  }
  /* sendReturn({order, items, reason, note, outcome, confirm}) -> Promise<result>, never rejects */
  function sendReturn(input) {
    try {
      if (!writeEnabled('return')) return Promise.resolve(writeFail('off', 'none', false));
      var inp = input && typeof input === 'object' && !Array.isArray(input) ? input : null;
      var allowed = { order: 1, items: 1, reason: 1, note: 1, outcome: 1, confirm: 1 };
      if (!inp || Object.keys(inp).length !== 6 || Object.keys(inp).some(function (k) { return !allowed[k]; })) return Promise.resolve(writeFail('bad_input', 'none', false));
      /* each value is read once (a getter cannot answer one thing to the check and another to the send) */
      var x = returnInput({ order: inp.order, items: inp.items, reason: inp.reason, note: inp.note, outcome: inp.outcome }), confirmTok = inp.confirm;
      if (!x) return Promise.resolve(writeFail('bad_input', 'none', false));
      if (writing) return Promise.resolve(writeFail('busy', 'none', false));
      var key = confirmKey({ kind: 'return', order: x.order, items: x.items, reason: x.reason, note: x.note, outcome: x.outcome });
      if (!useToken(confirmTok, key)) return Promise.resolve(writeFail('not_confirmed', 'none', false));
      writing = true;
      var sent = false, hold = null;
      var run = { over: false };      /* set when the deadline answered for this call: nothing is sent or reported after it */
      /* read up to n times (first at once, then every WRITE_POLL_MS) until the order shows the request; -> it, or null */
      var look = function (n, name, before) {
        var k = 0;
        return (function next() {
          if (run.over) return Promise.resolve(null);
          return returns({ fresh: true }).then(function (r) {
            var req = r && r.ok ? findRequest(orderView(r.data, x.order), name, before) : null;
            if (req || ++k >= n || run.over) return req;
            return sleep(WRITE_POLL_MS).then(next);
          });
        })();
      };
      var settle = function () { if (hold && returnHolds[x.order] === hold) delete returnHolds[x.order]; };
      var ok = function (req, via) { settle(); return { ok: true, request: publicRequest(req), via: via, sent: sent }; };
      var proceed = function (rec) {
        return returns({ fresh: true }).then(function (pre) {
          if (!pre || !pre.ok) return writeFail('precheck_failed', 'none', false);
          var v = orderView(pre.data, x.order);
          if (!v) return writeFail('changed', 'none', false, { state: 'unknown_order' });
          if (v.state !== 'returnable') return writeFail('changed', 'none', false, { state: v.state });
          var short = x.items.some(function (it) {
            var have = null;
            v.items.forEach(function (i) { if (i && i.key === it.key) have = i; });
            return !have || !(typeof have.returnable === 'number' && have.returnable >= it.quantity);
          });
          if (short) return writeFail('changed', 'none', false, { state: 'quantity' });
          var before = v.requests.map(function (r) { return r && r.name; }).filter(function (n) { return typeof n === 'string'; });
          var requestId = newRequestId();
          if (!requestId) return writeFail('error', 'none', false);
          var body = { requestId: requestId, order: x.order, items: x.items, reason: x.reason, note: x.note, outcome: x.outcome };
          hold = returnHolds[x.order] = { before: before, name: null, sentAt: 0, accepted: false, choice: returnPick(x) };
          var send = function (token) {
            if (run.over) return Promise.reject(new Error('deadline'));
            sent = true;
            hold.sentAt = now();
            return postReturn(token, body);
          };
          return send(rec.token).then(function (r) {
            if (r.status !== 401) return r;
            /* refused before anything happened (an expired login): the same press once more, renewed token, same id */
            return waitForToken({ exclude: rec.token }).then(function (rec2) {
              if (!rec2 || run.over) return r;
              return send(rec2.token);
            });
          }).then(function (r) {
            var b = r.body && typeof r.body === 'object' && !Array.isArray(r.body) ? r.body : {};
            var code = typeof b.code === 'string' && /^[a-z_]{1,40}$/.test(b.code) ? b.code : null;
            var made = r.status === 200 && b.ok === true && b['return'] && typeof b['return'] === 'object' && typeof b['return'].name === 'string' ? b['return'].name : null;
            if (made) {
              /* Shopify made it: success only once a re-read shows it */
              hold.accepted = true;
              hold.name = made;
              return look(1 + WRITE_POLLS, made, null).then(function (req) {
                if (req) return ok(req, 'answer');
                return writeFail('pending', 'unknown', true, { accepted: true });
              });
            }
            if (r.status === 401) { settle(); return writeFail('unauthorized', 'refused', true); }
            if ((r.status === 409 && code !== 'busy') || (r.status === 404 && code === 'unknown_order')) {
              settle();
              return writeFail('changed', 'none', true, { state: code || 'conflict' });
            }
            if (r.status === 403 && code === 'writes_off') { settle(); return writeFail('off', 'none', true); }
            if (r.status === 429) { settle(); return writeFail('rate_limited', 'refused', true); }
            if (r.status >= 400 && r.status < 500 && r.status !== 409) { settle(); return writeFail('refused', 'refused', true, { status: r.status, code: code }); }
            /* 202 pending, 409 busy, 5xx: Shopify may have it (or another press for this order may): re-read only */
            return look(3, null, hold.before).then(function (req) {
              if (req) return ok(req, 'reread');
              if ((r.status === 502 || r.status === 503) && (b.error === 'upstream_error' || b.error === 'unavailable')) {
                /* the service's own answer: it made nothing (a lost Shopify answer is its 202, never a 5xx) */
                settle();
                return writeFail('refused', 'refused', true, { status: r.status });
              }
              return writeFail(r.status === 409 ? 'pending' : 'unknown', 'unknown', true, code ? { code: code } : null);
            });
          }, function (err) {
            if (!run.over) report('sendReturn', err);
            if (!sent) { settle(); return writeFail('error', 'none', false); }
            return look(3, null, hold.before).then(function (req) {
              if (req) return ok(req, 'reread');
              return writeFail('unknown', 'unknown', true);
            });
          });
        });
      };
      var main = tokenRecord().then(function (rec) {
        if (!rec) return writeFail('no_token', 'none', false);
        var prev = Object.prototype.hasOwnProperty.call(returnHolds, x.order) ? returnHolds[x.order] : null;
        if (prev && !heldReturn(prev)) { delete returnHolds[x.order]; prev = null; }
        if (prev) {
          /* this page's earlier send for this order may still arrive: look again, never send a second one */
          hold = prev;
          var same = prev.choice === returnPick(x);
          return look(HELD_READS, prev.name, prev.before).then(function (req) {
            /* review fix: the request found answers only for the choice that send asked for. Another choice (other
               items, reason or outcome) is never reported as made: nothing is sent; once the earlier one shows, the
               order has changed (the screen reads again and the customer sends the new choice with a new press) */
            if (req && same) return ok(req, 'reread');
            if (req) { settle(); return writeFail('changed', 'none', false, { state: 'held_seen' }); }
            if (!same) return writeFail('held', 'none', false, { accepted: !!prev.accepted, held: true });
            return writeFail('pending', 'unknown', false, { accepted: !!prev.accepted, held: true });
          });
        }
        return proceed(rec);
      }).then(null, function (err) {
        report('sendReturn', err);
        return writeFail('error', sent ? 'unknown' : 'none', sent);
      });
      /* one overall deadline: the dialog's busy state (30 s) always outlasts the write */
      var timer = null;
      var deadline = new Promise(function (resolve) {
        timer = setTimeout(function () {
          run.over = true;
          log('sendReturn: deadline (' + (sent ? 'sent, outcome unknown' : 'nothing sent') + ')');
          resolve(sent ? writeFail('unknown', 'unknown', true, { deadline: true }) : writeFail('error', 'none', false, { deadline: true }));
        }, WRITE_DEADLINE_MS);
      });
      return Promise.race([main, deadline]).then(function (r) {
        clearTimeout(timer);
        run.over = true;
        writing = false;
        invalidate('returns');
        log('sendReturn:', r.ok ? 'ok via ' + r.via : r.reason);
        return r;
      });
    } catch (e) {
      writing = false;
      report('sendReturn', e);
      return Promise.resolve(writeFail('error', 'none', false));
    }
  }

  /* ------------------------------------------------------------------ */
  /* Klaviyo client subscriptions (batch 3, 1.1.0): the sign-up pop-up   */
  /* on Rewards. Public endpoint and public site key (no secret); email  */
  /* and SMS are separate calls, each to its own list, each only after   */
  /* the customer's own click in the dialog (confirmation token) and,    */
  /* for SMS, the ticked consent box. Strict format checks first         */
  /* (Klaviyo drops a bad number silently and still answers 202). The   */
  /* email and the phone number are never logged, cached or stored.     */
  /* r6 (1.2.0): the list ids are built in. The SMS list is double      */
  /* opt-in: 202 means Klaviyo accepted the request, not that the       */
  /* customer is subscribed or that a text went out.                    */
  /* ------------------------------------------------------------------ */
  var KLAVIYO_URL = 'https://a.klaviyo.com/client/subscriptions';
  var KLAVIYO_REVISION = '2026-07-15';
  var KLAVIYO_ID_RE = /^[A-Za-z0-9]{6}$/;
  var KLAVIYO = {
    companyId: 'SrsZWV',          /* MK Supplements' public site key (in every storefront page's klaviyo.js URL) */
    lists: {
      email: 'RHNWWp',            /* "MKS Newsletter", single opt-in (Cowork, Klaviyo read only, 2026-10-12) */
      sms: 'VxmYUP'               /* "SMS Subscribers", DOUBLE opt-in, kept by Jim: Klaviyo texts a confirmation first (widgets: stepClaim) */
    },
    source: 'MKS portal - Rewards page',   /* + ' - Email v1' / ' - SMS v1': staff see it on the consent record in Klaviyo */
    versions: { email: 'v1', sms: 'v1' }   /* the disclosure text's version; bump with the text (consent record) */
  };
  (function () {
    var k = config.klaviyo && typeof config.klaviyo === 'object' ? config.klaviyo : null;
    if (!k) return;
    if (KLAVIYO_ID_RE.test(String(k.companyId || ''))) KLAVIYO.companyId = String(k.companyId);
    if (KLAVIYO_ID_RE.test(String(k.emailListId || ''))) KLAVIYO.lists.email = String(k.emailListId);
    if (KLAVIYO_ID_RE.test(String(k.smsListId || ''))) KLAVIYO.lists.sms = String(k.smsListId);
    /* kill switch (r6; also used by the tests): false switches that list off -> not_configured, nothing sent */
    if (k.emailListId === false) KLAVIYO.lists.email = null;
    if (k.smsListId === false) KLAVIYO.lists.sms = null;
    if (/^v\d{1,2}$/.test(String(k.emailVersion || ''))) KLAVIYO.versions.email = String(k.emailVersion);
    if (/^v\d{1,2}$/.test(String(k.smsVersion || ''))) KLAVIYO.versions.sms = String(k.smsVersion);
  })();
  var EMAIL_RE = /^[A-Za-z0-9!#$%&'*+\/=?^_`{|}~-]+(?:\.[A-Za-z0-9!#$%&'*+\/=?^_`{|}~-]+)*@(?:[A-Za-z0-9](?:[A-Za-z0-9-]{0,61}[A-Za-z0-9])?\.)+[A-Za-z]{2,24}$/;
  /* -> the trimmed address, or null */
  function validEmail(v) {
    if (typeof v !== 'string') return null;
    var s = v.trim();
    if (!s || s.length > 254 || !EMAIL_RE.test(s)) return null;
    if (s.split('@')[0].length > 64) return null;
    return s;
  }
  /* US mobile number -> '+1XXXXXXXXXX' (E.164), or null. Accepts spaces, dashes, dots, brackets and a
     leading 1 / +1; then exactly 10 digits, area code and exchange starting 2-9, no N11 area code. */
  function normalizePhone(v) {
    if (typeof v !== 'string' && typeof v !== 'number') return null;
    var s = String(v).trim();
    if (!s || s.length > 25 || !/^\+?[\d\s().-]+$/.test(s)) return null;
    if (s.indexOf('+') > 0) return null;
    var d = s.replace(/\D/g, '');
    if (s.charAt(0) === '+') { if (d.charAt(0) !== '1' || d.length !== 11) return null; d = d.slice(1); }
    else if (d.length === 11 && d.charAt(0) === '1') d = d.slice(1);
    if (!/^[2-9]\d{2}[2-9]\d{6}$/.test(d) || /^[2-9]11/.test(d)) return null;
    return '+1' + d;
  }
  /* '+15125550142' -> '(512) 555-0142' (display only) */
  function formatPhone(v) {
    var e = normalizePhone(v);
    return e ? '(' + e.slice(2, 5) + ') ' + e.slice(5, 8) + '-' + e.slice(8) : '';
  }
  var klaviyoBusy = { email: false, sms: false };
  function klaviyoReason(status) {
    if (status === 400) return 'bad_request';
    if (status === 429) return 'rate_limited';
    if (status >= 500 || status === 0) return 'error';
    return 'refused';
  }
  function klaviyoSubscribe(channel, value, opts) {
    opts = opts || {};
    try {
      if (!on()) return Promise.resolve({ ok: false, reason: 'off' });
      var profile = {};
      if (channel === 'email') {
        var email = validEmail(value);
        if (!email) return Promise.resolve({ ok: false, reason: 'invalid_email' });
        profile.email = email;
        profile.subscriptions = { email: { marketing: { consent: 'SUBSCRIBED' } } };
      } else {
        var phone = normalizePhone(value);
        if (!phone) return Promise.resolve({ ok: false, reason: 'invalid_phone' });
        if (opts.consent !== true) return Promise.resolve({ ok: false, reason: 'consent_required' });
        profile.phone_number = phone;
        /* the account email only identifies the profile; no email consent is sent from the SMS step */
        var idEmail = opts.email != null ? validEmail(opts.email) : null;
        if (idEmail) profile.email = idEmail;
        profile.subscriptions = { sms: { marketing: { consent: 'SUBSCRIBED' } } };
      }
      var list = KLAVIYO.lists[channel];
      if (!KLAVIYO_ID_RE.test(String(list || '')) || !KLAVIYO_ID_RE.test(KLAVIYO.companyId)) return Promise.resolve({ ok: false, reason: 'not_configured' });
      if (klaviyoBusy[channel]) return Promise.resolve({ ok: false, reason: 'busy' });
      var trec = useToken(opts.confirm, confirmKey({ kind: 'klaviyo', channel: channel }));
      if (!trec) return Promise.resolve({ ok: false, reason: 'not_confirmed' });
      /* review fix: SMS consent is the ticked box in the confirming pop-up at the click, recorded in the token */
      if (channel === 'sms' && trec.consent !== true) return Promise.resolve({ ok: false, reason: 'consent_required' });
      klaviyoBusy[channel] = true;
      /* r9 review (1.4.1): whose sign-up this is: the customer logged in at the press */
      var pressUser = loginNowId();
      var body = {
        data: {
          type: 'subscription',
          attributes: {
            custom_source: KLAVIYO.source + ' - ' + (channel === 'email' ? 'Email ' : 'SMS ') + KLAVIYO.versions[channel],
            profile: { data: { type: 'profile', attributes: profile } }
          },
          relationships: { list: { data: { type: 'list', id: list } } }
        }
      };
      return timedRequest(KLAVIYO_URL + '?company_id=' + encodeURIComponent(KLAVIYO.companyId), {
        method: 'POST',
        headers: { 'content-type': 'application/vnd.api+json', 'revision': KLAVIYO_REVISION },
        body: JSON.stringify(body),
        credentials: 'omit',
        cache: 'no-store',
        mode: 'cors',
        redirect: 'error',
        referrerPolicy: 'no-referrer'
      }).then(function (r) {
        klaviyoBusy[channel] = false;
        var res = r.status >= 200 && r.status < 300 ? { ok: true, status: r.status } : { ok: false, reason: klaviyoReason(r.status), status: r.status };
        /* r9: claimReward() for this channel's reward needs this (same customer, this page view) */
        if (res.ok) noteSignedUp(channel, pressUser);
        log('klaviyo ' + channel + ':', res.ok ? 'accepted' : res.reason + ' (HTTP ' + r.status + ')');
        return res;
      }, function (err) {
        klaviyoBusy[channel] = false;
        log('klaviyo ' + channel + ': error (' + (err && err.message === 'timeout' ? 'timeout' : 'network') + ')');
        return { ok: false, reason: 'error' };
      });
    } catch (e) {
      klaviyoBusy[channel] = false;
      report('klaviyo', e);
      return Promise.resolve({ ok: false, reason: 'error' });
    }
  }
  var klaviyo = Object.freeze({
    subscribeEmail: function (email, opts) { return klaviyoSubscribe('email', email, opts); },
    subscribeSms: function (phone, opts) { return klaviyoSubscribe('sms', phone, opts); },
    validEmail: validEmail,
    normalizePhone: normalizePhone,
    formatPhone: formatPhone,
    ready: function (channel) { return (channel === 'email' || channel === 'sms') && KLAVIYO_ID_RE.test(String(KLAVIYO.lists[channel] || '')) && KLAVIYO_ID_RE.test(KLAVIYO.companyId); },
    source: function (channel) { return channel === 'email' || channel === 'sms' ? KLAVIYO.source + ' - ' + (channel === 'email' ? 'Email ' : 'SMS ') + KLAVIYO.versions[channel] : ''; }
  });

  /* ------------------------------------------------------------------ */
  /* track: pure helpers for the "Track package" pop-up (batch 3). Data  */
  /* in, data out; no network (tracking() is the only /v1/tracking call). */
  /* ------------------------------------------------------------------ */
  var ORDERS_EVENT_CAP = 10;      /* /v1/orders gives at most the 10 newest scans per shipment */
  function trackNumbers(f) {
    var t = f && Array.isArray(f.tracking) ? f.tracking : [];
    var out = [];
    t.forEach(function (x) { var n = x && typeof x.number === 'string' ? x.number.trim() : ''; if (n && out.indexOf(n) < 0) out.push(n); });
    return out;
  }
  /* the shipment's tracking page: the first https tracking URL, or null */
  function trackUrl(f) {
    var t = f && Array.isArray(f.tracking) ? f.tracking : [];
    for (var i = 0; i < t.length; i++) {
      var u = t[i] && typeof t[i].url === 'string' ? safeUrl(t[i].url, { httpsOnly: true, absolute: true }) : null;
      if (u) return u;
    }
    return null;
  }
  /* true when a shipment of this /v1/orders order shows the full 10 scans (there may be more: offer "Show all") */
  function trackHasMore(order) {
    var fs = order && Array.isArray(order.fulfillments) ? order.fulfillments : [];
    return fs.some(function (f) { return f && Array.isArray(f.events) && f.events.length >= ORDERS_EVENT_CAP; });
  }
  /* the /v1/orders shipments with their scans from a /v1/tracking answer, matched by tracking number;
     by position only when neither side has numbers. Each: a copy of the /v1/orders shipment with
     events (all scans when matched), moreScans, full (true when the /v1/tracking scans were used). */
  function trackMerge(order, data) {
    var fs = order && Array.isArray(order.fulfillments) ? order.fulfillments : [];
    var ts = data && Array.isArray(data.fulfillments) ? data.fulfillments : [];
    var used = [];
    return fs.map(function (f, i) {
      var out = copy(f) || {};
      var nums = trackNumbers(f), match = -1;
      if (nums.length) {
        for (var j = 0; j < ts.length && match < 0; j++) {
          if (used.indexOf(j) < 0 && trackNumbers(ts[j]).some(function (n) { return nums.indexOf(n) >= 0; })) match = j;
        }
      } else if (ts[i] && !trackNumbers(ts[i]).length && used.indexOf(i) < 0) {
        match = i;
      }
      if (match >= 0 && ts[match] && Array.isArray(ts[match].events)) {
        used.push(match);
        out.events = copy(ts[match].events) || [];
        out.moreScans = ts[match].moreScans === true;
        out.full = true;
        if (typeof ts[match].carrier === 'string' && ts[match].carrier) out.carrier = ts[match].carrier;
      } else {
        out.events = Array.isArray(f && f.events) ? copy(f.events) : [];
        out.moreScans = false;
        out.full = false;
      }
      return out;
    });
  }
  var track = Object.freeze({ numbers: trackNumbers, url: trackUrl, hasMore: trackHasMore, merge: trackMerge, EVENT_CAP: ORDERS_EVENT_CAP });

  /* ------------------------------------------------------------------ */
  /* export                                                               */
  /* ------------------------------------------------------------------ */
  var api = {
    __core: true,
    version: VERSION,
    config: config,
    on: on,
    log: log,
    parsePath: parsePath,
    route: route,
    onRoute: onRoute,
    waitFor: waitFor,
    getIdToken: getIdToken,
    claims: claims,
    storefrontUserId: storefrontUserId,
    skio: skio,
    skioPages: skioPages,
    catalogEnabled: catalogEnabled,
    /* R11 (1.6.0): returns (reads; the fourth writer is below) */
    returnsEnabled: returnsEnabled,
    returns: returns,
    checkReturn: checkReturn,
    returnHeld: returnHeld,
    delivery: delivery,
    deliveryStatus: deliveryStatus,
    tracking: tracking,
    landing: Object.freeze({ is: hashIs, drop: landingDropHash }),
    invalidate: invalidate,
    q: q, qa: qa, byName: byName,
    el: el, svg: svg, text: text, esc: esc, safeUrl: safeUrl,
    mount: mount, unmount: unmount, hide: hide, unhideAll: unhideAll, guard: guard,
    toDate: toDate, calendarDay: calendarDay, formatDate: formatDate,
    daysFromToday: daysFromToday, daysBetween: daysBetween, addDays: addDays, addInterval: addInterval,
    num: num, money: money, points: points, pointsText: pointsText, pointsValue: pointsValue, earnPoints: earnPoints,
    POINTS_PER_DOLLAR: POINTS_PER_DOLLAR,
    TIERS: copy(TIERS), tierName: tierName, tierFor: tierFor, nextTier: nextTier, tierArt: tierArt,
    schedule: schedule, screen: screen,
    focusCombineCard: focusCombineCard,
    keyboard: keyboard,
    pointer: pointer,
    dialog: dialog,
    addSubscriptionLine: addSubscriptionLine,
    /* r8 (1.3.0): the second writer, its read-only helper, the kill switch and the shared day rules */
    changeNextOrderDate: changeNextOrderDate,
    nextOrderDate: nextOrderDate,
    writeEnabled: writeEnabled,
    orderDay: orderDay,
    nextDateRange: nextDateRange,
    nextDateInstant: nextDateInstant,
    nextDateEligible: nextDateEligible,
    /* r9 (1.4.0): the third writer (Skio's own claim request), its pre-check and the read-only refetch nudge */
    claimReward: claimReward,
    canClaim: canClaim,
    nudgeSkio: nudgeSkio,
    /* R11 (1.6.0): the fourth writer (a return request to the returns service) */
    sendReturn: sendReturn,
    klaviyo: klaviyo,
    track: track
  };
  try { Object.freeze(api.TIERS); } catch (e) { /* ignore */ }
  window.MKPortal = api;
  if (on()) {
    try { watchActions(); } catch (e) { /* ignore */ }
    try { watchModality(); } catch (e) { /* ignore */ }
    /* the home's link is a full page load, so this page load is the only moment to act */
    if (location.hash === COMBINE_HASH) setTimeout(function () { focusCombineCard(); }, 0);
  }
})();
